import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import '../../../src/calendar.css'; // Import the custom calendar styles
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box,
    Alert,
    SelectChangeEvent,
    IconButton,
    Chip,
    Stack,
    Typography,
    Tooltip,
    FormControlLabel,
    Switch,
    DialogContentText,
    Divider,
    Drawer,
    Fab,
    useMediaQuery,
    useTheme,
    Badge,
    SpeedDial,
    SpeedDialAction,
    SpeedDialIcon,
    CircularProgress,
    Snackbar,
    SnackbarOrigin,
    Grid,
    InputAdornment,
    Autocomplete
} from '@mui/material';
import { DateSelectArg, EventClickArg, DatesSetArg, EventDropArg } from '@fullcalendar/core';
import { EventResizeStopArg } from '@fullcalendar/interaction';
import { appointmentTypeService, AppointmentType } from '../../services/appointmentTypeService';
import { appointmentService, Appointment, UpdateAppointmentDto } from '../../services/appointmentService';
import { jobService, Job, CreateJobDto, JobType, Priority } from '../../services/jobService';
import { customerService, Customer, CustomerSource } from '../../services/customerService';
import { businessService, Business } from '../../services/businessService';
import { appointmentTypeShareService, MyAppointmentTypeShare } from '../../services/appointmentTypeShareService';
import { useAuth } from '../../contexts/AuthContext';
import { useBusinessTheme } from '../../contexts/BusinessThemeContext';
import { BusinessUser } from '../../types/business';
import BaseDialog from '../common/BaseDialog';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import FilterListIcon from '@mui/icons-material/FilterList';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { DatePicker, DateTimePicker, TimePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import AddIcon from '@mui/icons-material/Add';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { signalRService } from '../../services/signalRService';
import GoogleMapsAutocomplete from '../common/GoogleMapsAutocomplete';
import CustomerDetailsCard from '../common/CustomerDetailsCard';
import JobForm from '../common/JobForm';
import CustomerFormDialog from '../common/CustomerFormDialog';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import MapIcon from '@mui/icons-material/Map';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import { format } from 'date-fns';
import { enGB } from 'date-fns/locale';
import { cacheService } from '../../services/cacheService';
import { offlineService, OFFLINE_EVENTS } from '../../services/offlineService';
import { indexedDbService } from '../../services/indexedDbService';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import { ChangeType } from '../../services/indexedDbService';
import { STORES } from '../../services/indexedDbService';
import { apiClient } from '../../services/apiClient';
import { sortUsers } from '../../utils/userHelpers';
import { darkenColor, getAppointmentColor as getAppointmentColorUtil, getAppointmentTextColor as getAppointmentTextColorUtil } from '../../utils/calendarColorUtils';
import { useSwipeNavigation } from '../../hooks/useSwipeNavigation';
import CalendarPreviewPanel from './CalendarPreviewPanel';
import { addWeeks, addDays, startOfWeek, endOfWeek, startOfDay, endOfDay } from 'date-fns';
import { extractApiErrorMessage } from '../../utils/apiUtils';
import { escapeHtml } from '../../utils/htmlUtils';

interface CreateAppointmentFormData {
    title: string;
    description: string;
    appointmentTypeId: string;
    address?: string;
    latitude?: number;
    longitude?: number;
    userId?: string;
    start: Date;
    end: Date;
    isAllDay: boolean;
    timeZoneId: string;
    recurrenceRule?: string;
    customerId?: string;
    jobId?: string;
}

interface MainCalendarProps {
    initialDate?: Date;
    highlightAppointmentId?: string;
}

const MainCalendar: React.FC<MainCalendarProps> = ({ initialDate, highlightAppointmentId }) => {
    const { user, isAuthenticated } = useAuth();
    const { theme, businessId, setBusinessId } = useBusinessTheme();
    const navigate = useNavigate();
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [businesses, setBusinesses] = useState<Business[]>([]);
    const [selectedBusiness, setSelectedBusiness] = useState<Business | null>(null);
    const [isLoadingBusinesses, setIsLoadingBusinesses] = useState(false);
    const [formData, setFormData] = useState<CreateAppointmentFormData>({
        title: '',
        description: '',
        appointmentTypeId: '',
        address: '',
        latitude: undefined,
        longitude: undefined,
        userId: undefined,
        start: new Date(),
        end: new Date(),
        isAllDay: false,
        timeZoneId: Intl.DateTimeFormat().resolvedOptions().timeZone,
        recurrenceRule: '',
        customerId: undefined,
        jobId: undefined
    });
    const [appointmentTypes, setAppointmentTypes] = useState<AppointmentType[]>([]);
    const [selectedAppointmentType, setSelectedAppointmentType] = useState<AppointmentType | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [sharedUsers, setSharedUsers] = useState<BusinessUser[]>([]);
    const [appointments, setAppointments] = useState<Appointment[]>([]);
    const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
    const [isEditing, setIsEditing] = useState(false);
    const [selectedTypeFilters, setSelectedTypeFilters] = useState<string[]>([]);
    const [availableUsers, setAvailableUsers] = useState<BusinessUser[]>([]);
    // Map of userId -> appointmentTypeIds that were shared with the current user
    const [userSharedAppointmentTypes, setUserSharedAppointmentTypes] = useState<Record<string, string[]>>({});
    const CALENDAR_VIEW_KEY = 'calendar_view';
    const MOBILE_VIEWS = ['listWeek', 'timeGridDay'];
    const DESKTOP_VIEWS = ['dayGridMonth', 'timeGridWeek', 'timeGridDay'];

    const [selectedUserFilter, setSelectedUserFilter] = useState<string | null>(() => {
        // Initialize from localStorage if available
        return localStorage.getItem('selected_user_filter') || null;
    });
    // Refs to track selectedUserFilter and user ID for use in SignalR callbacks (avoids stale closures)
    const selectedUserFilterRef = useRef<string | null>(selectedUserFilter);
    const userIdRef = useRef<string | undefined>(user?.id);
    const [swipeNavigationEnabled, setSwipeNavigationEnabled] = useState<boolean>(() => {
        const stored = localStorage.getItem('swipe_navigation_enabled');
        return stored === null ? true : stored === 'true';
    });
    const calendarRef = useRef<any>(null);
    const calendarContainerRef = useRef<HTMLDivElement>(null);
    const [showRecurrenceOptions, setShowRecurrenceOptions] = useState(false);
    const [recurrenceEndDate, setRecurrenceEndDate] = useState<Date | null>(null);
    const [isRecurringDialogOpen, setIsRecurringDialogOpen] = useState(false);
    
    // Customer and Job selection state
    const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
    const [selectedJob, setSelectedJob] = useState<Job | null>(null);
    const [customerJobs, setCustomerJobs] = useState<Job[]>([]);
    const [customerSearchTerm, setCustomerSearchTerm] = useState('');
    const [customerSearchResults, setCustomerSearchResults] = useState<Customer[]>([]);
    const [isSearchingCustomers, setIsSearchingCustomers] = useState(false);
    const [showCustomerSearch, setShowCustomerSearch] = useState(false);
    const [customers, setCustomers] = useState<Customer[]>([]);
    const [isJobDialogOpen, setIsJobDialogOpen] = useState(false);
    const [isCustomerDialogOpen, setIsCustomerDialogOpen] = useState(false);
    const [isLoadingCustomers, setIsLoadingCustomers] = useState(false);
    const customerSearchTimerRef = useRef<NodeJS.Timeout | null>(null);
    const [jobFormData, setJobFormData] = useState<CreateJobDto>({
        customerId: '',
        assignedSalesPersonId: selectedUserFilter || user?.id || '',
        jobAddress: '',
        type: JobType.Consultation,
        estimatedValue: 0,
        priority: Priority.Medium,
        notes: ''
    });
    const [recurringAction, setRecurringAction] = useState<'edit' | 'delete' | null>(null);
    const [isEditingSeries, setIsEditingSeries] = useState(false);
    const [filterPanelOpen, setFilterPanelOpen] = useState(false);
    const muiTheme = useTheme();
    const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
    
    // State for swipe drag offset and animation
    const [dragOffset, setDragOffset] = useState<number>(0);
    const [isSnapping, setIsSnapping] = useState<boolean>(false);
    const [shouldShowPreviews, setShouldShowPreviews] = useState<boolean>(false);
    // Track the top offset of the FullCalendar view harness (below toolbar)
    const [viewHarnessTop, setViewHarnessTop] = useState<number>(0);
    
    // State for adjacent period appointments (for swipe preview - handles both week and day views)
    const [currentPeriodStart, setCurrentPeriodStart] = useState<Date | null>(null);
    const [prevPeriodAppointments, setPrevPeriodAppointments] = useState<Appointment[]>([]);
    const [nextPeriodAppointments, setNextPeriodAppointments] = useState<Appointment[]>([]);
    const [currentViewType, setCurrentViewType] = useState<string>(() => {
        const stored = localStorage.getItem(CALENDAR_VIEW_KEY);
        const allowedViews = isMobile ? MOBILE_VIEWS : DESKTOP_VIEWS;
        const defaultView = isMobile ? 'listWeek' : 'timeGridWeek';
        if (stored && allowedViews.includes(stored)) {
            return stored;
        }
        return defaultView;
    });
    
    // Swipe navigation for mobile - swipe left/right to go to next/prev period
    useSwipeNavigation(calendarContainerRef, {
        enabled: isMobile && swipeNavigationEnabled,
        threshold: 100, // Require 100px minimum for a quick flick
        longSwipeDistance: 140, // A slow drag past 140px always navigates
        maxVerticalDistance: 100, // Allow some vertical movement
        maxTime: 500, // Allow 500ms for a quick swipe gesture
        onDrag: (offset) => {
            // Apply visual feedback during drag
            setDragOffset(offset);
            setIsSnapping(false);
            setShouldShowPreviews(true);  // Show panels when drag starts
        },
        onDragEnd: () => {
            // Trigger snap animation
            setIsSnapping(true);
            // Reset offset after transition completes
            setTimeout(() => {
                setDragOffset(0);
                setIsSnapping(false);
            }, 300); // Match CSS transition duration
            // Delay unmount to prevent flash
            setTimeout(() => {
                setShouldShowPreviews(false);
            }, 350);
        },
        onSwipeLeft: () => {
            // Swipe left = go to next period
            const calendarApi = calendarRef.current?.getApi();
            if (calendarApi) {
                calendarApi.next();
            }
        },
        onSwipeRight: () => {
            // Swipe right = go to previous period
            const calendarApi = calendarRef.current?.getApi();
            if (calendarApi) {
                calendarApi.prev();
            }
        }
    });

    // Measure the top offset of the .fc-view-harness so preview panels align below the toolbar
    useLayoutEffect(() => {
        if (isMobile && calendarContainerRef.current) {
            const viewHarness = calendarContainerRef.current.querySelector('.fc-view-harness');
            if (viewHarness) {
                const containerRect = calendarContainerRef.current.getBoundingClientRect();
                const harnessRect = viewHarness.getBoundingClientRect();
                setViewHarnessTop(harnessRect.top - containerRect.top);
            }
        }
    });

    const currentViewRef = useRef<{start: Date | null, end: Date | null}>({start: null, end: null});
    const appointmentsLoadedRef = useRef<boolean>(false);
    // Track recently processed updates to avoid duplicates
    const processedUpdates = useRef<Set<string>>(new Set());
    // Add loading state to prevent multiple simultaneous requests
    const [isLoadingAppointments, setIsLoadingAppointments] = useState(false);
    const isLoadingAppointmentsRef = useRef(false);
    // Add debounce timer ref
    const loadAppointmentsTimerRef = useRef<NodeJS.Timeout | null>(null);
    // Add request cancellation ref
    const currentRequestRef = useRef<AbortController | null>(null);
    // Add current view tracking to prevent stale updates
    const currentViewIdRef = useRef<string>('');
    // Add loading state for appointment types
    const [isLoadingAppointmentTypes, setIsLoadingAppointmentTypes] = useState(false);
    // Track whether appointment types have been loaded at least once for current business
    const [appointmentTypesLoaded, setAppointmentTypesLoaded] = useState(false);
    // Add state for offline status
    const [isOffline, setIsOffline] = useState<boolean>(!offlineService.getOnlineStatus());
    const [isSyncing, setIsSyncing] = useState<boolean>(false);
    const [pendingChangesCount, setPendingChangesCount] = useState<number>(0);
    // Add state for filter drawer
    const [filterDrawerOpen, setFilterDrawerOpen] = useState<boolean>(false);
    // Add state for dialog
    const [dialogOpen, setDialogOpen] = useState<boolean>(false);
    // Add loading state for users
    const [isLoadingUsers, setIsLoadingUsers] = useState<boolean>(false);
    // Add debounce timer ref for loading users
    const loadUsersTimerRef = useRef<NodeJS.Timeout | null>(null);
    // Add state for snackbar
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);
    const [snackbarSeverity, setSnackbarSeverity] = useState<'error' | 'success' | 'info' | 'warning'>('info');
    const [firstDayOfWeek, setFirstDayOfWeek] = useState<number>(1); // Default to Monday
    const syncButtonContainerRef = useRef<HTMLDivElement | null>(null);
    // State for view customer/job dialogs
    const [isViewCustomerDialogOpen, setIsViewCustomerDialogOpen] = useState(false);
    const [isViewJobDialogOpen, setIsViewJobDialogOpen] = useState(false);

    // darkenColor is now imported from calendarColorUtils

    // Keep the loading ref in sync with state so debounced callbacks see current value
    useEffect(() => {
        isLoadingAppointmentsRef.current = isLoadingAppointments;
    }, [isLoadingAppointments]);

    // Load business settings when component mounts or businessId changes
    useEffect(() => {
        const loadBusinessSettings = async () => {
            if (businessId) {
                try {
                    const businessDetails = await businessService.getBusinessDetails(businessId);
                    setFirstDayOfWeek(businessDetails.firstDayOfWeek);
                } catch (error) {
                    console.error('Error loading business settings:', error);
                }
            }
        };

        loadBusinessSettings();
    }, [businessId]);

    // Handle initial date navigation
    useEffect(() => {
        if (initialDate && calendarRef.current) {
            const calendarApi = calendarRef.current.getApi();
            calendarApi.gotoDate(initialDate);
        }
    }, [initialDate]);

    // Handle appointment highlighting
    useEffect(() => {
        if (highlightAppointmentId && appointments.length > 0) {
            const appointment = appointments.find(a => a.id === highlightAppointmentId);
            if (appointment) {
                setSelectedAppointment(appointment);
                // Navigate to the appointment date
                if (calendarRef.current) {
                    const calendarApi = calendarRef.current.getApi();
                    calendarApi.gotoDate(new Date(appointment.startTime));
                }
                // Open the appointment for editing
                setTimeout(() => {
                    startEdit(appointment);
                }, 500); // Small delay to ensure calendar has rendered
            }
        }
    }, [highlightAppointmentId, appointments]);

    // Listen for offline/online events (window-level listener)
    // NOTE: This handler ONLY updates UI state. The actual data refresh is handled
    // by the offlineService listener below to avoid duplicate/racing merge operations.
    // Bug fix: Previously both handlers were calling load functions, causing race conditions
    // that led to intermittent loss of recurring appointments after multiple offline/online cycles.
    useEffect(() => {
        const handleConnectivityChange = (event: Event) => {
            const customEvent = event as CustomEvent;
            const isOnline = customEvent.detail.isOnline;
            setIsOffline(!isOnline);
            
            console.log(`[MainCalendar] Window connectivity changed: ${isOnline ? 'Online' : 'Offline'}`);
            // Data refresh is handled by the offlineService.CONNECTIVITY_CHANGED listener
            // to ensure a single, coordinated sync/merge operation
        };

        window.addEventListener('connectivity-changed', handleConnectivityChange);
        
        return () => {
            window.removeEventListener('connectivity-changed', handleConnectivityChange);
        };
    }, []);

    // Helper function to generate a unique key for an update
    const getUpdateKey = (appointment: Appointment) => {
        return `${appointment.id}_${new Date().getTime()}`;
    };
    
    // Clear processed updates after a delay
    const clearProcessedUpdate = (key: string) => {
        setTimeout(() => {
            processedUpdates.current.delete(key);
        }, 1000); // Clear after 1 second
    };

    // Helper function to generate a unique key for a deletion
    const getDeletionKey = (appointmentId: string) => {
        return `delete_${appointmentId}_${new Date().getTime()}`;
    };
    
    // Helper function to generate a unique key for a recurring instance deletion
    const getRecurringDeletionKey = (data: { recurringAppointmentId: string, instanceDate: string }) => {
        return `delete_recurring_${data.recurringAppointmentId}_${data.instanceDate}_${new Date().getTime()}`;
    };

    // Helper function to generate a unique key for a recurring series deletion
    const getRecurringSeriesDeletedKey = (data: { recurringAppointmentId: string, deleteOption: string }) => {
        return `delete_recurring_series_${data.recurringAppointmentId}_${data.deleteOption}_${new Date().getTime()}`;
    };

    const timeZones = [
        'UTC',
        'America/New_York',
        'America/Chicago',
        'America/Denver',
        'America/Los_Angeles',
        'America/Phoenix',
        'America/Anchorage',
        'Pacific/Honolulu',
        'Europe/London',
        'Europe/Paris',
        'Europe/Berlin',
        'Europe/Moscow',
        'Asia/Dubai',
        'Asia/Shanghai',
        'Asia/Tokyo',
        'Australia/Sydney',
        'Pacific/Auckland'
    ];

    // Add a method to refresh the pending changes count
    const refreshPendingChangesCount = async () => {
        const count = await offlineService.refreshPendingChangesCount();
        setPendingChangesCount(count);
        console.log('Updated pending changes count:', count);
    };

    // Cleanup timer on unmount
    useEffect(() => {
        return () => {
            if (customerSearchTimerRef.current) {
                clearTimeout(customerSearchTimerRef.current);
            }
        };
    }, []);

    useEffect(() => {
        // Load businesses when component mounts
        loadBusinesses();
        
        // Set up offline event listeners
        const handleConnectivityChange = async (event: Event) => {
            const customEvent = event as CustomEvent;
            setIsOffline(!customEvent.detail.isOnline);
            
            // When coming back online, trigger sync which will refresh data when complete
            // NOTE: Do NOT call loadAppointmentsOptimized() here! That causes multiple
            // simultaneous refresh operations which race with each other and cause
            // recurring appointments to flicker on/off.
            // The handleSyncCompleted handler will do the ONE refresh we need.
            if (customEvent.detail.isOnline) {
                console.log('Coming back online - triggering sync (refresh will happen on sync complete)');
                loadBusinesses();
                if (selectedBusiness) {
                    loadAppointmentTypes();
                    debouncedLoadAvailableUsers();
                }
                // Always trigger sync - it will dispatch SYNC_COMPLETED even if no pending changes
                offlineService.syncPendingChanges();
            }
        };
        
        const handleSyncStarted = () => {
            setIsSyncing(true);
        };
        
        const handleSyncCompleted = async () => {
            setIsSyncing(false);
            
            // Refresh data after sync completes
            if (selectedBusiness) {
                try {
                    // BUG FIX: Do NOT clear the entire appointments store!
                    // Base recurring appointments (those with recurrenceRule set) are stored
                    // in IndexedDB and are needed to generate recurring instances when offline.
                    // The API returns expanded instances, not base appointments, so clearing
                    // the store would lose the base recurring appointments permanently.
                    // 
                    // Instead, we rely on mergeCachedAppointments() to intelligently update
                    // the cache while preserving base recurring appointments.
                    console.log('Sync completed - refreshing appointments (preserving base recurring appointments)');
                    
                    // Reset the appointments loaded flag to force a reload
                    appointmentsLoadedRef.current = false;
                    
                    // Clear in-memory caches (NOT IndexedDB!)
                    cacheService.removeByPrefix('appointments:');
                    
                    // Reload appointment types
                    await loadAppointmentTypes();
                    
                    // Reload users
                    await loadAvailableUsers();
                    
                    // Force a refresh of the appointments
                    // This will fetch from API and merge with IndexedDB, preserving base recurring appointments
                    await loadAppointmentsOptimized();
                    
                    // Also refresh the calendar view
                    const calendarApi = calendarRef.current?.getApi();
                    if (calendarApi) {
                        console.log('Forcing calendar refetch after sync');
                        calendarApi.refetchEvents();
                    }
                } catch (error) {
                    console.error('Error refreshing data after sync:', error);
                }
            }
            
            // Explicitly check for pending changes after sync
            setTimeout(async () => {
                await refreshPendingChangesCount();
            }, 500); // Small delay to ensure all changes are processed
        };
        
        const handleSyncFailed = () => {
            setIsSyncing(false);
        };
        
        const handlePendingChangesUpdated = (event: Event) => {
            const customEvent = event as CustomEvent;
            setPendingChangesCount(customEvent.detail.pendingChangesCount || 0);
        };
        
        // Check pending changes count
        const checkPendingChanges = async () => {
            await refreshPendingChangesCount();
        };
        
        checkPendingChanges();
        
        // Add event listeners
        offlineService.addEventListener(OFFLINE_EVENTS.CONNECTIVITY_CHANGED, handleConnectivityChange);
        offlineService.addEventListener(OFFLINE_EVENTS.SYNC_STARTED, handleSyncStarted);
        offlineService.addEventListener(OFFLINE_EVENTS.SYNC_COMPLETED, handleSyncCompleted);
        offlineService.addEventListener(OFFLINE_EVENTS.SYNC_FAILED, handleSyncFailed);
        offlineService.addEventListener(OFFLINE_EVENTS.PENDING_CHANGES_UPDATED, handlePendingChangesUpdated);
        
        // Clean up event listeners
        return () => {
            offlineService.removeEventListener(OFFLINE_EVENTS.CONNECTIVITY_CHANGED, handleConnectivityChange);
            offlineService.removeEventListener(OFFLINE_EVENTS.SYNC_STARTED, handleSyncStarted);
            offlineService.removeEventListener(OFFLINE_EVENTS.SYNC_COMPLETED, handleSyncCompleted);
            offlineService.removeEventListener(OFFLINE_EVENTS.SYNC_FAILED, handleSyncFailed);
            offlineService.removeEventListener(OFFLINE_EVENTS.PENDING_CHANGES_UPDATED, handlePendingChangesUpdated);
            
            // Cancel any pending requests
            if (currentRequestRef.current) {
                currentRequestRef.current.abort();
                currentRequestRef.current = null;
            }
        };
    }, [selectedBusiness]);

    useEffect(() => {
        console.log('Selected business changed:', selectedBusiness);
        if (selectedBusiness) {
            // Reset appointment types loaded flag so appointments wait for types to load
            setAppointmentTypesLoaded(false);
            // Run migration to fix appointments with undefined businessId (one-time fix)
            indexedDbService.fixAppointmentsWithoutBusinessId(selectedBusiness.id)
                .catch(error => console.error('Error running businessId migration:', error));

            loadAppointmentTypes();
            debouncedLoadAvailableUsers(); // Use debounced version
            loadCustomerAndJobData(); // Load customer and job data for offline use
            appointmentsLoadedRef.current = false;
        } else {
            setAppointmentTypes([]);
            setSelectedTypeFilters([]);
            setAppointments([]);
            setAvailableUsers([]);
            // Note: Don't clear selectedUserFilter here — it gets cleared on actual
            // business switches by the effect at line 609. Clearing it here on initial
            // mount (when selectedBusiness is still null) would destroy the persisted
            // localStorage value before loadAvailableUsers has a chance to use it.
            setAppointmentTypesLoaded(false);
        }

        const calendar = calendarRef.current?.getApi();
        if (calendar) {
            calendar.setOption('businessHours', selectedBusiness && selectedBusiness.useBusinessHours ? {
                daysOfWeek: [0, 1, 2, 3, 4, 5, 6],
                startTime: selectedBusiness.businessHoursStart || '09:00',
                endTime: selectedBusiness.businessHoursEnd || '17:00'
            } : false);

            console.log('Business hours set:', selectedBusiness, selectedBusiness?.businessHoursStart, selectedBusiness?.businessHoursEnd);
        }
    }, [selectedBusiness?.id]);

    // Clear cache when business changes
    useEffect(() => {
        // When business changes, we want to ensure we have fresh data
        // But we don't want to clear cache on initial mount
        const previousBusinessId = localStorage.getItem('previous_business_id');
        
        if (selectedBusiness?.id && previousBusinessId && previousBusinessId !== selectedBusiness.id) {
            console.log('Business changed, clearing old cache and user filter');
            indexedDbService.clearAppointmentCache(previousBusinessId);
            // Clear user filter when switching businesses
            setSelectedUserFilter(null);
        }
        
        if (selectedBusiness?.id) {
            localStorage.setItem('previous_business_id', selectedBusiness.id);
        }
    }, [selectedBusiness?.id]);

    // Persist selectedUserFilter to localStorage and keep refs in sync
    useEffect(() => {
        selectedUserFilterRef.current = selectedUserFilter;
        userIdRef.current = user?.id;
        if (selectedUserFilter !== null) {
            localStorage.setItem('selected_user_filter', selectedUserFilter);
        } else {
            localStorage.removeItem('selected_user_filter');
        }
    }, [selectedUserFilter, user?.id]);

    // Persist swipe navigation preference to localStorage
    useEffect(() => {
        localStorage.setItem('swipe_navigation_enabled', String(swipeNavigationEnabled));
    }, [swipeNavigationEnabled]);

    // Persist calendar view preference to localStorage
    useEffect(() => {
        localStorage.setItem(CALENDAR_VIEW_KEY, currentViewType);
    }, [currentViewType]);

    // Handle device-type changes (responsive resize)
    useEffect(() => {
        const allowedViews = isMobile ? MOBILE_VIEWS : DESKTOP_VIEWS;
        const defaultView = isMobile ? 'listWeek' : 'timeGridWeek';
        if (!allowedViews.includes(currentViewType)) {
            const calendarApi = calendarRef.current?.getApi();
            if (calendarApi) {
                calendarApi.changeView(defaultView);
            }
            setCurrentViewType(defaultView);
        }
    }, [isMobile]);

    useEffect(() => {
        // Wait for appointment types to be loaded before loading appointments
        // This ensures appointments display with correct colors from the start
        if (selectedBusiness && appointmentTypesLoaded) {
            const calendarApi = calendarRef.current?.getApi();
            if (calendarApi) {
                // Reset the loaded flag so loadAppointments doesn't skip the reload
                // when only filters (user/type) changed but the date range is the same
                appointmentsLoadedRef.current = false;
                debouncedLoadAppointments();

                // Also reload adjacent period appointments when filters change (for week and day views)
                if (isMobile && currentPeriodStart) {
                    const viewType = calendarApi.view.type;
                    const isSwipeableView = viewType === 'timeGridWeek' || viewType === 'listWeek' || viewType === 'timeGridDay';
                    if (isSwipeableView) {
                        loadAdjacentPeriodAppointments(currentPeriodStart, viewType);
                    }
                }
            }
        }
    }, [selectedBusiness?.id, selectedTypeFilters, selectedUserFilter, appointmentTypesLoaded]);

    // Set up SignalR connection
    useEffect(() => {
        let isComponentMounted = true;
        
        const setupSignalR = async () => {
            if (!user?.id) return;
            
            try {
                console.log('Starting SignalR connection for user:', user.id);
                await signalRService.startConnection(user.id);
                if (isComponentMounted) {
                    console.log('SignalR connection started successfully');
                }
            } catch (error) {
                if (isComponentMounted) {
                    console.error('Error starting SignalR connection:', error);
                }
            }
        };
        
        setupSignalR();
        
        return () => {
            isComponentMounted = false;
            // Don't disconnect on every unmount - let the service handle reconnections
            // Only disconnect when user changes or component fully unmounts
        };
    }, [user?.id]);

    // Subscribe to SignalR updates
    useEffect(() => {
        if (selectedBusiness?.id) {
            console.log('Joining business group:', selectedBusiness.id);
            signalRService.joinBusinessGroup(selectedBusiness.id)
                .then(() => {
                    console.log('Successfully joined business group:', selectedBusiness.id);
                })
                .catch(error => {
                    console.error('Error joining business group:', error);
                });

            const unsubscribeCreated = signalRService.onAppointmentCreated((appointment) => {
                console.log('SignalR: Received appointment created notification', appointment);

                // Filter out appointments that don't belong to the currently-viewed user
                // Falls back to current user ID when no explicit filter is set (matches pattern used throughout component)
                const effectiveUserFilter = selectedUserFilterRef.current || userIdRef.current;
                if (effectiveUserFilter && appointment.userId !== effectiveUserFilter) {
                    console.debug('SignalR: Ignoring appointment for different user', {
                        appointmentUserId: appointment.userId,
                        effectiveUserFilter
                    });
                    return;
                }

                // Check if we already have this appointment in our state
                const isDuplicate = (prev: Appointment[]) => prev.some((a: Appointment) => a.id === appointment.id);
                
                // If it's a duplicate, return the previous state unchanged
                if (isDuplicate(appointments)) {
                    console.log('SignalR: Ignoring duplicate appointment creation notification', appointment.id, appointments.length);
                    return;
                }
                
                // Invalidate appointment cache for the current view
                invalidateAppointmentCache();
                
                // For recurring appointments, we need to reload all appointments to get all instances
                if (appointment.recurrenceRule) {
                    console.log('SignalR: Received recurring appointment, using debounced reload');
                    debouncedLoadAppointments();
                    return;
                }
                
                // For non-recurring appointments, just add the new appointment to the state
                setAppointments(prev => {
                    // Check again for duplicates (in case state changed since we checked)
                    if (isDuplicate(prev)) {
                        console.log('SignalR: Ignoring duplicate appointment creation notification (rechecked)', appointment.id, prev.length);
                        return [...prev];
                    }
                    
                    // Set hasWriteAccess based on the current user
                    const hasWriteAccess = appointment.userId === user?.id || 
                        appointmentTypes.some(type => 
                            type.id === appointment.appointmentTypeId && 
                            type.shares?.some(share => 
                                share.userEmail === user?.email
                            )
                        );

                    // Update cache
                    if (selectedBusiness?.id) {
                        indexedDbService.addOrUpdateCachedAppointment(
                            { ...appointment, hasWriteAccess },
                            selectedBusiness.id
                        );
                    }
                    
                    // Add the new appointment to the state
                    return [...prev, { ...appointment, hasWriteAccess }];
                });
            });

            const unsubscribeUpdated = signalRService.onAppointmentUpdated((updatedAppointment) => {
                console.log('SignalR: Received appointment update', {
                    appointmentId: updatedAppointment.id,
                    hasWriteAccess: updatedAppointment.hasWriteAccess,
                    userId: updatedAppointment.userId,
                    currentUserId: user?.id,
                    recurringAppointmentId: updatedAppointment.recurringAppointmentId,
                    isException: updatedAppointment.isException,
                    exceptionDate: updatedAppointment.exceptionDate
                });

                // Filter out updates for appointments that don't belong to the currently-viewed user
                const effectiveUserFilter = selectedUserFilterRef.current || userIdRef.current;
                if (effectiveUserFilter && updatedAppointment.userId !== effectiveUserFilter) {
                    console.debug('SignalR: Ignoring appointment update for different user', {
                        appointmentUserId: updatedAppointment.userId,
                        effectiveUserFilter
                    });
                    return;
                }

                // Generate a unique key for this update
                const updateKey = getUpdateKey(updatedAppointment);
                
                // Check if we've already processed this update
                if (processedUpdates.current.has(updateKey)) {
                    console.log('SignalR: Ignoring duplicate appointment update notification', updatedAppointment.id);
                    return;
                }
                
                // Mark this update as processed
                processedUpdates.current.add(updateKey);
                clearProcessedUpdate(updateKey);
                
                // Invalidate appointment cache for the current view
                invalidateAppointmentCache();
                
                // For recurring appointments without an exception, we need to reload all appointments to get all instances
                if (updatedAppointment.recurrenceRule && !updatedAppointment.isException) {
                    console.log('SignalR: Received recurring appointment update, using debounced reload');
                    debouncedLoadAppointments();
                    return;
                }
                
                // Update the local state immediately without reloading
                setAppointments(prev => {
                    // If this is an exception to a recurring appointment
                    if (updatedAppointment.isException && updatedAppointment.exceptionDate && updatedAppointment.recurringAppointmentId) {
                        console.log('SignalR: Handling exception for recurring appointment', {
                            exceptionDate: updatedAppointment.exceptionDate,
                            recurringAppointmentId: updatedAppointment.recurringAppointmentId,
                            startTime: updatedAppointment.startTime
                        });
                        
                        // Extract the dates for comparison (without time component)
                        const exceptionDate = new Date(updatedAppointment.exceptionDate);
                        const exceptionDateString = exceptionDate.toDateString();
                        
                        // Also get the date of the updated start time (for when an instance is moved to a new date)
                        const updatedStartDate = new Date(updatedAppointment.startTime);
                        const updatedStartDateString = updatedStartDate.toDateString();
                        
                        console.log('SignalR: Comparing dates', {
                            exceptionDate: exceptionDateString,
                            updatedStartDate: updatedStartDateString,
                            isSameDay: exceptionDateString === updatedStartDateString,
                            originalStartTime: updatedAppointment.startTime
                        });

                        // Filter out appointments that need to be removed:
                        // 1. The original instance at the exception date (replaced by the exception)
                        // 2. If the exception was moved to a date with another instance, filter out that instance too
                        const filteredAppointments = prev.filter(a => {
                            // Keep all non-recurring appointments
                            if (!a.recurringAppointmentId) return true;
                            
                            // Keep recurring appointments that aren't from the same series
                            if (a.recurringAppointmentId !== updatedAppointment.recurringAppointmentId) return true;
                            
                            // Keep recurring appointments that don't fall on the exception date or the new date (if moved)
                            const appointmentDate = new Date(a.startTime);
                            const appointmentDateString = appointmentDate.toDateString();
                            
                            // Check if this is the instance at the exception date
                            const isExceptionDateInstance = appointmentDateString === exceptionDateString;
                            
                            // Check if this is an instance at the target date (if the instance was moved)
                            const isTargetDateInstance = (updatedStartDateString !== exceptionDateString) && 
                                                        (appointmentDateString === updatedStartDateString);
                            
                            // If either condition is true, filter out the appointment
                            if (isExceptionDateInstance || isTargetDateInstance) {
                                console.log('SignalR: Filtering out instance', {
                                    id: a.id,
                                    date: appointmentDateString,
                                    isExceptionDateInstance,
                                    isTargetDateInstance
                                });
                                return false;
                            }
                            
                            return true;
                        });
                        
                        console.log('SignalR: Filtered recurring instances', {
                            originalCount: prev.length,
                            filteredCount: filteredAppointments.length,
                            exceptionDate: exceptionDateString,
                            updatedDate: updatedStartDateString
                        });
                        
                        // Find if we already have this exception in our state
                        const existingExceptionIndex = filteredAppointments.findIndex(a => 
                            a.id === updatedAppointment.id
                        );
                        
                        // If we already have this exception, update it
                        if (existingExceptionIndex >= 0) {
                            console.log('SignalR: Updating existing exception in state');
                            return filteredAppointments.map((a, index) => 
                                index === existingExceptionIndex ? updatedAppointment : a
                            );
                        }
                        
                        // Otherwise, add it as a new appointment if it's in our current view
                        const calendarApi = calendarRef.current?.getApi();
                        if (calendarApi) {
                            const viewStart = calendarApi.view.activeStart;
                            const viewEnd = calendarApi.view.activeEnd;
                            const appointmentStart = new Date(updatedAppointment.startTime);
                            const appointmentEnd = new Date(updatedAppointment.endTime);

                            // Only add if the appointment falls within our current view
                            if (appointmentStart >= viewStart && appointmentEnd <= viewEnd) {
                                console.log('SignalR: Adding new exception to view');
                                
                                // Update cache
                                if (selectedBusiness?.id) {
                                    indexedDbService.addOrUpdateCachedAppointment(updatedAppointment, selectedBusiness.id);
                                }
                                
                                return [...filteredAppointments, updatedAppointment];
                            }
                        }
                        return filteredAppointments;
                    }
                    
                    // Check if we have the appointment in our current state
                    const hasExistingAppointment = prev.some(a => 
                        a.id === updatedAppointment.id || 
                        (updatedAppointment.recurringAppointmentId && 
                         a.recurringAppointmentId === updatedAppointment.recurringAppointmentId)
                    );

                    // If we don't have the appointment, add it if it's within our current view
                    if (!hasExistingAppointment) {
                        const calendarApi = calendarRef.current?.getApi();
                        if (calendarApi) {
                            const viewStart = calendarApi.view.activeStart;
                            const viewEnd = calendarApi.view.activeEnd;
                            const appointmentStart = new Date(updatedAppointment.startTime);
                            const appointmentEnd = new Date(updatedAppointment.endTime);

                            // Only add if the appointment falls within our current view
                            if (appointmentStart >= viewStart && appointmentEnd <= viewEnd) {
                                console.log('SignalR: Adding new appointment to view');
                                // Recalculate hasWriteAccess for the new appointment
                                const hasWriteAccess = updatedAppointment.userId === user?.id ||
                                    appointmentTypes.some(type =>
                                        type.id === updatedAppointment.appointmentTypeId &&
                                        type.shares?.some(share =>
                                            share.userEmail === user?.email
                                        )
                                    );
                                
                                // Update cache
                                if (selectedBusiness?.id) {
                                    indexedDbService.addOrUpdateCachedAppointment(
                                        { ...updatedAppointment, hasWriteAccess },
                                        selectedBusiness.id
                                    );
                                }
                                
                                return [...prev, { ...updatedAppointment, hasWriteAccess }];
                            }
                        }
                        console.log('SignalR: Appointment not in current view, skipping update');
                        return prev;
                    }

                    // Update existing appointments
                    const updatedAppointments = prev.map(a => {
                        if (a.id === updatedAppointment.id || 
                            (updatedAppointment.recurringAppointmentId && 
                             a.recurringAppointmentId === updatedAppointment.recurringAppointmentId)) {
                            
                            // Recalculate hasWriteAccess based on the current user
                            const hasWriteAccess = updatedAppointment.userId === user?.id ||
                                appointmentTypes.some(type =>
                                    type.id === updatedAppointment.appointmentTypeId &&
                                    type.shares?.some(share =>
                                        share.userEmail === user?.email
                                    )
                                );

                            const updated = { 
                                ...updatedAppointment,
                                id: a.id, // Preserve the individual instance ID
                                startTime: updatedAppointment.startTime, // Use the new times from the update
                                endTime: updatedAppointment.endTime,
                                hasWriteAccess 
                            };
                            
                            // Update cache
                            if (selectedBusiness?.id) {
                                indexedDbService.addOrUpdateCachedAppointment(updated, selectedBusiness.id);
                            }
                            
                            return updated;
                        }
                        return a;
                    });
                    
                    return updatedAppointments;
                });
            });

            const unsubscribeDeleted = signalRService.onAppointmentDeleted((appointmentId) => {
                console.log('SignalR: Received appointment deleted notification', appointmentId);
                
                // Generate a unique key for this deletion
                const deletionKey = getDeletionKey(appointmentId);
                
                // Check if we've already processed this deletion
                if (processedUpdates.current.has(deletionKey)) {
                    console.log('SignalR: Ignoring duplicate appointment deletion notification', appointmentId);
                    return;
                }
                
                // Mark this deletion as processed
                processedUpdates.current.add(deletionKey);
                clearProcessedUpdate(deletionKey);
                
                // Invalidate appointment cache for the current view
                invalidateAppointmentCache();
                
                // Check if this is a recurring appointment that was deleted
                const isRecurringAppointment = appointments.some(a => 
                    a.id === appointmentId && a.recurrenceRule
                );
                
                // If it's a recurring appointment, we need to reload all appointments
                if (isRecurringAppointment) {
                    console.log('SignalR: Recurring appointment deleted, using debounced reload');
                    debouncedLoadAppointments();
                    return;
                }
                
                // Remove from cache
                if (selectedBusiness?.id) {
                    indexedDbService.removeCachedAppointment(appointmentId, selectedBusiness.id);
                }
                
                // Remove the appointment and any instances that have it as their recurringAppointmentId
                setAppointments(prev => prev.filter(a => 
                    a.id !== appointmentId && a.recurringAppointmentId !== appointmentId
                ));
            });

            const unsubscribeRecurringInstanceDeleted = signalRService.onRecurringInstanceDeleted((data) => {
                console.log('SignalR: Received recurring instance deleted notification', data);
                
                // Generate a unique key for this deletion
                const deletionKey = getRecurringDeletionKey(data);
                
                // Check if we've already processed this deletion
                if (processedUpdates.current.has(deletionKey)) {
                    console.log('SignalR: Ignoring duplicate recurring instance deletion notification', data);
                    return;
                }
                
                // Mark this deletion as processed
                processedUpdates.current.add(deletionKey);
                clearProcessedUpdate(deletionKey);
                
                // Invalidate appointment cache for the current view
                invalidateAppointmentCache();
                
                // Use debounced reload for recurring instance deletions
                console.log('SignalR: Recurring instance deleted, using debounced reload');
                debouncedLoadAppointments();
            });

            const unsubscribeRecurringSeriesDeleted = signalRService.onRecurringSeriesDeleted((data) => {
                console.log('SignalR: Received recurring series deleted notification', data);
                
                // Generate a unique key for this deletion
                const deletionKey = getRecurringSeriesDeletedKey(data);
                
                // Check if we've already processed this deletion
                if (processedUpdates.current.has(deletionKey)) {
                    console.log('SignalR: Ignoring duplicate recurring series deletion notification', data);
                    return;
                }
                
                // Mark this deletion as processed
                processedUpdates.current.add(deletionKey);
                clearProcessedUpdate(deletionKey);
                
                // Invalidate appointment cache for the current view
                invalidateAppointmentCache();
                
                // For recurring series deletion, we need to reload all appointments
                console.log('SignalR: Recurring series deleted, using debounced reload');
                debouncedLoadAppointments();
            });

            return () => {
                if (selectedBusiness?.id) {
                    console.log('Leaving business group:', selectedBusiness.id);
                    signalRService.leaveBusinessGroup(selectedBusiness.id);
                }
                unsubscribeCreated();
                unsubscribeUpdated();
                unsubscribeDeleted();
                unsubscribeRecurringInstanceDeleted();
                unsubscribeRecurringSeriesDeleted();
            };
        }
    }, [selectedBusiness?.id, user?.id, user?.email, appointmentTypes]);

    // Helper function to invalidate appointment cache for the current view
    const invalidateAppointmentCache = () => {
        const calendarApi = calendarRef.current?.getApi();
        if (calendarApi) {
            const start = calendarApi.view.activeStart;
            const end = calendarApi.view.activeEnd;
            
            const params = {
                startDate: start.toISOString(),
                endDate: end.toISOString(),
                appointmentTypeIds: selectedTypeFilters
            };
            
            const queryString = new URLSearchParams();
            if (params.startDate) {
                queryString.append('start', params.startDate);
            }
            if (params.endDate) {
                queryString.append('end', params.endDate);
            }
            if (params.appointmentTypeIds?.length) {
                params.appointmentTypeIds.forEach(id => 
                    queryString.append('appointmentTypeIds', id)
                );
            }
            
            const cacheKey = `appointments:?${queryString.toString()}`;
            console.log('Invalidating appointment cache key:', cacheKey);
            cacheService.remove(cacheKey);
            
            // Reset the appointments loaded flag to force a reload
            appointmentsLoadedRef.current = false;
        }
    };

    // Add a debounced version of loadAppointments
    const debouncedLoadAppointments = () => {
        console.log('Debounced load appointments called');
        
        // Clear any existing timer
        if (loadAppointmentsTimerRef.current) {
            clearTimeout(loadAppointmentsTimerRef.current);
        }
        
        // Set a new timer
        loadAppointmentsTimerRef.current = setTimeout(() => {
            // Use ref instead of state to avoid stale closure when the timeout fires
            if (!isLoadingAppointmentsRef.current) {
                loadAppointments();
            } else {
                console.log('Already loading appointments, skipping request');
            }
        }, 300); // 300ms debounce time
    };

    // Optimized loadAppointments with preloading and intelligent caching
    const loadAppointmentsOptimized = async () => {
        try {
            console.log('[loadAppointmentsOptimized] Starting...');
            console.log('[loadAppointmentsOptimized] Calendar view changed - loading appointments for new date range');
            
            if (!selectedBusiness) {
                console.log('[loadAppointmentsOptimized] No selected business, clearing appointments');
                setAppointments([]);
                return;
            }

            const calendarApi = calendarRef.current?.getApi();
            if (!calendarApi) {
                console.log('[loadAppointmentsOptimized] Calendar API not available yet');
                return;
            }

            const start = calendarApi.view.activeStart;
            const end = calendarApi.view.activeEnd;

            // Create a unique view ID for this request
            const viewId = `${start.getTime()}-${end.getTime()}-${selectedBusiness.id}`;
            currentViewIdRef.current = viewId;

            console.log('[loadAppointmentsOptimized] Loading for range:', { start, end, businessId: selectedBusiness.id, viewId });

            // Cancel any existing request
            if (currentRequestRef.current) {
                console.log('[loadAppointmentsOptimized] Cancelling previous request');
                currentRequestRef.current.abort();
            }

            // Create new abort controller for this request
            const abortController = new AbortController();
            currentRequestRef.current = abortController;

            // Check if we already have appointments for this exact range
            if (appointmentsLoadedRef.current && 
                currentViewRef.current.start?.getTime() === start.getTime() && 
                currentViewRef.current.end?.getTime() === end.getTime()) {
                console.log('[loadAppointmentsOptimized] Appointments already loaded for this view, skipping');
                return;
            }

            // Phase 1: Always load from IndexedDB first with extended range
            console.log('[loadAppointmentsOptimized] Phase 1: Loading from IndexedDB...');
            
            // Extend the range by 2 weeks on each side for preloading
            const extendedStart = new Date(start);
            extendedStart.setDate(extendedStart.getDate() - 14); // 2 weeks before
            const extendedEnd = new Date(end);
            extendedEnd.setDate(extendedEnd.getDate() + 14); // 2 weeks after

            let indexedDbAppointments: Appointment[] = [];

            // Determine effective appointment type filters based on selected user (for IndexedDB)
            let effectiveTypeFiltersForIndexedDb = selectedTypeFilters;
            if (selectedUserFilter && selectedUserFilter !== user?.id) {
                // When viewing another user's calendar, only show shared appointment types
                const sharedTypeIds = userSharedAppointmentTypes[selectedUserFilter] || [];
                if (selectedTypeFilters.length > 0) {
                    // Intersect with manually selected type filters
                    effectiveTypeFiltersForIndexedDb = selectedTypeFilters.filter(id => sharedTypeIds.includes(id));
                } else {
                    // No manual filter, use all shared types
                    effectiveTypeFiltersForIndexedDb = sharedTypeIds;
                }
            }

            try {
                // Always load from IndexedDB first
                const rawAppointments = await indexedDbService.getAppointments(
                    extendedStart.toISOString(),
                    extendedEnd.toISOString(),
                    effectiveTypeFiltersForIndexedDb
                );

                    // Check if this request is still current
                    if (currentViewIdRef.current !== viewId) {
                    console.log('[loadAppointmentsOptimized] Request cancelled, skipping IndexedDB update');
                        return;
                    }

                // Process appointments for offline mode (handles recurring appointment generation)
                if (rawAppointments.length > 0) {
                    console.log(`[loadAppointmentsOptimized] Found ${rawAppointments.length} raw appointments in IndexedDB`);
                    console.log(`[loadAppointmentsOptimized] Raw appointments sample:`, rawAppointments.slice(0, 3).map(apt => ({
                        id: apt.id,
                        title: apt.title,
                        recurrenceRule: apt.recurrenceRule,
                        recurringAppointmentId: apt.recurringAppointmentId,
                        startTime: apt.startTime
                    })));
                    
                    // Check for base recurring appointments (those with recurrenceRule)
                    const baseRecurringAppointments = rawAppointments.filter(apt => apt.recurrenceRule && apt.recurrenceRule.trim() !== '');
                    console.log(`[loadAppointmentsOptimized] Base recurring appointments in IndexedDB: ${baseRecurringAppointments.length}`);
                    
                    // Check for recurring instances (those with recurringAppointmentId)
                    const recurringInstances = rawAppointments.filter(apt => apt.recurringAppointmentId);
                    console.log(`[loadAppointmentsOptimized] Recurring instances in IndexedDB: ${recurringInstances.length}`);
                    
                    if (baseRecurringAppointments.length > 0) {
                        console.log(`[loadAppointmentsOptimized] Base recurring appointments:`, baseRecurringAppointments.map(apt => ({
                            id: apt.id,
                            title: apt.title,
                            recurrenceRule: apt.recurrenceRule,
                            startTime: apt.startTime
                        })));
                    }
                    
                    // OFFLINE FIX: If we have base recurring appointments, use normal processing
                    // If we only have instances, use them directly without trying to regenerate
                    if (baseRecurringAppointments.length > 0) {
                        console.log(`[loadAppointmentsOptimized] Using normal processing with base recurring appointments`);
                        indexedDbAppointments = await appointmentService['processAppointmentsForOffline'](
                            rawAppointments,
                            extendedStart,
                            extendedEnd
                        );
                    } else if (recurringInstances.length > 0) {
                        console.log(`[loadAppointmentsOptimized] No base recurring appointments found, using existing instances directly (same as online behavior)`);
                        
                        // Filter all appointments to the extended date range (same logic as online)
                        indexedDbAppointments = rawAppointments.filter(appointment => {
                            const appointmentStart = new Date(appointment.startTime);
                            const appointmentEnd = new Date(appointment.endTime);
                            return appointmentStart <= extendedEnd && appointmentEnd >= extendedStart;
                        });
                        
                        console.log(`[loadAppointmentsOptimized] Using ${indexedDbAppointments.length} appointments directly from IndexedDB (same as online behavior)`);
                    } else {
                        console.log(`[loadAppointmentsOptimized] No recurring data found, using normal processing`);
                        indexedDbAppointments = await appointmentService['processAppointmentsForOffline'](
                            rawAppointments,
                            extendedStart,
                            extendedEnd
                        );
                    }
                    
                    console.log(`[loadAppointmentsOptimized] After processAppointmentsForOffline: ${indexedDbAppointments.length} appointments`);
                    console.log(`[loadAppointmentsOptimized] Processed appointments sample:`, indexedDbAppointments.slice(0, 3).map(apt => ({
                        id: apt.id,
                        title: apt.title,
                        isRecurringInstance: apt.isRecurringInstance,
                        recurringAppointmentId: apt.recurringAppointmentId,
                        startTime: apt.startTime
                    })));
                } else {
                    console.log(`[loadAppointmentsOptimized] No raw appointments found in IndexedDB for extended date range`);
                    indexedDbAppointments = [];
                }

                // Apply user filtering - default to current user if no filter selected
                const effectiveUserFilter = selectedUserFilter || user?.id;
                indexedDbAppointments = indexedDbAppointments.filter(appointment =>
                    appointment.userId === effectiveUserFilter
                );

                    // Filter to only show appointments in the current view range
                const filteredAppointments = indexedDbAppointments.filter(appointment => {
                        const appointmentStart = new Date(appointment.startTime);
                        return appointmentStart >= start && appointmentStart < end;
                    });

                console.log(`[loadAppointmentsOptimized] Loaded ${filteredAppointments.length} appointments from IndexedDB`);
                
                // Log all appointments being added to the calendar
                console.log('[loadAppointmentsOptimized] Appointments being added to calendar:', filteredAppointments.map(appointment => ({
                    id: appointment.id,
                    title: appointment.title || appointment.description,
                    startTime: appointment.startTime,
                    endTime: appointment.endTime,
                    appointmentType: appointment.appointmentTypeId,
                    isRecurring: !!appointment.recurringAppointmentId,
                    recurringAppointmentId: appointment.recurringAppointmentId
                })));
                
                // Always set appointments from IndexedDB first
                        if (currentViewIdRef.current === viewId) {
                            setAppointments(filteredAppointments);
                            appointmentsLoadedRef.current = true;
                            currentViewRef.current = { start, end };
                            
                    // Log appointments being set to calendar (regardless of caching)
                    console.log('[loadAppointmentsOptimized] Setting appointments to calendar:', filteredAppointments.map(appointment => ({
                        id: appointment.id,
                        title: appointment.title || appointment.description,
                        startTime: appointment.startTime,
                        endTime: appointment.endTime,
                        appointmentType: appointment.appointmentTypeId,
                        isRecurring: !!appointment.recurringAppointmentId,
                        recurringAppointmentId: appointment.recurringAppointmentId
                    })));
                }
                
            } catch (indexedDbError) {
                console.error('[loadAppointmentsOptimized] Error loading from IndexedDB:', indexedDbError);
                // If IndexedDB fails completely, show empty state
                if (currentViewIdRef.current === viewId) {
                    setAppointments([]);
                    appointmentsLoadedRef.current = true;
                    currentViewRef.current = { start, end };
                }
            }

            // Phase 2: Fetch fresh data from API in background to update IndexedDB
            console.log('[loadAppointmentsOptimized] Phase 2: Fetching from API to sync IndexedDB...');
            setIsLoadingAppointments(true);
            
            try {
                // Check if request was cancelled before making API call
                if (currentViewIdRef.current !== viewId) {
                    console.log('[loadAppointmentsOptimized] Request cancelled before API call');
                    return;
                }

                // Determine effective appointment type filters based on selected user
                let effectiveAppointmentTypeIds = selectedTypeFilters;
                if (selectedUserFilter && selectedUserFilter !== user?.id) {
                    // When viewing another user's calendar, only show shared appointment types
                    const sharedTypeIds = userSharedAppointmentTypes[selectedUserFilter] || [];
                    if (selectedTypeFilters.length > 0) {
                        // Intersect with manually selected type filters
                        effectiveAppointmentTypeIds = selectedTypeFilters.filter(id => sharedTypeIds.includes(id));
                    } else {
                        // No manual filter, use all shared types
                        effectiveAppointmentTypeIds = sharedTypeIds;
                    }
                }

                const params = {
                    startDate: extendedStart.toISOString(), // Use extended range for preloading
                    endDate: extendedEnd.toISOString(),
                    appointmentTypeIds: effectiveAppointmentTypeIds,
                    targetUserId: selectedUserFilter || undefined
                };

                console.log('[loadAppointmentsOptimized] API params (extended range):', params);
                const freshData = await appointmentService.getAppointments(params);
                
                // Check if request was cancelled after API call
                if (currentViewIdRef.current !== viewId) {
                    console.log('[loadAppointmentsOptimized] Request cancelled after API call, skipping update');
                    return;
                }
                
                console.log('[loadAppointmentsOptimized] Received fresh appointments from API:', freshData.length);

                // Update IndexedDB with fresh data from API
                console.log('[loadAppointmentsOptimized] Updating IndexedDB with fresh data...');
                await indexedDbService.mergeCachedAppointments(
                    freshData,
                    selectedBusiness.id,
                    { start: extendedStart, end: extendedEnd }
                );

                // Mark extended range as fetched
                await indexedDbService.markRangeFetched(extendedStart, extendedEnd, selectedBusiness.id);
                
                // Reload from IndexedDB to get the updated data
                console.log('[loadAppointmentsOptimized] Reloading from updated IndexedDB...');
                const updatedRawAppointments = await indexedDbService.getAppointments(
                    extendedStart.toISOString(),
                    extendedEnd.toISOString(),
                    effectiveAppointmentTypeIds
                );
                
                if (updatedRawAppointments.length > 0) {
                    const updatedAppointments = await appointmentService['processAppointmentsForOffline'](
                        updatedRawAppointments,
                        extendedStart,
                        extendedEnd
                    );
                    
                    // Apply user filtering - default to current user if no filter selected
                    const effectiveUserFilter = selectedUserFilter || user?.id;
                    let finalAppointments = updatedAppointments.filter(appointment =>
                        appointment.userId === effectiveUserFilter
                    );
                
                // Filter to only show appointments in the current view range
                    const filteredAppointments = finalAppointments.filter(appointment => {
                    const appointmentStart = new Date(appointment.startTime);
                    return appointmentStart >= start && appointmentStart < end;
                });

                    // Final check before updating UI
                    if (currentViewIdRef.current === viewId) {
                setAppointments(filteredAppointments);
                        console.log(`[loadAppointmentsOptimized] Updated UI with ${filteredAppointments.length} appointments from synced IndexedDB`);
                        
                        // Log all appointments being updated after API sync
                        console.log('[loadAppointmentsOptimized] Updated appointments after API sync:', filteredAppointments.map(appointment => ({
                            id: appointment.id,
                            title: appointment.title || appointment.description,
                            startTime: appointment.startTime,
                            endTime: appointment.endTime,
                            appointmentType: appointment.appointmentTypeId,
                            isRecurring: !!appointment.recurringAppointmentId,
                            recurringAppointmentId: appointment.recurringAppointmentId
                        })));
                    }
                }
                
                console.log('[loadAppointmentsOptimized] Complete!');
            } catch (apiError) {
                // Check if request was cancelled
                if (currentViewIdRef.current !== viewId) {
                    console.log('[loadAppointmentsOptimized] Request cancelled during error handling');
                    return;
                }
                
                console.log('[loadAppointmentsOptimized] API call failed (offline or error), using IndexedDB data only:', apiError);
                console.log(`[loadAppointmentsOptimized] IndexedDB appointments available when offline: ${indexedDbAppointments.length}`);
                
                if (indexedDbAppointments.length > 0) {
                    console.log(`[loadAppointmentsOptimized] Recurring appointments in IndexedDB:`, indexedDbAppointments.filter(apt => apt.isRecurringInstance || apt.recurringAppointmentId).map(apt => ({
                        id: apt.id,
                        title: apt.title,
                        isRecurringInstance: apt.isRecurringInstance,
                        recurringAppointmentId: apt.recurringAppointmentId,
                        startTime: apt.startTime
                    })));
                }
                
                // API failed, but we already have IndexedDB data loaded, so we're good
                if (indexedDbAppointments.length === 0) {
                    setError('Unable to load appointments (offline)');
                }
            }
        } catch (error) {
            console.error('[loadAppointmentsOptimized] Unexpected error:', error);
            setError('Failed to load appointments');
        } finally {
            setIsLoadingAppointments(false);
        }
    };

    // Preload adjacent weeks for smoother navigation
    const preloadAdjacentWeeks = async (currentStart: Date, currentEnd: Date) => {
        try {
            console.log('[preloadAdjacentWeeks] Starting background preload...');
            
            // Calculate adjacent week ranges
            const prevWeekStart = new Date(currentStart);
            prevWeekStart.setDate(prevWeekStart.getDate() - 7);
            const prevWeekEnd = new Date(currentEnd);
            prevWeekEnd.setDate(prevWeekEnd.getDate() - 7);
            
            const nextWeekStart = new Date(currentStart);
            nextWeekStart.setDate(nextWeekStart.getDate() + 7);
            const nextWeekEnd = new Date(currentEnd);
            nextWeekEnd.setDate(nextWeekEnd.getDate() + 7);
            
            // Check if we already have data for these ranges
            const hasPrevWeek = await indexedDbService.hasRangeCached(prevWeekStart, prevWeekEnd, selectedBusiness!.id);
            const hasNextWeek = await indexedDbService.hasRangeCached(nextWeekStart, nextWeekEnd, selectedBusiness!.id);
            
            if (!hasPrevWeek || !hasNextWeek) {
                console.log('[preloadAdjacentWeeks] Preloading missing adjacent weeks...');

                // Determine effective appointment type filters based on selected user
                let effectiveTypeFilters = selectedTypeFilters;
                if (selectedUserFilter && selectedUserFilter !== user?.id) {
                    const sharedTypeIds = userSharedAppointmentTypes[selectedUserFilter] || [];
                    if (selectedTypeFilters.length > 0) {
                        effectiveTypeFilters = selectedTypeFilters.filter(id => sharedTypeIds.includes(id));
                    } else {
                        effectiveTypeFilters = sharedTypeIds;
                    }
                }

                // Preload missing weeks in the background
                const preloadPromises = [];

                if (!hasPrevWeek) {
                    preloadPromises.push(
                        appointmentService.getAppointments({
                            startDate: prevWeekStart.toISOString(),
                            endDate: prevWeekEnd.toISOString(),
                            appointmentTypeIds: effectiveTypeFilters,
                            targetUserId: selectedUserFilter || undefined
                        }).then(async (appointments) => {
                            await indexedDbService.mergeCachedAppointments(
                                appointments,
                                selectedBusiness!.id,
                                { start: prevWeekStart, end: prevWeekEnd }
                            );
                            await indexedDbService.markRangeFetched(prevWeekStart, prevWeekEnd, selectedBusiness!.id);
                            console.log(`[preloadAdjacentWeeks] Preloaded previous week: ${appointments.length} appointments`);
                        })
                    );
                }

                if (!hasNextWeek) {
                    preloadPromises.push(
                        appointmentService.getAppointments({
                            startDate: nextWeekStart.toISOString(),
                            endDate: nextWeekEnd.toISOString(),
                            appointmentTypeIds: effectiveTypeFilters,
                            targetUserId: selectedUserFilter || undefined
                        }).then(async (appointments) => {
                            await indexedDbService.mergeCachedAppointments(
                                appointments,
                                selectedBusiness!.id,
                                { start: nextWeekStart, end: nextWeekEnd }
                            );
                            await indexedDbService.markRangeFetched(nextWeekStart, nextWeekEnd, selectedBusiness!.id);
                            console.log(`[preloadAdjacentWeeks] Preloaded next week: ${appointments.length} appointments`);
                        })
                    );
                }
                
                // Run preloading in parallel without blocking the UI
                Promise.all(preloadPromises).catch(error => {
                    console.error('[preloadAdjacentWeeks] Error preloading adjacent weeks:', error);
                });
            } else {
                console.log('[preloadAdjacentWeeks] Adjacent weeks already cached, skipping preload');
            }
        } catch (error) {
            console.error('[preloadAdjacentWeeks] Error in preload process:', error);
        }
    };

    // Add a debounced version of loadAvailableUsers
    const debouncedLoadAvailableUsers = () => {
        console.log('Debounced load users called');
        
        // Clear any existing timer
        if (loadUsersTimerRef.current) {
            clearTimeout(loadUsersTimerRef.current);
        }
        
        // Set a new timer
        loadUsersTimerRef.current = setTimeout(() => {
            // Only load if we're not already loading
            if (!isLoadingUsers) {
                loadAvailableUsers();
            } else {
                console.log('Already loading users, skipping request');
            }
        }, 300); // 300ms debounce time
    };

    const loadBusinesses = async () => {
        try {
            setIsLoadingBusinesses(true);
            const data = await businessService.getBusinesses();
            setBusinesses(data);

            // If no business is selected, try to select the user's business or the first one
            if (data.length > 0 && !selectedBusiness) {
                // First try to find the user's business from businessId context
                const userBusiness = businessId ? data.find(b => b.id === businessId) : null;
                if (userBusiness) {
                    setSelectedBusiness(userBusiness);
                } else {
                    // Fallback to first business (now correctly ordered)
                    setSelectedBusiness(data[0]);
                }
            }
        } catch (error) {
            console.error('Error loading businesses:', error);
            setError('Failed to load businesses');
        } finally {
            setIsLoadingBusinesses(false);
        }
    };

    /**
     * Sync customer and job data from appointments to IndexedDB
     * @param appointments Array of appointments to extract customer/job data from
     * @param businessId The business ID
     */
    const syncCustomerAndJobData = async (appointments: any[], businessId: string) => {
        try {
            console.log('[syncCustomerAndJobData] Starting...');
            
            // Extract unique customers and jobs from appointments
            const customersMap = new Map<string, any>();
            const jobsMap = new Map<string, any>();
            
            for (const appointment of appointments) {
                // Extract customer data if available
                if (appointment.customerId && appointment.customerName) {
                    if (!customersMap.has(appointment.customerId)) {
                        customersMap.set(appointment.customerId, {
                            id: appointment.customerId,
                            firstName: appointment.customerName.split(' ')[0] || '',
                            lastName: appointment.customerName.split(' ').slice(1).join(' ') || '',
                            fullName: appointment.customerName,
                            email: appointment.customerEmail || '',
                            phoneNumber: appointment.customerPhone || '',
                            address: '',
                            city: '',
                            state: '',
                            zipCode: '',
                            country: '',
                            secondaryContactName: '',
                            secondaryContactPhone: '',
                            secondaryContactEmail: '',
                            source: 1, // Default to Website
                            sourceDisplayName: 'Website',
                            firstContactDate: new Date().toISOString(),
                            status: 1, // Default to Active
                            statusDisplayName: 'Active',
                            preferredCommunicationMethod: '',
                            notes: '',
                            createdDate: new Date().toISOString(),
                            modifiedDate: new Date().toISOString(),
                            jobCount: 0,
                            totalJobValue: 0,
                            lifetimeValue: 0,
                            businessId: businessId
                        });
                    }
                }
                
                // Extract job data if available
                if (appointment.jobId && appointment.customerId) {
                    if (!jobsMap.has(appointment.jobId)) {
                        jobsMap.set(appointment.jobId, {
                            id: appointment.jobId,
                            jobNumber: appointment.jobNumber || `JOB-${appointment.jobId.substring(0, 8)}`,
                            customerId: appointment.customerId,
                            customerName: appointment.customerName || '',
                            customerEmail: appointment.customerEmail || '',
                            customerPhone: appointment.customerPhone || '',
                            assignedSalesPersonId: '',
                            assignedSalesPersonName: '',
                            jobAddress: '',
                            statusId: 1,
                            statusName: 'New',
                            statusColor: '#4CAF50',
                            type: 1, // Default to KitchenDesign
                            estimatedValue: 0,
                            actualValue: 0,
                            priority: 2, // Default to Medium
                            createdDate: new Date().toISOString(),
                            expectedCompletionDate: '',
                            actualCompletionDate: '',
                            lastStatusChangeDate: new Date().toISOString(),
                            lastStatusChangedByName: '',
                            notes: '',
                            appointmentCount: 0,
                            documentCount: 0,
                            communicationCount: 0,
                            businessId: businessId
                        });
                    }
                }
            }
            
            // Store customers in IndexedDB
            if (customersMap.size > 0) {
                const customers = Array.from(customersMap.values());
                console.log(`[syncCustomerAndJobData] Storing ${customers.length} customers in IndexedDB`);
                await indexedDbService.storeCustomers(customers);
            }
            
            // Store jobs in IndexedDB
            if (jobsMap.size > 0) {
                const jobs = Array.from(jobsMap.values());
                console.log(`[syncCustomerAndJobData] Storing ${jobs.length} jobs in IndexedDB`);
                await indexedDbService.storeJobs(jobs);
            }
            
            console.log('[syncCustomerAndJobData] Completed successfully');
        } catch (error) {
            console.error('[syncCustomerAndJobData] Error syncing customer and job data:', error);
            // Don't throw error as this is not critical for appointment loading
        }
    };

    /**
     * Load all customers and jobs for the selected business
     */
    const loadCustomerAndJobData = async () => {
        try {
            if (!selectedBusiness) {
                console.log('[loadCustomerAndJobData] No selected business');
                return;
            }

            console.log('[loadCustomerAndJobData] Loading customer and job data...');
            
            // Load customers with API-first approach
            try {
                const customers = await customerService.getCustomers();
                if (customers.length > 0) {
                    console.log(`[loadCustomerAndJobData] Storing ${customers.length} customers in IndexedDB`);
                    await indexedDbService.storeCustomers(customers);
                }
            } catch (error) {
                console.error('[loadCustomerAndJobData] Error loading customers from API:', error);
                // Try to load from IndexedDB as fallback
                try {
                    const cachedCustomers = await indexedDbService.getCustomersForBusiness(selectedBusiness.id);
                    if (cachedCustomers.length > 0) {
                        console.log(`[loadCustomerAndJobData] Using ${cachedCustomers.length} cached customers`);
                    }
                } catch (indexedDbError) {
                    console.error('[loadCustomerAndJobData] Error loading customers from IndexedDB:', indexedDbError);
                }
            }
            
            // Load jobs with API-first approach
            try {
                const jobs = await jobService.getJobs();
                if (jobs.length > 0) {
                    console.log(`[loadCustomerAndJobData] Storing ${jobs.length} jobs in IndexedDB`);
                    await indexedDbService.storeJobs(jobs, selectedBusiness.id);
                }
            } catch (error) {
                console.error('[loadCustomerAndJobData] Error loading jobs from API:', error);
                // Try to load from IndexedDB as fallback
                try {
                    const cachedJobs = await indexedDbService.getJobsForBusiness(selectedBusiness.id);
                    if (cachedJobs.length > 0) {
                        console.log(`[loadCustomerAndJobData] Using ${cachedJobs.length} cached jobs`);
                    }
                } catch (indexedDbError) {
                    console.error('[loadCustomerAndJobData] Error loading jobs from IndexedDB:', indexedDbError);
                }
            }
            
            console.log('[loadCustomerAndJobData] Completed successfully');
        } catch (error) {
            console.error('[loadCustomerAndJobData] Error loading customer and job data:', error);
        }
    };

    const loadAppointments = async () => {
        try {
            console.log('[loadAppointments] Starting...');
            
            if (!selectedBusiness) {
                console.log('[loadAppointments] No selected business, clearing appointments');
                setAppointments([]);
                return;
            }

            const calendarApi = calendarRef.current?.getApi();
            if (!calendarApi) {
                console.log('[loadAppointments] Calendar API not available yet');
                return;
            }

            const start = calendarApi.view.activeStart;
            const end = calendarApi.view.activeEnd;

            console.log('[loadAppointments] Loading for range:', { start, end, businessId: selectedBusiness.id });

            if (appointmentsLoadedRef.current && 
                currentViewRef.current.start?.getTime() === start.getTime() && 
                currentViewRef.current.end?.getTime() === end.getTime()) {
                console.log('[loadAppointments] Appointments already loaded for this view, skipping');
                return;
            }

            // Phase 1: Always load from IndexedDB first
            console.log('[loadAppointments] Phase 1: Loading from IndexedDB...');
            let indexedDbAppointments: Appointment[] = [];

            // Determine effective appointment type filters based on selected user (for IndexedDB)
            let effectiveTypeFiltersForIndexedDb = selectedTypeFilters;
            if (selectedUserFilter && selectedUserFilter !== user?.id) {
                // When viewing another user's calendar, only show shared appointment types
                const sharedTypeIds = userSharedAppointmentTypes[selectedUserFilter] || [];
                if (selectedTypeFilters.length > 0) {
                    // Intersect with manually selected type filters
                    effectiveTypeFiltersForIndexedDb = selectedTypeFilters.filter(id => sharedTypeIds.includes(id));
                } else {
                    // No manual filter, use all shared types
                    effectiveTypeFiltersForIndexedDb = sharedTypeIds;
                }
            }

            try {
                // Use IndexedDB service directly to get appointments
                const rawAppointments = await indexedDbService.getAppointments(
                    start.toISOString(),
                    end.toISOString(),
                    effectiveTypeFiltersForIndexedDb
                );
                
                // Process appointments for offline mode (handles recurring appointment generation)
                if (rawAppointments.length > 0) {
                    console.log(`[loadAppointments] Found ${rawAppointments.length} raw appointments in IndexedDB`);
                    console.log(`[loadAppointments] Raw appointments sample:`, rawAppointments.slice(0, 3).map(apt => ({
                        id: apt.id,
                        title: apt.title,
                        recurrenceRule: apt.recurrenceRule,
                        recurringAppointmentId: apt.recurringAppointmentId,
                        startTime: apt.startTime
                    })));
                    
                    // Check for base recurring appointments (those with recurrenceRule)
                    const baseRecurringAppointments = rawAppointments.filter(apt => apt.recurrenceRule && apt.recurrenceRule.trim() !== '');
                    console.log(`[loadAppointments] Base recurring appointments in IndexedDB: ${baseRecurringAppointments.length}`);
                    
                    // Check for recurring instances (those with recurringAppointmentId)
                    const recurringInstances = rawAppointments.filter(apt => apt.recurringAppointmentId);
                    console.log(`[loadAppointments] Recurring instances in IndexedDB: ${recurringInstances.length}`);
                    
                    if (baseRecurringAppointments.length > 0) {
                        console.log(`[loadAppointments] Base recurring appointments:`, baseRecurringAppointments.map(apt => ({
                            id: apt.id,
                            title: apt.title,
                            recurrenceRule: apt.recurrenceRule,
                            startTime: apt.startTime
                        })));
                    }
                    
                    // OFFLINE FIX: If we have base recurring appointments, use normal processing
                    // If we only have instances, use them directly without trying to regenerate
                    if (baseRecurringAppointments.length > 0) {
                        console.log(`[loadAppointments] Using normal processing with base recurring appointments`);
                        indexedDbAppointments = await appointmentService['processAppointmentsForOffline'](
                            rawAppointments,
                            start,
                            end
                        );
                    } else if (recurringInstances.length > 0) {
                        console.log(`[loadAppointments] No base recurring appointments found, using existing instances directly (same as online behavior)`);
                        
                        // Filter all appointments to the current date range (same logic as online)
                        indexedDbAppointments = rawAppointments.filter(appointment => {
                            const appointmentStart = new Date(appointment.startTime);
                            const appointmentEnd = new Date(appointment.endTime);
                            return appointmentStart <= end && appointmentEnd >= start;
                        });
                        
                        console.log(`[loadAppointments] Using ${indexedDbAppointments.length} appointments directly from IndexedDB (same as online behavior)`);
                    } else {
                        console.log(`[loadAppointments] No recurring data found, using normal processing`);
                        indexedDbAppointments = await appointmentService['processAppointmentsForOffline'](
                            rawAppointments,
                            start,
                            end
                        );
                    }
                    
                    console.log(`[loadAppointments] After processAppointmentsForOffline: ${indexedDbAppointments.length} appointments`);
                    console.log(`[loadAppointments] Processed appointments sample:`, indexedDbAppointments.slice(0, 3).map(apt => ({
                        id: apt.id,
                        title: apt.title,
                        isRecurringInstance: apt.isRecurringInstance,
                        recurringAppointmentId: apt.recurringAppointmentId,
                        startTime: apt.startTime
                    })));
                } else {
                    console.log(`[loadAppointments] No raw appointments found in IndexedDB for date range`);
                    indexedDbAppointments = [];
                }

                // Apply user filtering - default to current user if no filter selected
                const effectiveUserFilter = selectedUserFilter || user?.id;
                indexedDbAppointments = indexedDbAppointments.filter(appointment =>
                    appointment.userId === effectiveUserFilter
                );

                console.log(`[loadAppointments] Phase 1: Retrieved ${indexedDbAppointments.length} appointments from IndexedDB`);
                
                // Log all appointments being added to the calendar
                console.log('[loadAppointments] Appointments being added to calendar:', indexedDbAppointments.map(appointment => ({
                    id: appointment.id,
                    title: appointment.title || appointment.description,
                    startTime: appointment.startTime,
                    endTime: appointment.endTime,
                    appointmentType: appointment.appointmentTypeId,
                    isRecurring: !!appointment.recurringAppointmentId,
                    recurringAppointmentId: appointment.recurringAppointmentId
                })));
                
                // Always set appointments from IndexedDB first
                console.log(`[loadAppointments] Setting ${indexedDbAppointments.length} appointments to calendar state`);
                setAppointments(indexedDbAppointments);
                appointmentsLoadedRef.current = true;
                currentViewRef.current = { start, end };
                
                // Log the appointments that were just set
                console.log(`[loadAppointments] Appointments set to calendar state:`, indexedDbAppointments.map(apt => ({
                    id: apt.id,
                    title: apt.title || apt.description,
                    startTime: apt.startTime,
                    isRecurring: !!apt.recurringAppointmentId
                })));
                
            } catch (indexedDbError) {
                console.error('[loadAppointments] Error loading from IndexedDB:', indexedDbError);
                // If IndexedDB fails completely, show empty state
                setAppointments([]);
                appointmentsLoadedRef.current = true;
                currentViewRef.current = { start, end };
            }

            // Phase 2: Fetch fresh data from API in background to update IndexedDB
            console.log('[loadAppointments] Phase 2: Fetching from API to sync IndexedDB...');
            console.log(`[loadAppointments] Current appointments state before API call: ${indexedDbAppointments.length}`);
            setIsLoadingAppointments(true);
            
            try {
                // Determine effective appointment type filters based on selected user
                let effectiveAppointmentTypeIds = selectedTypeFilters;
                if (selectedUserFilter && selectedUserFilter !== user?.id) {
                    // When viewing another user's calendar, only show shared appointment types
                    const sharedTypeIds = userSharedAppointmentTypes[selectedUserFilter] || [];
                    if (selectedTypeFilters.length > 0) {
                        // Intersect with manually selected type filters
                        effectiveAppointmentTypeIds = selectedTypeFilters.filter(id => sharedTypeIds.includes(id));
                    } else {
                        // No manual filter, use all shared types
                        effectiveAppointmentTypeIds = sharedTypeIds;
                    }
                }

                const params = {
                    startDate: start.toISOString(),
                    endDate: end.toISOString(),
                    appointmentTypeIds: effectiveAppointmentTypeIds,
                    targetUserId: selectedUserFilter || undefined
                };

                console.log('[loadAppointments] API params:', params);
                const freshData = await appointmentService.getAppointments(params);
                
                console.log('[loadAppointments] Received fresh appointments from API:', freshData.length);
                
                // Check what the API returned - look for base recurring appointments
                const apiBaseRecurring = freshData.filter(apt => apt.recurrenceRule && apt.recurrenceRule.trim() !== '');
                const apiRecurringInstances = freshData.filter(apt => apt.recurringAppointmentId);
                console.log(`[loadAppointments] API returned: ${apiBaseRecurring.length} base recurring appointments, ${apiRecurringInstances.length} recurring instances`);
                
                if (apiBaseRecurring.length > 0) {
                    console.log(`[loadAppointments] API base recurring appointments:`, apiBaseRecurring.map(apt => ({
                        id: apt.id,
                        title: apt.title,
                        recurrenceRule: apt.recurrenceRule,
                        startTime: apt.startTime
                })));
                }

                // Check if we need to preserve base recurring appointments from IndexedDB
                const existingAppointments = await indexedDbService.getAppointments(
                    start.toISOString(),
                    end.toISOString(),
                    effectiveAppointmentTypeIds
                );
                const existingBaseRecurring = existingAppointments.filter(apt => apt.recurrenceRule && apt.recurrenceRule.trim() !== '');
                
                if (existingBaseRecurring.length > 0 && apiBaseRecurring.length === 0) {
                    console.log(`[loadAppointments] Preserving ${existingBaseRecurring.length} base recurring appointments from IndexedDB (API only returned instances)`);
                    
                    // Merge API appointments with existing base recurring appointments
                    const combinedAppointments = [...freshData, ...existingBaseRecurring];
                    console.log(`[loadAppointments] Combining ${freshData.length} API appointments with ${existingBaseRecurring.length} base recurring appointments`);
                    
                    // Update IndexedDB with combined data
                    console.log('[loadAppointments] Updating IndexedDB with combined data...');
                    await indexedDbService.mergeCachedAppointments(
                        combinedAppointments,
                        selectedBusiness.id,
                        { start, end }
                    );
                    console.log('[loadAppointments] IndexedDB updated with combined data');
                    
                    // Sync customer and job data from combined appointments
                    await syncCustomerAndJobData(combinedAppointments, selectedBusiness.id);
                } else {
                    // Update IndexedDB with fresh data from API
                    console.log('[loadAppointments] Updating IndexedDB with fresh data...');
                    await indexedDbService.mergeCachedAppointments(
                        freshData,
                        selectedBusiness.id,
                        { start, end }
                    );
                    console.log('[loadAppointments] IndexedDB updated with fresh data from API');
                }

                // Mark range as fetched
                await indexedDbService.markRangeFetched(start, end, selectedBusiness.id);
                
                // Sync customer and job data from appointments
                if (selectedBusiness) {
                    await syncCustomerAndJobData(freshData, selectedBusiness.id);
                }
                
                // Reload from IndexedDB to get the updated data
                console.log('[loadAppointments] Reloading from updated IndexedDB...');
                const updatedRawAppointments = await indexedDbService.getAppointments(
                    start.toISOString(),
                    end.toISOString(),
                    effectiveAppointmentTypeIds
                );
                
                if (updatedRawAppointments.length > 0) {
                    const updatedAppointments = await appointmentService['processAppointmentsForOffline'](
                        updatedRawAppointments,
                        start,
                        end
                    );
                    
                    // Apply user filtering - default to current user if no filter selected
                    const effectiveUserFilter = selectedUserFilter || user?.id;
                    let finalAppointments = updatedAppointments.filter(appointment =>
                        appointment.userId === effectiveUserFilter
                    );
                    
                    // Update UI with fresh data from IndexedDB
                    setAppointments(finalAppointments);
                    console.log(`[loadAppointments] Updated UI with ${finalAppointments.length} appointments from synced IndexedDB`);
                    
                    // Log all appointments being updated after API sync
                    console.log('[loadAppointments] Updated appointments after API sync:', finalAppointments.map(appointment => ({
                        id: appointment.id,
                        title: appointment.title || appointment.description,
                        startTime: appointment.startTime,
                        endTime: appointment.endTime,
                        appointmentType: appointment.appointmentTypeId,
                        isRecurring: !!appointment.recurringAppointmentId,
                        recurringAppointmentId: appointment.recurringAppointmentId
                    })));
                }
                
                console.log('[loadAppointments] Complete!');
            } catch (apiError) {
                console.log('[loadAppointments] API call failed (offline or error), using IndexedDB data only:', apiError);
                console.log(`[loadAppointments] IndexedDB appointments available when offline: ${indexedDbAppointments.length}`);
                
                if (indexedDbAppointments.length > 0) {
                    console.log(`[loadAppointments] Recurring appointments in IndexedDB:`, indexedDbAppointments.filter(apt => apt.isRecurringInstance || apt.recurringAppointmentId).map(apt => ({
                        id: apt.id,
                        title: apt.title,
                        isRecurringInstance: apt.isRecurringInstance,
                        recurringAppointmentId: apt.recurringAppointmentId,
                        startTime: apt.startTime
                    })));
                    
                    // Ensure appointments remain displayed when offline
                    console.log(`[loadAppointments] Ensuring ${indexedDbAppointments.length} appointments remain displayed when offline`);
                    setAppointments(indexedDbAppointments);
                }
                
                // API failed, but we already have IndexedDB data loaded, so we're good
                if (indexedDbAppointments.length === 0) {
                    setError('Unable to load appointments (offline)');
                }
            }
        } catch (error) {
            console.error('[loadAppointments] Unexpected error:', error);
            setError('Failed to load appointments');
        } finally {
            setIsLoadingAppointments(false);
        }
    };

    const loadAppointmentTypes = async () => {
        try {
            if (!selectedBusiness) {
                console.log('[loadAppointmentTypes] No selected business, clearing appointment types');
                setAppointmentTypes([]);
                return;
            }

            console.log('[loadAppointmentTypes] Loading for business:', selectedBusiness.id, selectedBusiness.name);
            setIsLoadingAppointmentTypes(true);
            const data = await appointmentTypeService.ensureAppointmentTypesLoaded(selectedBusiness.id);
            console.log('[loadAppointmentTypes] Received appointment types:', data);
            setAppointmentTypes(data);

            // Always select all appointment types by default
            setSelectedTypeFilters(data.map(type => type.id));
        } catch (error) {
            console.error('Error loading appointment types:', error);
            setError('Failed to load appointment types');
        } finally {
            setIsLoadingAppointmentTypes(false);
            setAppointmentTypesLoaded(true);
        }
    };

    const loadCustomers = async (searchTerm: string = '') => {
        try {
            console.log('loadCustomers called with:', {
                searchTerm,
                selectedBusiness: selectedBusiness?.id,
                userBusinessId: user?.businessId,
                userId: user?.id,
                userEmail: user?.email
            });
            
            if (!selectedBusiness || !user?.businessId) {
                console.log('loadCustomers: Missing required data, clearing customers');
                setCustomers([]);
                return;
            }
            
            setIsLoadingCustomers(true);
            console.log('loadCustomers: Calling getCustomers API');
            const data = await customerService.getCustomers({ 
                searchTerm: searchTerm.trim() || undefined 
            });
            console.log('loadCustomers: Received customers:', data.length);
            setCustomers(data);
        } catch (error) {
            console.error('Error loading customers:', error);
        } finally {
            setIsLoadingCustomers(false);
        }
    };

    // Debounced customer search function
    const debouncedSearchCustomers = (searchTerm: string) => {
        // Clear any existing timer
        if (customerSearchTimerRef.current) {
            clearTimeout(customerSearchTimerRef.current);
        }
        
        // Set a new timer
        customerSearchTimerRef.current = setTimeout(() => {
            loadCustomers(searchTerm);
        }, 300); // 300ms debounce time
    };

    const loadAvailableUsers = async () => {
        if (!selectedBusiness) return;

        // If already loading, don't start another load
        if (isLoadingUsers) {
            return;
        }

        console.log('Loading available users for business:', selectedBusiness.id);

        // Set loading state
        setIsLoadingUsers(true);

        try {
            // Fetch both business users and shared appointment types in parallel
            const [users, shares] = await Promise.all([
                businessService.getBusinessUsers(selectedBusiness.id),
                appointmentTypeShareService.getMyShares()
            ]);
            console.log('Loaded users:', users);
            console.log('Loaded shares:', shares);

            // Build mapping of userId -> appointmentTypeIds from shares
            const userAppointmentTypesMap: Record<string, string[]> = {};
            shares.forEach(share => {
                if (!userAppointmentTypesMap[share.sharedByUserId]) {
                    userAppointmentTypesMap[share.sharedByUserId] = [];
                }
                if (!userAppointmentTypesMap[share.sharedByUserId].includes(share.appointmentTypeId)) {
                    userAppointmentTypesMap[share.sharedByUserId].push(share.appointmentTypeId);
                }
            });
            setUserSharedAppointmentTypes(userAppointmentTypesMap);
            console.log('User shared appointment types map:', userAppointmentTypesMap);

            // Get current user
            const currentUser = users.find(u => u.id === user?.id);

            // Only include users who have shared appointment types with the current user
            const usersWithShares = Object.keys(userAppointmentTypesMap);
            const sharedUsersFiltered = users.filter(u =>
                u.id !== user?.id && usersWithShares.includes(u.id)
            );

            // If current user has access to appointments (their own or others)
            if (currentUser) {
                // Include current user and users who have shared with them, then sort by admin/name
                const allUsers = sortUsers([currentUser, ...sharedUsersFiltered]);
                console.log('Setting available users (current + shared, sorted):', allUsers);
                setAvailableUsers(allUsers);
                // Keep the persisted filter if it refers to a valid user, otherwise default to current user
                setSelectedUserFilter(prev => {
                    if (prev && allUsers.some(u => u.id === prev)) {
                        return prev;
                    }
                    return currentUser.id;
                });
            } else {
                console.log('Current user not found in business users');
                // Current user has no access to any appointments
                setAvailableUsers([]);
                setSelectedUserFilter(null);
            }
        } catch (error) {
            console.error('Error loading available users:', error);
        } finally {
            setIsLoadingUsers(false);
        }
    };

    const handleDateSelect = async (selectInfo: DateSelectArg) => {
        if (!selectedBusiness) {
            showError("Please select a business first");
            return;
        }

        if (appointmentTypes.length === 0) {
            showError("No appointment types available. Please create an appointment type first.");
            return;
        }

        setError(null);
        setSuccess(null);

        // Always set the first appointment type
        const firstType = appointmentTypes[0];
        setSelectedAppointmentType(firstType);
        
        const newFormData = {
            ...formData,
            appointmentTypeId: firstType.id,
            start: selectInfo.start,
            end: selectInfo.end,
            isAllDay: selectInfo.allDay,
            userId: selectedUserFilter || user?.id
        };
        
        console.log('[handleDateSelect] Setting initial formData:', newFormData);
        console.log('[handleDateSelect] Current user:', user);
        setFormData(newFormData);

        setIsEditing(false);
        // Open dialog immediately
        setIsDialogOpen(true);

        // Load users who have shared their appointment types with the current user
        setIsLoadingUsers(true);
        console.log('[handleDateSelect] Starting to load users from appointment type shares, isLoadingUsers=true');
        appointmentTypeShareService.getMyShares()
            .then(shares => {
                console.log('[handleDateSelect] Shares loaded:', shares);

                // Extract unique users from shares
                const uniqueUserIds = new Set<string>();
                const usersFromShares: BusinessUser[] = [];

                shares.forEach(share => {
                    if (!uniqueUserIds.has(share.sharedByUserId)) {
                        uniqueUserIds.add(share.sharedByUserId);
                        // Split the full name into firstName and lastName
                        const nameParts = share.sharedByUserName.trim().split(' ');
                        const firstName = nameParts[0] || '';
                        const lastName = nameParts.slice(1).join(' ') || '';

                        usersFromShares.push({
                            id: share.sharedByUserId,
                            email: '',
                            firstName,
                            lastName,
                            role: '',
                            businessId: '',
                            businessRoles: []
                        });
                    }
                });

                // Add current user if not already in the list, then sort
                const currentUserInList = usersFromShares.some(u => u.id === user?.id);
                const allUsersUnsorted: BusinessUser[] = currentUserInList
                    ? usersFromShares
                    : [{
                        id: user?.id || '',
                        email: user?.email || '',
                        firstName: user?.firstName || '',
                        lastName: user?.lastName || '',
                        role: '',
                        businessId: '',
                        businessRoles: user?.businessRoles?.map(r => ({ role: r.role, businessId: r.businessId, isBusinessAdmin: r.isBusinessAdmin ?? false })) || []
                    }, ...usersFromShares];
                const allUsers = sortUsers(allUsersUnsorted);

                console.log('[handleDateSelect] Users from shares (sorted):', allUsers);
                setSharedUsers(allUsers);

                // Preserve the userId that was set initially (respects selectedUserFilter)
                setFormData(prev => {
                    console.log('[handleDateSelect] After users loaded - prev.userId:', prev.userId);
                    console.log('[handleDateSelect] After users loaded - selectedUserFilter:', selectedUserFilter);

                    // If userId is already set (from selectedUserFilter or current user), keep it
                    if (prev.userId) {
                        console.log('[handleDateSelect] Preserving existing userId:', prev.userId);
                        return prev;
                    }

                    // Fallback to current user if no userId set
                    console.log('[handleDateSelect] Setting userId to current user');
                    return {
                        ...prev,
                        userId: user?.id || ''
                    };
                });

                console.log('[handleDateSelect] Setting isLoadingUsers=false');
                setIsLoadingUsers(false);
            })
            .catch(error => {
                console.error('Error loading shared users:', error);
                showError('Failed to load shared users');
                setIsLoadingUsers(false);
            });

        // Load customers for selection asynchronously
        loadCustomers();
    };
    
    const handleCustomerCreated = async (customer: Customer) => {
        // Add the new customer to the customers list
        setCustomers(prev => [...prev, customer]);
        setSelectedCustomer(customer);
        setIsCustomerDialogOpen(false);
        
        // Update form data with customer ID
        setFormData(prev => ({
            ...prev,
            customerId: customer.id
        }));
        
        // Load jobs for the new customer
        try {
            const allJobs = await jobService.getJobs();
            const customerJobs = allJobs.filter(job => job.customerId === customer.id);
            setCustomerJobs(customerJobs);
        } catch (error) {
            console.error('Error loading customer jobs:', error);
            setCustomerJobs([]);
        }
        
        showSuccess('Customer created');
    };

    const handleEventClick = (clickInfo: EventClickArg) => {
        setError(null);
        setSuccess(null);
        const appointment = appointments.find(a => a.id === clickInfo.event.id);
        if (appointment) {
            console.log('Clicked appointment full data:', appointment);
            
            if (!appointment.hasWriteAccess) {
                showError("You don't have permission to edit this appointment");
                return;
            }

            // Ensure we're using the complete appointment object from our state
            setSelectedAppointment(appointment);
            
            // Go straight to edit mode
            if (appointment.recurringAppointmentId) {
                setRecurringAction('edit');
                // Don't set form data here, it will be set in startEdit after the recurring dialog
                setIsRecurringDialogOpen(true);
            } else {
                startEdit(appointment);
            }
        }
    };

    const handleEdit = () => {
        setError(null);
        setSuccess(null);
        if (selectedAppointment && !selectedAppointment.hasWriteAccess) {
            showError("You don't have permission to edit this appointment");
            return;
        }

        if (selectedAppointment?.recurringAppointmentId) {
            setRecurringAction('edit');
            // Don't set form data here, it will be set in startEdit after the recurring dialog
            setIsRecurringDialogOpen(true);
        } else {
            startEdit();
        }
    };

    const startEdit = async (appointmentOverride?: Appointment) => {
        const appointmentToUse = appointmentOverride || selectedAppointment;
        if (!appointmentToUse) return;

        // If editing a recurring series, fetch the parent appointment first
        let appointmentToEdit = appointmentToUse;
        let parentAppointment = null;
        if (appointmentToUse.recurringAppointmentId) {
            try {
                parentAppointment = await appointmentService.getAppointment(appointmentToUse.recurringAppointmentId);
                if (parentAppointment && isEditingSeries) {
                    // When editing a series, we must use the original parent appointment
                    appointmentToEdit = parentAppointment;
                }
            } catch (error) {
                console.error('Error fetching parent appointment:', error);
                showError('Failed to load recurring appointment details');
                return;
            }
        }

        const appointmentType = appointmentTypes.find(type => type.id === appointmentToEdit.appointmentTypeId);
        setSelectedAppointmentType(appointmentType || null);

        // Set the form data using the correct appointment
        const editFormData = {
            title: appointmentToEdit.title,
            description: appointmentToEdit.description,
            appointmentTypeId: appointmentToEdit.appointmentTypeId,
            address: appointmentToEdit.address || '',
            latitude: appointmentToEdit.latitude,
            longitude: appointmentToEdit.longitude,
            userId: appointmentToEdit.userId,
            start: new Date(appointmentToEdit.startTime),
            end: new Date(appointmentToEdit.endTime),
            isAllDay: appointmentToEdit.isAllDay,
            timeZoneId: appointmentToEdit.timeZoneId || Intl.DateTimeFormat().resolvedOptions().timeZone,
            recurrenceRule: appointmentToEdit.recurrenceRule || '',
            customerId: appointmentToEdit.customerId,
            jobId: appointmentToEdit.jobId
        };
        
        console.log('[startEdit] Setting initial formData:', editFormData);
        console.log('[startEdit] appointmentToEdit.userId:', appointmentToEdit.userId);
        console.log('[startEdit] Current user:', user);
        setFormData(editFormData);

        // Handle recurrence options
        const hasRecurrenceRule = Boolean(appointmentToEdit.recurrenceRule);
        // For single instances of recurring appointments, check if the parent has a recurrence rule
        const isRecurringInstance = appointmentToUse.recurringAppointmentId && !isEditingSeries;
        const parentHasRecurrenceRule = parentAppointment ? Boolean(parentAppointment.recurrenceRule) : false;
        setShowRecurrenceOptions(Boolean(hasRecurrenceRule || isEditingSeries || (isRecurringInstance && parentHasRecurrenceRule)));
        
        // Use parent recurrence rule for single instances, or appointment recurrence rule for series
        const recurrenceRuleToUse = isRecurringInstance && parentAppointment ? parentAppointment.recurrenceRule : appointmentToEdit.recurrenceRule;
        
        if (recurrenceRuleToUse) {
            const intervalMatch = recurrenceRuleToUse.match(/INTERVAL=(\d+)/);
            const interval = intervalMatch ? parseInt(intervalMatch[1]) : 1;
            
            const untilMatch = recurrenceRuleToUse.match(/UNTIL=(\d{8}T\d{6}Z)/);
            if (untilMatch) {
                const dateStr = untilMatch[1];
                // Parse the UTC date string from UNTIL
                const year = parseInt(dateStr.substring(0, 4));
                const month = parseInt(dateStr.substring(4, 6)) - 1;
                const day = parseInt(dateStr.substring(6, 8));
                const hours = parseInt(dateStr.substring(9, 11));
                const minutes = parseInt(dateStr.substring(11, 13));
                const seconds = parseInt(dateStr.substring(13, 15));
                const utcDate = new Date(Date.UTC(year, month, day, hours, minutes, seconds));
                setRecurrenceEndDate(utcDate);
            } else {
                setRecurrenceEndDate(null);
            }

            const pattern = recurrenceRuleToUse.split(';')[0].split('=')[1];
            handleRecurrenceChange(pattern, interval);
        } else {
            setRecurrenceEndDate(null);
            if (!isEditingSeries) {
                setFormData(prev => ({ ...prev, recurrenceRule: '' }));
            }
        }
        
        setIsEditing(true);
        // Open dialog immediately
        setIsDialogOpen(true);

        // Load users who have shared their appointment types with the current user
        if (appointmentType) {
            setIsLoadingUsers(true);
            console.log('[startEdit] Starting to load users from appointment type shares, isLoadingUsers=true');
            appointmentTypeShareService.getMyShares()
                .then(shares => {
                    console.log('[startEdit] Shares loaded:', shares);

                    // Extract unique users from shares
                    const uniqueUserIds = new Set<string>();
                    const usersFromShares: BusinessUser[] = [];

                    shares.forEach(share => {
                        if (!uniqueUserIds.has(share.sharedByUserId)) {
                            uniqueUserIds.add(share.sharedByUserId);
                            const nameParts = share.sharedByUserName.trim().split(' ');
                            const firstName = nameParts[0] || '';
                            const lastName = nameParts.slice(1).join(' ') || '';

                            usersFromShares.push({
                                id: share.sharedByUserId,
                                email: '',
                                firstName,
                                lastName,
                                role: '',
                                businessId: '',
                                businessRoles: []
                            });
                        }
                    });

                    // Add current user if not already in the list, then sort
                    const currentUserInList = usersFromShares.some(u => u.id === user?.id);
                    const allUsersUnsorted: BusinessUser[] = currentUserInList
                        ? usersFromShares
                        : [{
                            id: user?.id || '',
                            email: user?.email || '',
                            firstName: user?.firstName || '',
                            lastName: user?.lastName || '',
                            role: '',
                            businessId: '',
                            businessRoles: user?.businessRoles?.map(r => ({ role: r.role, businessId: r.businessId, isBusinessAdmin: r.isBusinessAdmin ?? false })) || []
                        }, ...usersFromShares];
                    const allUsers = sortUsers(allUsersUnsorted);

                    console.log('[startEdit] Users from shares (sorted):', allUsers);
                    setSharedUsers(allUsers);

                    // When editing, preserve the original userId from the appointment
                    // Only set userId for new appointments (when prev.userId is not set)
                    setFormData(prev => {
                        console.log('[startEdit] After users loaded - prev.userId:', prev.userId);
                        console.log('[startEdit] After users loaded - user?.id:', user?.id);

                        // If editing an existing appointment, preserve its userId
                        if (prev.userId) {
                            console.log('[startEdit] Preserving existing userId:', prev.userId);
                            return prev;
                        }

                        // For new appointments, set userId to current user
                        console.log('[startEdit] Setting userId to current user for new appointment');
                        return {
                            ...prev,
                            userId: user?.id || ''
                        };
                    });

                    console.log('[startEdit] Setting isLoadingUsers=false');
                    setIsLoadingUsers(false);
                })
                .catch(error => {
                    console.error('Error loading shared users:', error);
                    showError('Failed to load shared users');
                    setIsLoadingUsers(false);
                });
        }

        // Load customers for selection asynchronously
        loadCustomers();

        // If the appointment has a customer, load and set it asynchronously
        if (appointmentToEdit.customerId) {
            customerService.getCustomer(appointmentToEdit.customerId)
                .then(async (customer) => {
                    setSelectedCustomer(customer);
                    
                    // Load jobs for this customer
                    try {
                        const customerJobsList = await jobService.getJobsByCustomer(customer.id);
                        setCustomerJobs(customerJobsList);
                        
                        // If the appointment has a jobId, set the selected job
                        if (appointmentToEdit.jobId) {
                            let job = customerJobsList.find(j => j.id === appointmentToEdit.jobId);
                            
                            // If job not found in the filtered list, try to get it directly from IndexedDB
                            if (!job) {
                                try {
                                    console.log(`[MainCalendar] Job ${appointmentToEdit.jobId} not found in customer jobs list, trying to get directly from IndexedDB`);
                                    job = await indexedDbService.getJob(appointmentToEdit.jobId);
                                    if (job) {
                                        console.log(`[MainCalendar] Found job ${appointmentToEdit.jobId} directly from IndexedDB:`, job.jobNumber);
                                    }
                                } catch (error) {
                                    console.error(`[MainCalendar] Error getting job ${appointmentToEdit.jobId} from IndexedDB:`, error);
                                }
                            }
                            
                            if (job) {
                                setSelectedJob(job);
                            } else {
                                console.log(`[MainCalendar] Job ${appointmentToEdit.jobId} not found anywhere, creating fallback`);
                                // Job not found anywhere, create a fallback job object
                                const fallbackJob = {
                                    id: appointmentToEdit.jobId,
                                    jobNumber: appointmentToEdit.jobNumber || `JOB-${appointmentToEdit.jobId.substring(0, 8)}`,
                                    customerId: customer.id,
                                    customerName: customer.fullName,
                                    customerEmail: customer.email,
                                    customerPhone: customer.phoneNumber,
                                    assignedSalesPersonId: '',
                                    assignedSalesPersonName: '',
                                    jobAddress: '',
                                    statusId: 1,
                                    statusName: 'New',
                                    statusColor: '#4CAF50',
                                    type: 1, // Default to KitchenDesign
                                    estimatedValue: 0,
                                    actualValue: 0,
                                    priority: 2, // Default to Medium
                                    createdDate: new Date().toISOString(),
                                    expectedCompletionDate: '',
                                    actualCompletionDate: '',
                                    lastStatusChangeDate: new Date().toISOString(),
                                    lastStatusChangedByName: '',
                                    notes: '',
                                    appointmentCount: 0,
                                    documentCount: 0,
                                    communicationCount: 0
                                };
                                setSelectedJob(fallbackJob);
                            }
                        }
                    } catch (error) {
                        console.error('Error loading customer jobs:', error);
                        // In offline mode, this might fail if data isn't cached
                        // Set empty array to prevent UI issues
                        setCustomerJobs([]);
                    }
                })
                .catch(async (error) => {
                    console.error('Error loading customer:', error);
                    // In offline mode, customer might not be in IndexedDB
                    // Create a minimal customer object from appointment data
                    if (appointmentToEdit.customerName && appointmentToEdit.customerId) {
                        const fallbackCustomer = {
                            id: appointmentToEdit.customerId,
                            firstName: appointmentToEdit.customerName.split(' ')[0] || '',
                            lastName: appointmentToEdit.customerName.split(' ').slice(1).join(' ') || '',
                            fullName: appointmentToEdit.customerName,
                            email: appointmentToEdit.customerEmail || '',
                            phoneNumber: appointmentToEdit.customerPhone || '',
                            address: '',
                            city: '',
                            state: '',
                            zipCode: '',
                            country: '',
                            secondaryContactName: '',
                            secondaryContactPhone: '',
                            secondaryContactEmail: '',
                            source: 1, // Default to Website
                            sourceDisplayName: 'Website',
                            firstContactDate: new Date().toISOString(),
                            status: 1, // Default to Active
                            statusDisplayName: 'Active',
                            preferredCommunicationMethod: '',
                            notes: '',
                            createdDate: new Date().toISOString(),
                            modifiedDate: new Date().toISOString(),
                            jobCount: 0,
                            totalJobValue: 0,
                            lifetimeValue: 0,
                            isDifficultPayer: false
                        };
                        setSelectedCustomer(fallbackCustomer);
                        setCustomerJobs([]); // No jobs available in offline mode
                        
                        // If we have a jobId but no customer data, try to get the actual job from IndexedDB
                        if (appointmentToEdit.jobId && appointmentToEdit.customerId) {
                            try {
                                console.log(`[MainCalendar] Trying to get job ${appointmentToEdit.jobId} from IndexedDB for fallback customer`);
                                const job = await indexedDbService.getJob(appointmentToEdit.jobId);
                                if (job) {
                                    console.log(`[MainCalendar] Found job ${appointmentToEdit.jobId} from IndexedDB:`, job.jobNumber);
                                    setSelectedJob(job);
                                } else {
                                    console.log(`[MainCalendar] Job ${appointmentToEdit.jobId} not found in IndexedDB, creating fallback`);
                                    // Job not found, create a fallback job object
                                    const fallbackJob = {
                                        id: appointmentToEdit.jobId,
                                        jobNumber: appointmentToEdit.jobNumber || `JOB-${appointmentToEdit.jobId.substring(0, 8)}`,
                                        customerId: appointmentToEdit.customerId,
                                        customerName: appointmentToEdit.customerName || '',
                                        customerEmail: appointmentToEdit.customerEmail || '',
                                        customerPhone: appointmentToEdit.customerPhone || '',
                                        assignedSalesPersonId: '',
                                        assignedSalesPersonName: '',
                                        jobAddress: '',
                                        statusId: 1,
                                        statusName: 'New',
                                        statusColor: '#4CAF50',
                                        type: 1, // Default to KitchenDesign
                                        estimatedValue: 0,
                                        actualValue: 0,
                                        priority: 2, // Default to Medium
                                        createdDate: new Date().toISOString(),
                                        expectedCompletionDate: '',
                                        actualCompletionDate: '',
                                        lastStatusChangeDate: new Date().toISOString(),
                                        lastStatusChangedByName: '',
                                        notes: '',
                                        appointmentCount: 0,
                                        documentCount: 0,
                                        communicationCount: 0
                                    };
                                    setSelectedJob(fallbackJob);
                                }
                            } catch (error) {
                                console.error(`[MainCalendar] Error getting job ${appointmentToEdit.jobId} from IndexedDB:`, error);
                                // Create fallback job if IndexedDB access fails
                                const fallbackJob = {
                                    id: appointmentToEdit.jobId,
                                    jobNumber: appointmentToEdit.jobNumber || `JOB-${appointmentToEdit.jobId.substring(0, 8)}`,
                                    customerId: appointmentToEdit.customerId,
                                    customerName: appointmentToEdit.customerName || '',
                                    customerEmail: appointmentToEdit.customerEmail || '',
                                    customerPhone: appointmentToEdit.customerPhone || '',
                                    assignedSalesPersonId: '',
                                    assignedSalesPersonName: '',
                                    jobAddress: '',
                                    statusId: 1,
                                    statusName: 'New',
                                    statusColor: '#4CAF50',
                                    type: 1, // Default to KitchenDesign
                                    estimatedValue: 0,
                                    actualValue: 0,
                                    priority: 2, // Default to Medium
                                    createdDate: new Date().toISOString(),
                                    expectedCompletionDate: '',
                                    actualCompletionDate: '',
                                    lastStatusChangeDate: new Date().toISOString(),
                                    lastStatusChangedByName: '',
                                    notes: '',
                                    appointmentCount: 0,
                                    documentCount: 0,
                                    communicationCount: 0
                                };
                                setSelectedJob(fallbackJob);
                            }
                        }
                    }
                });
        }
    };

    const handleDelete = () => {
        setError(null);
        setSuccess(null);
        if (selectedAppointment && !selectedAppointment.hasWriteAccess) {
            showError("You don't have permission to delete this appointment");
            return;
        }

        // Only show recurring dialog for appointments that are actually recurring
        if (selectedAppointment?.recurrenceRule || selectedAppointment?.recurringAppointmentId) {
            setRecurringAction('delete');
            setIsRecurringDialogOpen(true);
        } else {
            if (!window.confirm('Are you sure you want to delete this appointment?')) {
                return;
            }
            deleteAppointment(selectedAppointment!.id);
        }
    };

    const deleteAppointment = async (appointmentId: string) => {
        try {
            setLoading(true);
            await appointmentService.deleteAppointment(appointmentId);
            
            // Remove from cache
            if (selectedBusiness) {
                await indexedDbService.removeCachedAppointment(appointmentId, selectedBusiness.id);
            }
            
            showSuccess('Appointment deleted successfully');
            setIsDialogOpen(false);
            
            // Force a refresh of the appointments
            appointmentsLoadedRef.current = false;
            await loadAppointmentsOptimized();
            
            // Also refresh the calendar view
            const calendarApi = calendarRef.current?.getApi();
            if (calendarApi) {
                calendarApi.refetchEvents();
            }
        } catch (error) {
            console.error('Error deleting appointment:', error);
            showError('Failed to delete appointment');
        } finally {
            setLoading(false);
        }
    };

    const handleAppointmentTypeChange = async (event: SelectChangeEvent<string>) => {
        const typeId = event.target.value;
        const selectedType = appointmentTypes.find(type => type.id === typeId);
        
        setFormData(prev => ({ ...prev, appointmentTypeId: typeId }));
        setSelectedAppointmentType(selectedType || null);

        if (selectedType) {
            try {
                const shares = await appointmentTypeShareService.getMyShares();
                console.log('[handleAppointmentTypeChange] Shares loaded:', shares);

                // Extract unique users from shares
                const uniqueUserIds = new Set<string>();
                const usersFromShares: BusinessUser[] = [];

                shares.forEach(share => {
                    if (!uniqueUserIds.has(share.sharedByUserId)) {
                        uniqueUserIds.add(share.sharedByUserId);
                        const nameParts = share.sharedByUserName.trim().split(' ');
                        const firstName = nameParts[0] || '';
                        const lastName = nameParts.slice(1).join(' ') || '';

                        usersFromShares.push({
                            id: share.sharedByUserId,
                            email: '',
                            firstName,
                            lastName,
                            role: '',
                            businessId: '',
                            businessRoles: []
                        });
                    }
                });

                // Add current user if not already in the list, then sort
                const currentUserInList = usersFromShares.some(u => u.id === user?.id);
                const allUsersUnsorted: BusinessUser[] = currentUserInList
                    ? usersFromShares
                    : [{
                        id: user?.id || '',
                        email: user?.email || '',
                        firstName: user?.firstName || '',
                        lastName: user?.lastName || '',
                        role: '',
                        businessId: '',
                        businessRoles: user?.businessRoles?.map(r => ({ role: r.role, businessId: r.businessId, isBusinessAdmin: r.isBusinessAdmin ?? false })) || []
                    }, ...usersFromShares];
                const allUsers = sortUsers(allUsersUnsorted);

                console.log('[handleAppointmentTypeChange] Users from shares (sorted):', allUsers);
                setSharedUsers(allUsers);

                // Only set the current user as default for new appointments, not when editing
                if (allUsers.length === 0 || (!isEditing && !formData.userId)) {
                    setFormData(prev => ({ ...prev, userId: user?.id }));
                }
            } catch (error) {
                console.error('Error loading shared users:', error);
                showError('Failed to load shared users');
            }
        } else {
            setSharedUsers([]);
        }
    };

    const validateForm = (): string | null => {
        if (formData.description?.length > 1000) {
            return "Description cannot be longer than 1000 characters";
        }
        if (!formData.appointmentTypeId) {
            return "Appointment Type is required";
        }
        if (!formData.start) {
            return "Start Time is required";
        }
        if (!formData.end) {
            return "End Time is required";
        }
        if (formData.end < formData.start) {
            return "End Time must be after Start Time";
        }
        // Address validation - only validate if address is provided
        if (formData.address && formData.address.trim()) {
            if (formData.address.length > 500) {
                return "Address cannot be longer than 500 characters";
            }
            if (formData.latitude !== undefined && (formData.latitude < -90 || formData.latitude > 90)) {
                return "Latitude must be between -90 and 90";
            }
            if (formData.longitude !== undefined && (formData.longitude < -180 || formData.longitude > 180)) {
                return "Longitude must be between -180 and 180";
            }
        }
        if (!formData.timeZoneId) {
            return "Time Zone is required";
        }
        if (formData.timeZoneId.length > 100) {
            return "Time Zone cannot be longer than 100 characters";
        }
        if (formData.recurrenceRule && formData.recurrenceRule.length > 200) {
            return "Recurrence Rule cannot be longer than 200 characters";
        }
        return null;
    };

    const handleClose = () => {
        setIsDialogOpen(false);
        // Don't reset state here - it will be reset in handleDialogExited after animation completes
    };

    const handleDialogExited = () => {
        // Reset state after dialog animation completes to prevent flashing
        setIsEditing(false);
        setIsEditingSeries(false);
        setSelectedAppointment(null);
        setSelectedAppointmentType(null);
        setError(null);
        setSuccess(null);
        setShowRecurrenceOptions(false);
        setRecurrenceEndDate(null);
        setFormData({
            title: '',
            description: '',
            appointmentTypeId: '',
            address: '',
            latitude: undefined,
            longitude: undefined,
            userId: undefined,
            start: new Date(),
            end: new Date(),
            isAllDay: false,
            timeZoneId: Intl.DateTimeFormat().resolvedOptions().timeZone,
            recurrenceRule: '',
            customerId: undefined,
            jobId: undefined
        });
        // Reset customer selection state
        setSelectedCustomer(null);
        setSelectedJob(null);
        setCustomerJobs([]);
        setCustomerSearchTerm('');
        setCustomers([]);
        // Reset loading states
        setIsLoadingUsers(false);
        setIsLoadingCustomers(false);
        // Clear users list to avoid showing stale data
        setSharedUsers([]);
    };

    const handleSubmit = async () => {
        try {
            if (!formData.appointmentTypeId) {
                showError('Appointment type is required');
                return;
            }

            setLoading(true);
            setError(null);

            let recurrenceRule = '';
            if (showRecurrenceOptions && formData.recurrenceRule) {
                recurrenceRule = formData.recurrenceRule;
                if (recurrenceEndDate) {
                    const utcDate = new Date(Date.UTC(
                        recurrenceEndDate.getFullYear(),
                        recurrenceEndDate.getMonth(),
                        recurrenceEndDate.getDate(),
                        0, 0, 0
                    ));
                    const utcYear = utcDate.getUTCFullYear();
                    const utcMonth = String(utcDate.getUTCMonth() + 1).padStart(2, '0');
                    const utcDay = String(utcDate.getUTCDate()).padStart(2, '0');
                    recurrenceRule += `;UNTIL=${utcYear}${utcMonth}${utcDay}T000000Z`;
                }
            }

            if (isEditing && selectedAppointment) {
                const appointmentData = {
                    id: selectedAppointment.id,
                    title: formData.title,
                    description: formData.description,
                    appointmentTypeId: formData.appointmentTypeId,
                    address: formData.address,
                    latitude: formData.latitude,
                    longitude: formData.longitude,
                    userId: formData.userId,
                    startTime: formatDateToLocalString(formData.start, true),
                    endTime: formatDateToLocalString(formData.end, true),
                    isAllDay: formData.isAllDay,
                    timeZoneId: formData.timeZoneId,
                    recurrenceRule: recurrenceRule || formData.recurrenceRule,
                    customerId: formData.customerId,
                    jobId: formData.jobId
                };

                let response;
                if (selectedAppointment.recurringAppointmentId && !isEditingSeries) {
                    // Updating a single instance of a recurring appointment
                    // Get the appointment's timezone
                    const appointmentTimeZone = formData.timeZoneId || Intl.DateTimeFormat().resolvedOptions().timeZone;
                    
                    // Create a normalized date that will be properly parsed on the server
                    // Use the original appointment's startTime to ensure we reference the correct instance
                    const originalStartTime = new Date(selectedAppointment.startTime);
                    
                    // Format the date in the appointment's timezone to ensure correct date
                    const dateFormatter = new Intl.DateTimeFormat('en-GB', {
                        timeZone: appointmentTimeZone,
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit'
                    });
                    
                    // Get the date part in the appointment's timezone
                    const originalDateFormatted = dateFormatter.format(originalStartTime);
                    
                    // Parse the formatted date and create a UTC midnight date
                    const dateParts = originalDateFormatted.split('/');
                    const normalizedDate = new Date(Date.UTC(
                        parseInt(dateParts[2]), // year
                        parseInt(dateParts[1]) - 1, // month (0-based)
                        parseInt(dateParts[0]) // day
                    ));
                    
                    console.log('Setting original date for recurring instance update:', {
                        originalAppointmentStartTime: selectedAppointment.startTime,
                        originalStartTimeObj: originalStartTime,
                        originalDateFormatted,
                        normalizedDate: normalizedDate.toISOString(),
                        appointmentTimeZone
                    });
                    
                    response = await appointmentService.updateRecurringInstance(
                        selectedAppointment.recurringAppointmentId,
                        {
                            ...appointmentData,
                            originalDate: normalizedDate
                        }
                    );
                } else if (isEditingSeries && selectedAppointment.recurringAppointmentId) {
                    // Updating the entire series
                    // Set the id on the appointment DTO
                    appointmentData.id = selectedAppointment.recurringAppointmentId;
                    response = await appointmentService.updateAppointment(appointmentData);
                } else {
                    // Updating a regular appointment
                    // Set the id on the appointment DTO
                    appointmentData.id = selectedAppointment.id;
                    response = await appointmentService.updateAppointment(appointmentData);
                }

                if (response.success === false) {
                    showError(response.errorMessage || 'An error occurred while updating the appointment');
                    return;
                }

                // Update cache with the updated appointment
                if (response && response.appointment && selectedBusiness) {
                    await indexedDbService.addOrUpdateCachedAppointment(response.appointment, selectedBusiness.id);
                }

                showSuccess('Appointment updated');
                handleClose();

                // Force reload appointments from API by resetting the loaded flag
                appointmentsLoadedRef.current = false;
            } else {
                const response = await appointmentService.createAppointment({
                    title: formData.title,
                    description: formData.description,
                    appointmentTypeId: formData.appointmentTypeId,
                    address: formData.address,
                    latitude: formData.latitude,
                    longitude: formData.longitude,
                    userId: formData.userId,
                    startTime: formatDateToLocalString(formData.start, true),
                    endTime: formatDateToLocalString(formData.end, true),
                    isAllDay: formData.isAllDay,
                    timeZoneId: formData.timeZoneId,
                    recurrenceRule: recurrenceRule || formData.recurrenceRule,
                    customerId: formData.customerId,
                    jobId: formData.jobId
                });

                if (response.success === false) {
                    showError(response.errorMessage || 'An error occurred while creating the appointment');
                    return;
                }

                // Update cache with the new appointment
                if (response && response.appointment && selectedBusiness) {
                    await indexedDbService.addOrUpdateCachedAppointment(response.appointment, selectedBusiness.id);
                }

                showSuccess('Appointment created successfully');
                handleClose();

                // Force reload appointments from API by resetting the loaded flag
                appointmentsLoadedRef.current = false;
            }

            await loadAppointments();
        } catch (error: any) {
            console.error('Error submitting appointment:', error);
            showError(extractApiErrorMessage(error, 'An error occurred while saving the appointment'));
        } finally {
            setLoading(false);
        }
    };

    const handleTypeFilterChange = (typeId: string) => {
        setSelectedTypeFilters(prev => 
            prev.includes(typeId) 
                ? prev.filter(id => id !== typeId)
                : [...prev, typeId]
        );
        
        // Reset the appointments loaded flag to force a reload when type filter changes
        appointmentsLoadedRef.current = false;
    };

    const handleUserFilterChange = (userId: string) => {
        // Toggle the user filter - if the same user is clicked, deselect it
        setSelectedUserFilter(prev => prev === userId ? null : userId);
        
        // Reset the appointments loaded flag to force a reload when user filter changes
        appointmentsLoadedRef.current = false;
    };

    const handleBusinessSelect = (business: Business | null) => {
        console.log('Business selected (full object):', business);
        console.log('Business hours settings:', {
            useBusinessHours: business?.useBusinessHours,
            start: business?.businessHoursStart,
            end: business?.businessHoursEnd,
            restrictToBusinessHours: business?.restrictToBusinessHours
        });
        setSelectedBusiness(business);
        if (business) {
            setBusinessId(business.id);
            // Load full business details
            businessService.getBusinessDetails(business.id)
                .then(details => {
                    console.log('Loaded business details:', details);
                    setSelectedBusiness({
                        ...business,
                        useBusinessHours: details.useBusinessHours,
                        businessHoursStart: details.businessHoursStart,
                        businessHoursEnd: details.businessHoursEnd,
                        restrictToBusinessHours: details.restrictToBusinessHours
                    });
                    // Also update the first day of week from business details
                    if (details.firstDayOfWeek !== undefined) {
                        console.log('Setting firstDayOfWeek from business details:', details.firstDayOfWeek);
                        setFirstDayOfWeek(details.firstDayOfWeek);
                    }
                })
                .catch(error => console.error('Error loading business details:', error));
        }
    };

    // Helper function to convert TimeSpan string to time string
    const convertTimeSpanToTime = (timeSpan: string | null): string => {
        if (!timeSpan) return '00:00';
        // TimeSpan format: "00:00:00"
        return timeSpan.substring(0, 5); // Take just HH:mm
    };

    // Helper function to get business hours configuration
    const getBusinessHoursConfig = () => {
        console.log('Business hours config check:', {
            business: selectedBusiness,
            useBusinessHours: selectedBusiness?.useBusinessHours,
            start: selectedBusiness?.businessHoursStart,
            end: selectedBusiness?.businessHoursEnd
        });

        if (selectedBusiness?.useBusinessHours && 
            selectedBusiness.businessHoursStart && 
            selectedBusiness.businessHoursEnd) {
            const config = {
                daysOfWeek: [0, 1, 2, 3, 4, 5, 6],
                startTime: convertTimeSpanToTime(selectedBusiness.businessHoursStart),
                endTime: convertTimeSpanToTime(selectedBusiness.businessHoursEnd),
                display: 'background',
                color: theme?.accentColor ? `${theme.accentColor}20` : '#f5f5f5'
            };
            console.log('Returning business hours config:', config);
            return config;
        }
        console.log('Business hours disabled - conditions not met');
        return false;
    };

    // Load appointments for adjacent periods (weeks or days, depending on view)
    const loadAdjacentPeriodAppointments = async (periodStart: Date, viewType: string) => {
        if (!selectedBusiness || !isMobile) return;
        
        try {
            const isDayView = viewType === 'timeGridDay' || viewType === 'listDay';
            
            let prevStart: Date, prevEnd: Date, nextStart: Date, nextEnd: Date;
            
            if (isDayView) {
                // Day view - load prev/next DAY
                const currentDay = startOfDay(periodStart);
                
                prevStart = addDays(currentDay, -1);
                prevEnd = endOfDay(prevStart);
                nextStart = addDays(currentDay, 1);
                nextEnd = endOfDay(nextStart);
                
                console.log('[loadAdjacentPeriodAppointments] Loading adjacent days:', {
                    prevDay: prevStart.toDateString(),
                    nextDay: nextStart.toDateString()
                });
            } else {
                // Week view - load prev/next WEEK
                const weekStartDate = startOfWeek(periodStart, { weekStartsOn: firstDayOfWeek as 0 | 1 | 2 | 3 | 4 | 5 | 6 });
                
                prevStart = addWeeks(weekStartDate, -1);
                prevEnd = endOfWeek(prevStart, { weekStartsOn: firstDayOfWeek as 0 | 1 | 2 | 3 | 4 | 5 | 6 });
                nextStart = addWeeks(weekStartDate, 1);
                nextEnd = endOfWeek(nextStart, { weekStartsOn: firstDayOfWeek as 0 | 1 | 2 | 3 | 4 | 5 | 6 });
                
                console.log('[loadAdjacentPeriodAppointments] Loading adjacent weeks:', {
                    prevWeek: `${prevStart.toDateString()} - ${prevEnd.toDateString()}`,
                    nextWeek: `${nextStart.toDateString()} - ${nextEnd.toDateString()}`
                });
            }
            
            // Load prev period appointments
            const prevPeriodData = await appointmentService.getAppointments({
                startDate: prevStart.toISOString(),
                endDate: prevEnd.toISOString(),
                appointmentTypeIds: selectedTypeFilters,
                targetUserId: selectedUserFilter || undefined
            });
            
            // Load next period appointments
            const nextPeriodData = await appointmentService.getAppointments({
                startDate: nextStart.toISOString(),
                endDate: nextEnd.toISOString(),
                appointmentTypeIds: selectedTypeFilters,
                targetUserId: selectedUserFilter || undefined
            });
            
            setPrevPeriodAppointments(prevPeriodData);
            setNextPeriodAppointments(nextPeriodData);
            
            console.log('[loadAdjacentPeriodAppointments] Loaded:', {
                prevCount: prevPeriodData.length,
                nextCount: nextPeriodData.length
            });
        } catch (error) {
            console.error('[loadAdjacentPeriodAppointments] Error:', error);
            // Don't show error to user - this is just for preview
            setPrevPeriodAppointments([]);
            setNextPeriodAppointments([]);
        }
    };

    const handleDatesSet = (arg: DatesSetArg) => {
        const newStart = arg.view.activeStart;
        const newEnd = arg.view.activeEnd;
        const currentStart = currentViewRef.current.start;
        const currentEnd = currentViewRef.current.end;
        
        // Update current view type
        setCurrentViewType(arg.view.type);
        
        if (!currentStart || !currentEnd || 
            newStart.getTime() !== currentStart.getTime() || 
            newEnd.getTime() !== currentEnd.getTime()) {
            
            console.log('[handleDatesSet] View changed, cancelling previous requests and loading new data');
            console.log('[handleDatesSet] New date range:', { 
                start: newStart.toISOString(), 
                end: newEnd.toISOString(),
                startDate: newStart.toDateString(),
                endDate: newEnd.toDateString(),
                viewType: arg.view.type
            });
            
            // Cancel any existing request immediately
            if (currentRequestRef.current) {
                console.log('[handleDatesSet] Cancelling previous request');
                currentRequestRef.current.abort();
                currentRequestRef.current = null;
            }
            
            currentViewRef.current = {
                start: newStart,
                end: newEnd
            };
            
            appointmentsLoadedRef.current = false;
            
            // Update current period start for adjacent period loading
            setCurrentPeriodStart(newStart);
            
            // Load adjacent period appointments for swipe preview (mobile only, for week and day views)
            const isSwipeableView = arg.view.type === 'timeGridWeek' || arg.view.type === 'listWeek' || arg.view.type === 'timeGridDay';
            if (isMobile && isSwipeableView) {
                loadAdjacentPeriodAppointments(newStart, arg.view.type);
            }
            
            if (selectedBusiness && selectedTypeFilters.length > 0) {
                // Use the regular loadAppointments function instead of optimized
                loadAppointments();
            }
        }
    };

    // Determine effective appointment type filters for display
    const effectiveDisplayTypeFilters = (() => {
        if (selectedUserFilter && selectedUserFilter !== user?.id) {
            // When viewing another user's calendar, filter by shared appointment types
            const sharedTypeIds = userSharedAppointmentTypes[selectedUserFilter] || [];
            if (selectedTypeFilters.length > 0) {
                // Intersect with manually selected type filters
                return selectedTypeFilters.filter(id => sharedTypeIds.includes(id));
            }
            // No manual filter, use all shared types
            return sharedTypeIds;
        }
        // Viewing current user's calendar, use selected type filters
        return selectedTypeFilters;
    })();

    const getAppointmentColor = (appointment: Appointment): string => {
        return getAppointmentColorUtil(appointment, appointmentTypes);
    };

    const getAppointmentTextColor = (appointment: Appointment): string => {
        return getAppointmentTextColorUtil(appointment, appointmentTypes);
    };

    const filteredEvents = appointments
        .filter(appointment => {
            const appointmentType = appointmentTypes.find(type => type.id === appointment.appointmentTypeId);
            // Only filter by appointment types (user filtering is now handled by the API)
            return appointmentType &&
                   effectiveDisplayTypeFilters.includes(appointment.appointmentTypeId);
        })
        .map(appointment => {
            // For all-day events, FullCalendar needs the end time to be the day after the actual end date
            // This is for display purposes only - the original appointment data is preserved
            let endTime = appointment.endTime;
            if (appointment.isAllDay) {
                const endDate = new Date(appointment.endTime);
                endDate.setDate(endDate.getDate() + 1);
                endTime = endDate.toISOString();
            }

            return {
                id: appointment.id,
                title: appointment.description,
                start: appointment.startTime,
                end: endTime,
                allDay: appointment.isAllDay,
                backgroundColor: getAppointmentColor(appointment),
                textColor: getAppointmentTextColor(appointment),
                extendedProps: {
                    description: appointment.description,
                    appointmentTypeId: appointment.appointmentTypeId,
                    recurringAppointmentId: appointment.recurringAppointmentId,
                    recurrenceRule: appointment.recurrenceRule,
                    appointment: appointment,
                    customerName: appointment.customerName,
                    customerEmail: appointment.customerEmail,
                    customerPhone: appointment.customerPhone
                }
            };
        });

    const handleRecurrenceChange = (pattern: string, interval: number = 1) => {
        if (!pattern) {
            setFormData(prev => ({ ...prev, recurrenceRule: '' }));
            setRecurrenceEndDate(null);
            return;
        }

        let rule = `FREQ=${pattern}`;
        
        // Add interval if greater than 1
        if (interval > 1) {
            rule += `;INTERVAL=${interval}`;
        }

        // Add end date if set
        if (recurrenceEndDate) {
            // Set the end date to the start of the next day in UTC
            const endDate = new Date(recurrenceEndDate);
            endDate.setDate(endDate.getDate() + 1);
            endDate.setHours(0, 0, 0, 0);
            
            // Convert to UTC string format
            const utcYear = endDate.getUTCFullYear();
            const utcMonth = String(endDate.getUTCMonth() + 1).padStart(2, '0');
            const utcDay = String(endDate.getUTCDate()).padStart(2, '0');
            
            // Use UTC midnight of the next day to ensure the last day is included
            rule += `;UNTIL=${utcYear}${utcMonth}${utcDay}T000000Z`;
        }

        setFormData(prev => ({ ...prev, recurrenceRule: rule }));
    };

    /**
     * Format a date to a local ISO string format that preserves timezone information
     * @param date The date to format
     * @param includeSeconds Whether to include seconds in the formatted string
     * @returns A formatted date string in ISO format
     */
    const formatDateToLocalString = (date: Date, includeSeconds: boolean = true): string => {
        // Get the timezone offset in minutes
        const tzOffset = date.getTimezoneOffset();
        
        // Log the timezone information for debugging
        console.log('Formatting date with timezone info:', {
            date,
            dateUTC: date.toUTCString(),
            dateISO: date.toISOString(),
            dateLocalString: date.toString(),
            tzOffset,
            localTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
        });
        
        // Format the date as an ISO string (in UTC)
        const isoString = date.toISOString();
        
        console.log('Formatted date:', {
            original: date.toString(),
            isoString
        });
        
        return isoString;
    };

    const handleEventDrop = async (dropInfo: EventDropArg) => {
        try {
            // Add debugging for the entire drop event
            console.log('Event drop detected:', {
                eventId: dropInfo.event.id,
                oldEvent: {
                    start: dropInfo.oldEvent.start?.toString(),
                    startISO: dropInfo.oldEvent.start?.toISOString(),
                    end: dropInfo.oldEvent.end?.toString()
                },
                newEvent: {
                    start: dropInfo.event.start?.toString(),
                    startISO: dropInfo.event.start?.toISOString(),
                    end: dropInfo.event.end?.toString()
                },
                delta: {
                    days: dropInfo.delta.days,
                    milliseconds: dropInfo.delta.milliseconds
                }
            });
            
            // Early validation - if we don't have start times, we can't proceed
            if (!dropInfo.event.start) {
                console.error('Missing new start time for drag operation');
                return;
            }
            
            // Use the browser's timezone directly where needed instead of storing in a variable
            // to avoid redeclaration issues
            console.log('Current browser timezone:', Intl.DateTimeFormat().resolvedOptions().timeZone);
            
            // Check if we're offline first - use both the service and the browser's status
            const serviceOfflineStatus = !offlineService.getOnlineStatus();
            const browserOfflineStatus = !navigator.onLine;
            const isOffline = serviceOfflineStatus || browserOfflineStatus;
            
            console.log('Event drop - Offline detection:', {
                serviceOfflineStatus,
                browserOfflineStatus,
                isOffline
            });
            
            // Get the appointment that was dragged
            let appointment = appointments.find(a => a.id === dropInfo.event.id);
            
            // If appointment is not found in the current state, try to extract basic information from the event
            // This can happen during rapid drag operations where the state might be temporarily out of sync
            if (!appointment) {
                console.warn('Appointment not immediately found in state, attempting recovery:', dropInfo.event.id);
                
                // Try to extract basic appointment information from the event object
                const eventProps = dropInfo.event.extendedProps || {};
                
                // Create a temporary appointment object with the minimum required fields
                appointment = {
                    id: dropInfo.event.id,
                    title: dropInfo.event.title,
                    startTime: dropInfo.event.start?.toISOString() || '',
                    endTime: dropInfo.event.end?.toISOString() || '',
                    description: eventProps.description || '',
                    appointmentTypeId: eventProps.appointmentTypeId || '',
                    recurringAppointmentId: eventProps.recurringAppointmentId || undefined,
                    recurrenceRule: eventProps.recurrenceRule || undefined,
                    isAllDay: dropInfo.event.allDay || false,
                    timeZoneId: Intl.DateTimeFormat().resolvedOptions().timeZone,
                    businessId: selectedBusiness?.id || '',
                    hasWriteAccess: true,
                    createdAt: new Date().toISOString(),
                    updatedAt: new Date().toISOString()
                };
                
                console.log('Created recovery appointment object:', appointment);
                
                // If we still don't have enough info, try to reload appointments and abort the operation
                if (!appointment.appointmentTypeId) {
                    console.error('Insufficient data for appointment recovery, aborting update:', dropInfo.event.id);
                    dropInfo.revert();
                    await loadAppointments();
                    return;
                }
            }
            
            console.log('Dragged appointment:', appointment);

            // TypeScript safety check - this shouldn't happen due to our recovery logic above
            if (!appointment) {
                console.error('Appointment still undefined after recovery attempts');
                dropInfo.revert();
                await loadAppointments();
                return;
            }
            
            // At this point, TypeScript should know appointment is defined, but we'll add an assertion to be sure
            const safeAppointment = appointment as Appointment;  // Type assertion to satisfy TypeScript

            const appointmentType = appointmentTypes.find(type => type.id === safeAppointment.appointmentTypeId);
            const business = appointmentType?.business;
            
            // Validate business hours if restricted
            if (business?.useBusinessHours && business?.restrictToBusinessHours && 
                business?.businessHoursStart && business?.businessHoursEnd) {
                const [startHours, startMinutes] = business.businessHoursStart.split(':').map(Number);
                const [endHours, endMinutes] = business.businessHoursEnd.split(':').map(Number);

                const startTime = new Date(dropInfo.event.start!);
                const endTime = new Date(dropInfo.event.end!);
                const businessStart = new Date(startTime);
                const businessEnd = new Date(startTime);

                businessStart.setHours(startHours, startMinutes, 0);
                businessEnd.setHours(endHours, endMinutes, 0);

                if (startTime < businessStart || endTime > businessEnd) {
                    dropInfo.revert();
                    showError("Appointments must be within business hours");
                    return;
                }
            }

            // Check if this is a recurring instance and we're moving it to a date
            // that already has another instance of the same series
            if (safeAppointment.recurringAppointmentId && dropInfo.event.start) {
                // Get the appointment's timezone (this is the business timezone)
                const appointmentTimeZone = safeAppointment.timeZoneId || Intl.DateTimeFormat().resolvedOptions().timeZone;
                
                // Format dates in the business timezone, not the browser's local timezone
                // We need to create the date strings in the appointment's timezone
                const originalDate = new Date(safeAppointment.startTime);
                const targetDate = new Date(dropInfo.event.start);
                
                // Create formatter for the business timezone
                const dateFormatter = new Intl.DateTimeFormat('en-GB', {
                    timeZone: appointmentTimeZone,
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit'
                });
                
                // Get formatted dates in business timezone
                const originalDateString = dateFormatter.format(originalDate);
                const targetDateString = dateFormatter.format(targetDate);
                
                console.log('Date comparison for recurring instance move (in business timezone):', {
                    originalDate: originalDate.toString(),
                    targetDate: targetDate.toString(),
                    originalDateString,
                    targetDateString,
                    businessTimeZone: appointmentTimeZone,
                    browserTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
                });
                
                // Only check if moving to a different day in the business timezone
                if (originalDateString !== targetDateString) {
                    console.log('Moving recurring instance to different day in business timezone - checking for conflicts', {
                        originalDate: originalDateString,
                        targetDate: targetDateString,
                        businessTimeZone: appointmentTimeZone
                    });
                    
                    // Check if any other instance of the same series is on the target date
                    // Use the same timezone-aware formatter for each appointment
                    const hasInstanceOnTargetDate = appointments.some(a => {
                        if (a.id === safeAppointment.id) return false; // Not the appointment being moved
                        if (a.recurringAppointmentId !== safeAppointment.recurringAppointmentId) return false; // Not same series
                        
                        // Format this appointment's date in the business timezone
                        const instanceDateString = dateFormatter.format(new Date(a.startTime));
                        return instanceDateString === targetDateString; // Check if on target date
                    });
                    
                    if (hasInstanceOnTargetDate) {
                        console.log('Conflict detected: Target date already has an instance of this recurring series (in business timezone)');
                        dropInfo.revert();
                        showError("Cannot move appointment to a date that already has another instance of this recurring series");
                        return;
                    }
                } else {
                    console.log('Just changing time on same day (in business timezone) - no conflict check needed');
                }
            }

            setLoading(true);
            const startTime = dropInfo.event.start ? 
                formatDateToLocalString(dropInfo.event.start, true) : 
                safeAppointment.startTime;
            const endTime = dropInfo.event.end ? 
                formatDateToLocalString(dropInfo.event.end, true) : 
                safeAppointment.endTime;
                
            // Add explicit logging
            console.log('Appointment time update:', {
                oldStartTime: safeAppointment.startTime,
                newStartTime: startTime,
                oldEndTime: safeAppointment.endTime,
                newEndTime: endTime,
                hasChanged: startTime !== safeAppointment.startTime || endTime !== safeAppointment.endTime
            });
            
            // Use the browser's timezone directly to avoid variable redeclaration
            console.log('Using browser timezone:', Intl.DateTimeFormat().resolvedOptions().timeZone);

            // Check if this is a recurring appointment instance
            // The check needs to ensure that recurringAppointmentId is not only defined but also not null
            const isRecurringInstance = safeAppointment.recurringAppointmentId !== undefined && 
                                        safeAppointment.recurringAppointmentId !== null;
            
            // For recurring instances, we need the parent ID for the API call
            // but we need to update the specific instance in the UI
            const instanceId = safeAppointment.id;
            const parentId = isRecurringInstance ? safeAppointment.recurringAppointmentId! : safeAppointment.id;
            
            console.log('Appointment IDs:', {
                instanceId,
                parentId,
                isRecurringInstance
            });
            
            // Store the original appointment data for potential reversion
            const originalAppointment = { ...safeAppointment };
            
            // Create an optimistic update by modifying the local appointments array
            // This provides immediate visual feedback while the API call is in progress
            const updatedAppointments = appointments.map(a => {
                if (a.id === instanceId) {
                    return {
                        ...a,
                        startTime: startTime,
                        endTime: endTime
                    };
                }
                return a;
            });
            
            // Update the state immediately with the optimistic update
            setAppointments(updatedAppointments);

            // Create the updated appointment data
            const updatedAppointmentData: UpdateAppointmentDto = {
                id: parentId,
                title: safeAppointment.title,
                description: safeAppointment.description,
                appointmentTypeId: safeAppointment.appointmentTypeId,
                address: safeAppointment.address,
                latitude: safeAppointment.latitude,
                longitude: safeAppointment.longitude,
                userId: safeAppointment.userId || user?.id,
                startTime: startTime,
                endTime: endTime,
                isAllDay: safeAppointment.isAllDay,
                timeZoneId: safeAppointment.timeZoneId || Intl.DateTimeFormat().resolvedOptions().timeZone, // Use appointment timezone or browser timezone
                recurrenceRule: safeAppointment.recurrenceRule,
                customerId: safeAppointment.customerId,
                jobId: safeAppointment.jobId
            };

            // If this is a recurring instance, add the original date
            if (isRecurringInstance) {
                // Get the appointment's timezone (this is the business timezone)
                const appointmentTimeZone = safeAppointment.timeZoneId || Intl.DateTimeFormat().resolvedOptions().timeZone;
                
                // Use the original appointment's startTime to ensure we reference the correct instance
                const originalStartTime = new Date(safeAppointment.startTime);
                
                // Format the date in the appointment's timezone to ensure correct date
                const dateFormatter = new Intl.DateTimeFormat('en-GB', {
                    timeZone: appointmentTimeZone,
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit'
                });
                
                // Get the date part in the appointment's timezone
                const originalDateFormatted = dateFormatter.format(originalStartTime);
                
                // Parse the formatted date and create a UTC midnight date
                const dateParts = originalDateFormatted.split('/');
                const normalizedDate = new Date(Date.UTC(
                    parseInt(dateParts[2]), // year
                    parseInt(dateParts[1]) - 1, // month (0-based)
                    parseInt(dateParts[0]) // day
                ));
                
                console.log('Setting original date for recurring instance:', {
                    originalAppointmentStartTime: safeAppointment.startTime,
                    originalStartTimeObj: originalStartTime,
                    originalDateFormatted,
                    normalizedDate: normalizedDate.toISOString(),
                    businessTimeZone: appointmentTimeZone,
                    browserTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
                });
                
                updatedAppointmentData.originalDate = normalizedDate;
            }

            let success = false;

            if (isOffline) {
                // Handle offline mode
                try {
                    console.log('Handling appointment update in offline mode');
                    
                    if (isRecurringInstance) {
                        // For recurring instances in offline mode, we need to:
                        // 1. Create an exception for the recurring instance
                        // 2. Store the original appointment date for proper exception handling when syncing
                        
                        console.log('Creating exception for recurring instance in offline mode');
                        
                        // Extract the original date from the appointment for the exception
                        const originalDate = new Date(safeAppointment.startTime);
                        console.log('Original date for exception:', originalDate);
                        
                        // Create the exception object that will be stored for later sync
                        const exceptionData = {
                            appointmentId: parentId,
                            exceptionDate: originalDate.toISOString().split('T')[0],
                            isRecurringInstance: true,
                            originalAppointment: safeAppointment,
                            updatedAppointmentData: {
                                ...updatedAppointmentData,
                                originalDate: originalDate
                            },
                            isOfflineChange: true
                        };
                        
                        console.log('Creating exception data for offline storage:', exceptionData);
                        
                        try {
                            // Store the pending exception in IndexedDB
                            await indexedDbService.addPendingChange({
                                entityId: parentId,
                                entityType: 'AppointmentException',
                                changeType: ChangeType.UPDATE,
                                data: exceptionData,
                                endpoint: `/api/appointments/recurring/instance/${parentId}`,
                                method: 'PUT'
                            });
                            
                            console.log('Successfully stored pending exception');
                            
                            // Update the current instance in the UI to show it as an exception
                            setAppointments(prev => prev.map(a => {
                                if (a.id === instanceId) {
                                    return {
                                        ...a,
                                        isException: true,
                                        exceptionDate: originalDate.toISOString()
                                    };
                                }
                                return a;
                            }));
                            
                            // Update the pending changes count
                            await refreshPendingChangesCount();
                            
                            success = true;
                        } catch (storeError) {
                            console.error('Error storing pending exception:', storeError);
                            success = false;
                        }
                    } else {
                        // For non-recurring appointments, just update normally
                        // Update the appointment in IndexedDB
                        const updatedAppointment = {
                            ...safeAppointment,
                            startTime,
                            endTime
                        };
                        await indexedDbService.updateAppointment(updatedAppointment);
                    }

                    // Use the API client to update the appointment
                    // This will be intercepted in offline mode and add a pending change
                    try {
                        const endpoint = isRecurringInstance 
                            ? `/appointments/recurring/instance/${parentId}`
                            : `/appointments/${parentId}`;
                        
                        // For recurring instances, make sure we include the original date
                        const dataToSend = isRecurringInstance
                            ? { 
                                ...updatedAppointmentData, 
                                originalDate: new Date(safeAppointment.startTime)
                              }
                            : updatedAppointmentData;
                            
                        console.log('Making API call to update appointment (will be handled by offline interceptor):', {
                            endpoint,
                            isRecurringInstance,
                            data: dataToSend
                        });
                        
                        await apiClient.put(endpoint, dataToSend);
                        
                        success = true;
                        
                        // Update the pending changes count
                        await refreshPendingChangesCount();
                    } catch (error: any) {
                        // This error is expected in offline mode
                        if (error.isOfflineError) {
                            console.log('Offline error caught as expected, update request queued for processing when online');
                            success = true;
                            
                            // Update the pending changes count
                            await refreshPendingChangesCount();
                        } else {
                            console.error('Unexpected error making API call for update:', error);
                            success = false;
                        }
                    }
                } catch (error) {
                    console.error('Error updating appointment in offline mode:', error);
                    success = false;
                }
            } else {
                // Online mode - use the API
                try {
                    console.log('Making API call in online mode with data:', {
                        isRecurringInstance,
                        appointmentId: safeAppointment.id,
                        parentId,
                        startTime,
                        endTime,
                        originalDate: safeAppointment.startTime
                    });
                    
                    // For recurring instances, we need to create an exception
                    if (isRecurringInstance) {
                        console.log('Creating exception for recurring instance:', {
                            parentId,
                            originalDate: new Date(safeAppointment.startTime)
                        });
                        
                        // For recurring instance, create an exception
                        // Generate the exception date from the start time
                        const exceptionDate = new Date(safeAppointment.startTime);
                        
                        // Add the original date to the appointment data
                        updatedAppointmentData.originalDate = exceptionDate;
                        
                        // Now send the update to the server with the original date to create an exception
                        const recurringResponse = await appointmentService.updateRecurringInstance(parentId, updatedAppointmentData);
                        success = recurringResponse.success;
                        
                        if (!recurringResponse.success) {
                            // Check for actual error message content before showing an error
                            const errorMessage = recurringResponse.errorMessage || 'Failed to update appointment';
                            console.error('Server returned error when updating recurring instance:', errorMessage);
                            
                            // Only show the error and revert if there's a real error (not undefined or empty)
                            if (typeof errorMessage === 'string' && errorMessage.trim() !== '' && errorMessage !== 'undefined') {
                                showError(errorMessage);
                                
                                // Immediately revert the drag operation
                                dropInfo.revert();
                                
                                // Also reload appointments to ensure UI is in sync with server
                                await loadAppointments();
                                
                                // Exit early to prevent further processing
                                return;
                            } else {
                                // If the error is empty/undefined but we still received a success=false,
                                // log it but don't show to the user since the update seems to have worked
                                console.log('Received success=false but with empty error message. Operation may have succeeded anyway.');
                                success = true; // Treat as success since the update actually worked
                            }
                        }
                    } else {
                        // For regular appointments, just update normally
                        updatedAppointmentData.id = parentId;
                        const regularResponse = await appointmentService.updateAppointment(updatedAppointmentData);
                        success = regularResponse.success;
                        
                        if (!regularResponse.success) {
                            // Check for actual error message content before showing an error
                            const errorMessage = regularResponse.errorMessage || 'Failed to update appointment';
                            console.error('Server returned error when updating regular appointment:', errorMessage);
                            
                            // Only show the error and revert if there's a real error (not undefined or empty)
                            if (typeof errorMessage === 'string' && errorMessage.trim() !== '' && errorMessage !== 'undefined') {
                                showError(errorMessage);
                                
                                // Immediately revert the drag operation
                                dropInfo.revert();
                                
                                // Also reload appointments to ensure UI is in sync with server
                                await loadAppointments();
                                
                                // Exit early to prevent further processing
                                return;
                            } else {
                                // If the error is empty/undefined but we still received a success=false,
                                // log it but don't show to the user since the update seems to have worked
                                console.log('Received success=false but with empty error message. Operation may have succeeded anyway.');
                                success = true; // Treat as success since the update actually worked
                            }
                        }
                    }
                } catch (onlineError: any) {
                    console.error('Error updating appointment via API:', onlineError);
                    
                    // Handle ASP.NET Core validation errors
                    if (onlineError.response?.status === 400 && onlineError.response?.data?.errors) {
                        const validationErrors = onlineError.response.data.errors;
                        const errorMessages = Object.entries(validationErrors)
                            .map(([field, messages]) => `${field}: ${(messages as string[]).join(', ')}`)
                            .join('; ');
                        
                        showError(`Validation failed: ${errorMessages}`);
                        
                        // Revert the appointment to its original position
                        dropInfo.revert();
                        
                        // Also manually revert the appointment in state to original data
                        setAppointments(prev => prev.map(a => 
                            a.id === instanceId ? originalAppointment : a
                        ));
                        
                        return;
                    }
                    
                    // Handle other API errors
                    const errorMessage = onlineError.response?.data?.title || 
                                       onlineError.response?.data?.message || 
                                       onlineError.message || 
                                       'Failed to update appointment';
                    
                    showError(errorMessage);
                    
                    // Revert the appointment to its original position
                    dropInfo.revert();
                    
                    // Also manually revert the appointment in state to original data
                    setAppointments(prev => prev.map(a => 
                        a.id === instanceId ? originalAppointment : a
                    ));
                    
                    return;
                }
            }

            if (!success) {
                // If the update fails, revert the optimistic update
                dropInfo.revert();
                // Also reload appointments to ensure UI is in sync with server
                await loadAppointments();
                return;
            }

            // Update cache with the updated appointment
            if (selectedBusiness) {
                const updatedAppointment: Appointment = {
                    ...safeAppointment,
                    startTime,
                    endTime
                };
                await indexedDbService.addOrUpdateCachedAppointment(updatedAppointment, selectedBusiness.id);
            }

            // Set success message
            showSuccess('Appointment updated');
            
            // For recurring instances, reload the appointments to get updated exceptions
            if (isRecurringInstance) {
                console.log('Reloading appointments to get updated recurring exception');
                await loadAppointments();
            } else {
                // For regular appointments, just invalidate the cache
                invalidateAppointmentCache();
            }
        } catch (error: any) {
            console.error('Error updating appointment:', error);
            showError(extractApiErrorMessage(error, 'An error occurred while updating the appointment'));
            // Revert the drag operation
            dropInfo.revert();
        } finally {
            setLoading(false);
        }
    };

    const handleEventResize = async (resizeInfo: EventResizeStopArg) => {
        try {
            console.log('Event resize detected:', {
                eventId: resizeInfo.event.id,
                // Log what we can from the resize info
                event: {
                    start: resizeInfo.event.start?.toString(),
                    startISO: resizeInfo.event.start?.toISOString(),
                    end: resizeInfo.event.end?.toString()
                },
                // Just log that a resize occurred - we don't have delta info like in EventDropArg
                resizeOccurred: true
            });
            
            // Validation - if we don't have enough info, we can't proceed
            if (!resizeInfo.event.start || !resizeInfo.event.end) {
                console.error('Missing start or end time for resize operation');
                return;
            }
            
            // Check if we're offline first
            const isOffline = !offlineService.getOnlineStatus() || !navigator.onLine;
            console.log(`Current offline status: ${isOffline ? 'Offline' : 'Online'}`);
            
            // Get the appointment that was resized
            let appointment = appointments.find(a => a.id === resizeInfo.event.id);
            
            // If appointment is not found in the current state, try to extract basic information from the event
            // This can happen during rapid operations where the state might be temporarily out of sync
            if (!appointment) {
                console.warn('Appointment not immediately found in state for resize, attempting recovery:', resizeInfo.event.id);
                
                // Try to extract basic appointment information from the event object
                const eventProps = resizeInfo.event.extendedProps || {};
                
                // Create a temporary appointment object with the minimum required fields
                appointment = {
                    id: resizeInfo.event.id,
                    title: resizeInfo.event.title,
                    startTime: resizeInfo.event.start?.toISOString() || '',
                    endTime: resizeInfo.event.end?.toISOString() || '',
                    description: eventProps.description || '',
                    appointmentTypeId: eventProps.appointmentTypeId || '',
                    recurringAppointmentId: eventProps.recurringAppointmentId || undefined,
                    recurrenceRule: eventProps.recurrenceRule || undefined,
                    isAllDay: resizeInfo.event.allDay || false,
                    timeZoneId: Intl.DateTimeFormat().resolvedOptions().timeZone,
                    businessId: selectedBusiness?.id || '',
                    hasWriteAccess: true,
                    createdAt: new Date().toISOString(),
                    updatedAt: new Date().toISOString()
                };
                
                console.log('Created recovery appointment object for resize:', appointment);
                
                // If we still don't have enough info, try to reload appointments and abort the operation
                if (!appointment.appointmentTypeId) {
                    console.error('Insufficient data for appointment recovery during resize, aborting update');
                    // Can't use revert directly since it might not be available
                    // Instead, reload appointments to restore the original state
                    await loadAppointments();
                    return;
                }
            }
            
            console.log('Resized appointment:', appointment);

            // TypeScript safety check - this shouldn't happen due to our recovery logic above
            if (!appointment) {
                console.error('Appointment still undefined after recovery attempts');
                await loadAppointments();
                return;
            }
            
            // At this point, TypeScript should know appointment is defined, but we'll add an assertion to be sure
            const safeAppointment = appointment as Appointment;  // Type assertion to satisfy TypeScript

            const appointmentType = appointmentTypes.find(type => type.id === safeAppointment.appointmentTypeId);
            const business = appointmentType?.business;
            
            // Validate business hours if restricted
            if (business?.useBusinessHours && business?.restrictToBusinessHours && 
                business?.businessHoursStart && business?.businessHoursEnd) {
                const [startHours, startMinutes] = business.businessHoursStart.split(':').map(Number);
                const [endHours, endMinutes] = business.businessHoursEnd.split(':').map(Number);

                const startTime = new Date(resizeInfo.event.start!);
                const endTime = new Date(resizeInfo.event.end!);
                const businessStart = new Date(startTime);
                const businessEnd = new Date(startTime);

                businessStart.setHours(startHours, startMinutes, 0);
                businessEnd.setHours(endHours, endMinutes, 0);

                if (startTime < businessStart || endTime > businessEnd) {
                    // Note: resizeInfo doesn't have a revert method like dropInfo does
                    // Instead, we'll reload appointments to restore the original state
                    await loadAppointments();
                    showError("Appointments must be within business hours");
                    return;
                }
            }

            // Note: For resizing, we don't need to check for conflicts with other instances
            // because resizing only changes the duration, not the date of the appointment

            // For recurring instances, we need the parent ID for the API call
            // but we need to update the specific instance in the UI
            const instanceId = safeAppointment.id;
            const isRecurringInstance = safeAppointment.recurringAppointmentId !== undefined && 
                                        safeAppointment.recurringAppointmentId !== null;
            const parentId = isRecurringInstance ? safeAppointment.recurringAppointmentId! : safeAppointment.id;
            
            console.log('Appointment IDs for resize:', {
                instanceId,
                parentId,
                isRecurringInstance
            });

            setLoading(true);
            // Use the same approach as handleEventDrop for consistency
            const startTime = resizeInfo.event.start ? 
                formatDateToLocalString(resizeInfo.event.start, true) : 
                safeAppointment.startTime;
            const endTime = resizeInfo.event.end ? 
                formatDateToLocalString(resizeInfo.event.end, true) : 
                safeAppointment.endTime;
                
            // Add explicit logging
            console.log('Appointment time update:', {
                oldStartTime: safeAppointment.startTime,
                newStartTime: startTime,
                oldEndTime: safeAppointment.endTime,
                newEndTime: endTime,
                hasChanged: startTime !== safeAppointment.startTime || endTime !== safeAppointment.endTime
            });
            
            // Use browser timezone directly to avoid variable redeclaration issues
            console.log('Current browser timezone for resize:', Intl.DateTimeFormat().resolvedOptions().timeZone);

            // Create an optimistic update by modifying the local appointments array
            const updatedAppointments = appointments.map(a => {
                if (a.id === instanceId) {
                    return {
                        ...a,
                        startTime: startTime,
                        endTime: endTime
                    };
                }
                return a;
            });
            
            // Update the state immediately with the optimistic update
            setAppointments(updatedAppointments);

            // Create the updated appointment data
            const updatedAppointmentData: UpdateAppointmentDto = {
                id: parentId,
                title: safeAppointment.title,
                description: safeAppointment.description,
                appointmentTypeId: safeAppointment.appointmentTypeId,
                address: safeAppointment.address,
                latitude: safeAppointment.latitude,
                longitude: safeAppointment.longitude,
                userId: safeAppointment.userId || user?.id,
                startTime: startTime,
                endTime: endTime,
                isAllDay: safeAppointment.isAllDay,
                timeZoneId: safeAppointment.timeZoneId || Intl.DateTimeFormat().resolvedOptions().timeZone, // Use appointment timezone or browser timezone
                recurrenceRule: safeAppointment.recurrenceRule,
                customerId: safeAppointment.customerId,
                jobId: safeAppointment.jobId
            };

            // If this is a recurring instance, add the original date
            if (isRecurringInstance) {
                // Get the appointment's timezone (this is the business timezone)
                const appointmentTimeZone = safeAppointment.timeZoneId || Intl.DateTimeFormat().resolvedOptions().timeZone;
                
                // Use the original appointment's startTime to ensure we reference the correct instance
                const originalStartTime = new Date(safeAppointment.startTime);
                
                // Format the date in the appointment's timezone to ensure correct date
                const dateFormatter = new Intl.DateTimeFormat('en-GB', {
                    timeZone: appointmentTimeZone,
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit'
                });
                
                // Get the date part in the appointment's timezone
                const originalDateFormatted = dateFormatter.format(originalStartTime);
                
                // Parse the formatted date and create a UTC midnight date
                const dateParts = originalDateFormatted.split('/');
                const normalizedDate = new Date(Date.UTC(
                    parseInt(dateParts[2]), // year
                    parseInt(dateParts[1]) - 1, // month (0-based)
                    parseInt(dateParts[0]) // day
                ));
                
                console.log('Setting original date for recurring instance resize:', {
                    originalAppointmentStartTime: safeAppointment.startTime,
                    originalStartTimeObj: originalStartTime,
                    originalDateFormatted,
                    normalizedDate: normalizedDate.toISOString(),
                    businessTimeZone: appointmentTimeZone,
                    browserTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
                });
                
                updatedAppointmentData.originalDate = normalizedDate;
            }

            let success = false;

            if (isOffline) {
                // Handle offline mode
                try {
                    console.log('Handling appointment resize in offline mode');
                    
                    if (isRecurringInstance) {
                        // For recurring instances in offline mode, we need to:
                        // 1. Create an exception for the recurring instance
                        // 2. Store the original appointment date for proper exception handling when syncing
                        
                        console.log('Creating exception for recurring instance resize in offline mode');
                        
                        // Extract the original date from the appointment for the exception
                        const originalDate = new Date(safeAppointment.startTime);
                        console.log('Original date for exception:', originalDate);
                        
                        // Create the exception object that will be stored for later sync
                        const exceptionData = {
                            appointmentId: instanceId,
                            exceptionDate: originalDate.toISOString().split('T')[0],
                            isRecurringInstance: true,
                            originalAppointment: safeAppointment,
                            updatedAppointmentData: {
                                ...updatedAppointmentData,
                                originalDate: originalDate
                            },
                            isOfflineChange: true
                        };
                        
                        console.log('Creating exception data for offline storage:', exceptionData);
                        
                        try {
                            // Store the pending exception in IndexedDB
                            await indexedDbService.addPendingChange({
                                entityId: instanceId,
                                entityType: 'AppointmentException',
                                changeType: ChangeType.UPDATE,
                                data: exceptionData,
                                endpoint: `/api/appointments/recurring/instance/${instanceId}`,
                                method: 'PUT'
                            });
                            
                            console.log('Successfully stored pending exception');
                            
                            // Update the current instance in the UI to show it as an exception
                            setAppointments(prev => prev.map(a => {
                                if (a.id === instanceId) {
                                    return {
                                        ...a,
                                        isException: true,
                                        exceptionDate: originalDate.toISOString()
                                    };
                                }
                                return a;
                            }));
                            
                            // Update the pending changes count
                            await refreshPendingChangesCount();
                            
                            success = true;
                        } catch (storeError) {
                            console.error('Error storing pending exception:', storeError);
                            success = false;
                        }
                    } else {
                        // For non-recurring appointments, just update normally
                        // Update the appointment in IndexedDB
                        const updatedAppointment = {
                            ...safeAppointment,
                            startTime,
                            endTime
                        };
                        await indexedDbService.updateAppointment(updatedAppointment);
                    }

                    // Use the API client to update the appointment
                    // This will be intercepted in offline mode and add a pending change
                    try {
                        const endpoint = isRecurringInstance 
                            ? `/appointments/recurring/instance/${instanceId}`
                            : `/appointments/${instanceId}`;
                        
                        // For recurring instances, make sure we include the original date
                        const dataToSend = isRecurringInstance
                            ? { 
                                ...updatedAppointmentData, 
                                originalDate: new Date(safeAppointment.startTime)
                              }
                            : updatedAppointmentData;
                            
                        console.log('Making API call to update appointment (will be handled by offline interceptor):', {
                            endpoint,
                            isRecurringInstance,
                            data: dataToSend
                        });
                        
                        await apiClient.put(endpoint, dataToSend);
                        
                        success = true;
                        
                        // Update the pending changes count
                        await refreshPendingChangesCount();
                    } catch (error: any) {
                        // This error is expected in offline mode
                        if (error.isOfflineError) {
                            console.log('Offline error caught as expected, update request queued for processing when online');
                            success = true;
                            
                            // Update the pending changes count
                            await refreshPendingChangesCount();
                        } else {
                            console.error('Unexpected error making API call for update:', error);
                            success = false;
                        }
                    }
                } catch (offlineError) {
                    console.error('Error saving offline appointment resize:', offlineError);
                    success = false;
                }
            } else {
                // Online mode - use the API
                try {
                    // For recurring instances, we need to create an exception
                    if (isRecurringInstance) {
                        console.log('Creating exception for recurring instance resize:', {
                            parentId: instanceId,
                            originalDate: new Date(safeAppointment.startTime)
                        });
                        
                        // For recurring instance, create an exception
                        // Generate the exception date from the start time
                        const exceptionDate = new Date(safeAppointment.startTime);
                        
                        // Add the original date to the appointment data
                        updatedAppointmentData.originalDate = exceptionDate;
                        
                        // Now send the update to the server with the original date to create an exception
                        const recurringResponse = await appointmentService.updateRecurringInstance(instanceId, updatedAppointmentData);
                        success = recurringResponse.success;
                        
                        if (!recurringResponse.success) {
                            // Check for actual error message content before showing an error
                            const errorMessage = recurringResponse.errorMessage || 'Failed to update appointment';
                            console.error('Server returned error when updating recurring instance during resize:', errorMessage);
                            
                            // Only show the error and revert if there's a real error (not undefined or empty)
                            if (typeof errorMessage === 'string' && errorMessage.trim() !== '' && errorMessage !== 'undefined') {
                                showError(errorMessage);
                                
                                // Reload appointments to restore the original state since direct revert isn't available
                                await loadAppointments();
                                
                                // Exit early to prevent further processing
                                return;
                            } else {
                                // If the error is empty/undefined but we still received a success=false,
                                // log it but don't show to the user since the update seems to have worked
                                console.log('Received success=false but with empty error message during resize. Operation may have succeeded anyway.');
                                success = true; // Treat as success since the update actually worked
                            }
                        }
                    } else {
                        // Now send the update to the server
                        // Make sure the id is set on the appointment DTO
                        updatedAppointmentData.id = instanceId;
                        const regularResponse = await appointmentService.updateAppointment(updatedAppointmentData);
                        success = regularResponse.success;
                        
                        if (!regularResponse.success) {
                            // Check for actual error message content before showing an error
                            const errorMessage = regularResponse.errorMessage || 'Failed to update appointment';
                            console.error('Server returned error when updating regular appointment during resize:', errorMessage);
                            
                            // Only show the error and revert if there's a real error (not undefined or empty)
                            if (typeof errorMessage === 'string' && errorMessage.trim() !== '' && errorMessage !== 'undefined') {
                                showError(errorMessage);
                                
                                // We need to reload appointments to revert the changes since resizeInfo.revert() isn't available
                                await loadAppointments();
                                
                                // Exit early to prevent further processing
                                return;
                            } else {
                                // If the error is empty/undefined but we still received a success=false,
                                // log it but don't show to the user since the update seems to have worked
                                console.log('Received success=false but with empty error message during resize. Operation may have succeeded anyway.');
                                success = true; // Treat as success since the update actually worked
                            }
                        }
                    }
                } catch (onlineError) {
                    console.error('Error updating appointment via API:', onlineError);
                    success = false;
                }
            }

            if (!success) {
                // If the update fails, reload appointments to restore the original state
                await loadAppointments();
                return;
            }
            // Update cache with the updated appointment
            if (selectedBusiness) {
                const updatedAppointment: Appointment = {
                    ...safeAppointment,
                    startTime,
                    endTime
                };
                await indexedDbService.addOrUpdateCachedAppointment(updatedAppointment, selectedBusiness.id);
            }
            
            showSuccess('Appointment updated');
            
            // For recurring instances, we need to reload appointments to get the updated exception
            // This is because the server generates a new ID for the exception
            if (isRecurringInstance) {
                console.log('Reloading appointments to get updated recurring exception');
                await loadAppointments();
            } else {
                // For regular appointments, just invalidate the cache
                invalidateAppointmentCache();
            }
        } catch (error: any) {
            console.error('Error updating appointment:', error);
            // Reload appointments to restore the original state
            await loadAppointments();
            showError(extractApiErrorMessage(error, 'Failed to update appointment'));
        } finally {
            setLoading(false);
        }
    };

    const isWithinBusinessHours = (date: Date): boolean => {
        const business = selectedAppointmentType?.business;
        if (!business?.businessHoursStart || !business?.businessHoursEnd) return true;

        const startTime = new Date(date);
        startTime.setHours(
            parseInt(business.businessHoursStart.split(':')[0]),
            parseInt(business.businessHoursStart.split(':')[1])
        );
        
        const endTime = new Date(date);
        endTime.setHours(
            parseInt(business.businessHoursEnd.split(':')[0]),
            parseInt(business.businessHoursEnd.split(':')[1])
        );

        return date >= startTime && date <= endTime;
    };

    const handleRecurringDialogClose = () => {
        setIsRecurringDialogOpen(false);
        setRecurringAction(null);
    };

    const handleRecurringChoice = async (editSeries: boolean) => {
        if (!selectedAppointment) return;
        
        try {
            setLoading(true);
            
            console.log('Handling recurring choice - Full appointment data:', {
                editSeries,
                action: recurringAction,
                appointmentId: selectedAppointment.id,
                recurringAppointmentId: selectedAppointment.recurringAppointmentId,
                recurrenceRule: selectedAppointment.recurrenceRule,
                startTime: selectedAppointment.startTime,
                fullAppointment: selectedAppointment
            });

            if (recurringAction === 'edit') {
                if (editSeries) {
                    setIsEditingSeries(true);
                    handleRecurringDialogClose();
                    startEdit();
                } else {
                    setIsEditingSeries(false);
                    handleRecurringDialogClose();
                    startEdit();
                }
                return;
            } else if (recurringAction === 'delete') {
                if (editSeries) {
                    // For series deletion, use the parent appointment ID
                    const parentId = selectedAppointment.recurringAppointmentId || selectedAppointment.id;
                    await appointmentService.deleteAppointment(parentId);
                } else {
                    // For single instance deletion
                    const appointmentToDelete = selectedAppointment;
                    console.log('Deleting single instance:', {
                        appointmentId: appointmentToDelete.id,
                        recurringAppointmentId: appointmentToDelete.recurringAppointmentId,
                        startTime: appointmentToDelete.startTime,
                        isRecurring: Boolean(appointmentToDelete.recurrenceRule || appointmentToDelete.recurringAppointmentId)
                    });

                    if (!appointmentToDelete.recurringAppointmentId && !appointmentToDelete.recurrenceRule) {
                        console.log('Deleting non-recurring appointment');
                        await appointmentService.deleteAppointment(appointmentToDelete.id);
                    } else {
                        console.log('Deleting instance of recurring appointment');
                        // For recurring appointments, we need to use the parent ID
                        const parentId = appointmentToDelete.recurringAppointmentId || appointmentToDelete.id;
                        
                        // Get the appointment's timezone or default to browser timezone
                        const timeZoneId = appointmentToDelete.timeZoneId || Intl.DateTimeFormat().resolvedOptions().timeZone;
                        
                        // Create a date object from the startTime
                        const startTime = new Date(appointmentToDelete.startTime);
                        
                        // Format the date according to the appointment's timezone to ensure proper date handling
                        const dateFormatter = new Intl.DateTimeFormat('en-GB', {
                            timeZone: timeZoneId,
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit'
                        });
                        
                        // Get the date part in the appointment's timezone
                        const dateFormatted = dateFormatter.format(startTime);
                        
                        console.log('Deleting recurring instance with date calculations:', {
                            originalStartTime: appointmentToDelete.startTime,
                            startTimeObj: startTime,
                            timeZoneId: timeZoneId,
                            formattedDate: dateFormatted
                        });
                        
                        // Parse the formatted date
                        // Use a UTC midnight time to avoid timezone shifts
                        const [year, month, day] = dateFormatted.split('-').map(Number);
                        const normalizedDate = new Date(Date.UTC(year, month - 1, day));
                        
                        console.log('Using normalized date for deletion:', {
                            normalizedDateISO: normalizedDate.toISOString(),
                            originalDateISO: startTime.toISOString()
                        });
                        
                        await appointmentService.deleteAppointment(
                            parentId,
                            normalizedDate
                        );
                    }
                }
                showSuccess('Appointment deleted successfully');
                setIsRecurringDialogOpen(false);
                
                // Force a refresh of the appointments
                appointmentsLoadedRef.current = false;
                await loadAppointments();
                
                // Also refresh the calendar view
                const calendarApi = calendarRef.current?.getApi();
                if (calendarApi) {
                    calendarApi.refetchEvents();
                }
            }
            
            handleRecurringDialogClose();
        } catch (error: any) {
            console.error('Error handling recurring appointment:', error);
            showError(extractApiErrorMessage(error, 'An error occurred while processing the appointment'));
        } finally {
            setLoading(false);
        }
    };


    // Effect to load users when selected business changes
    useEffect(() => {
        if (selectedBusiness) {
            loadAvailableUsers();
        }
    }, [selectedBusiness]);

    // Effect to inject sync button into calendar toolbar
    useEffect(() => {
        const titleElement = document.querySelector('.fc-toolbar-title');
        if (titleElement && syncButtonContainerRef.current) {
            // Remove any existing sync button container
            const existingContainer = titleElement.parentElement?.querySelector('.sync-button-inline');
            if (existingContainer) {
                existingContainer.remove();
            }
            
            // Insert the sync button container after the title
            titleElement.insertAdjacentElement('afterend', syncButtonContainerRef.current);
        }
    }, [appointments, isOffline, isSyncing, pendingChangesCount]);

    // Update the setError function to show a snackbar
    const showError = (message: string) => {
        setError(message);
        setSnackbarMessage(message);
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
    };

    // Update the setSuccess function to show a snackbar
    const showSuccess = (message: string) => {
        setSuccess(message);
        setSnackbarMessage(message);
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
    };

    // Modify existing code that calls setError to use showError instead
    // For example:
    const handleSnackbarClose = () => {
        setSnackbarOpen(false);
    };

    // Customer and Job selection functions
    const handleSearchCustomers = async () => {
        if (!customerSearchTerm.trim()) return;
        
        setIsSearchingCustomers(true);
        try {
            const results = await customerService.searchCustomers({ searchTerm: customerSearchTerm });
            setCustomerSearchResults(results);
        } catch (error) {
            console.error('Error searching customers:', error);
            setCustomerSearchResults([]);
        } finally {
            setIsSearchingCustomers(false);
        }
    };

    const handleSelectCustomer = async (customer: Customer) => {
        setSelectedCustomer(customer);
        setSelectedJob(null);
        setShowCustomerSearch(false);
        setCustomerSearchTerm('');
        setCustomerSearchResults([]);
        
        // Load jobs for this customer
        try {
            const allJobs = await jobService.getJobs();
            const customerJobs = allJobs.filter(job => job.customerId === customer.id);
            setCustomerJobs(customerJobs);
        } catch (error) {
            console.error('Error loading customer jobs:', error);
            setCustomerJobs([]);
        }
    };

    const handleClearCustomer = () => {
        setSelectedCustomer(null);
        setSelectedJob(null);
        setCustomerJobs([]);
        setCustomerSearchTerm('');
        setCustomerSearchResults([]);
        
        // Clear customer and job from form data
        setFormData(prev => ({
            ...prev,
            customerId: undefined,
            jobId: undefined
        }));
    };

    const handleSelectJob = (job: Job | null) => {
        setSelectedJob(job);
        
        // Update form data with job ID and optionally address
        setFormData(prev => ({
            ...prev,
            jobId: job?.id,
            // If job is selected and appointment address field is visible but not set, copy job address
            address: (job && selectedAppointmentType?.requiresLocation && !prev.address) 
                ? (job.jobAddress || prev.address) 
                : prev.address
        }));
    };

    const handleCreateNewJob = () => {
        if (!selectedCustomer) return;
        
        setJobFormData({
            customerId: selectedCustomer.id,
            assignedSalesPersonId: formData.userId || selectedUserFilter || user?.id || '',
            jobAddress: selectedCustomer.address || '',
            type: JobType.Consultation,
            estimatedValue: 0,
            priority: Priority.Medium,
            notes: ''
        });
        setIsJobDialogOpen(true);
    };

    const handleCreateJob = async () => {
        try {
            const job = await jobService.createJob(jobFormData);
            showSuccess('Job created successfully');
            
            // Add the new job to the customer's jobs list
            setCustomerJobs(prev => [...prev, job]);
            setSelectedJob(job);
            setIsJobDialogOpen(false);
            
            // Update form data with the new job ID
            setFormData(prev => ({
                ...prev,
                jobId: job.id
            }));
            
            // Reset job form
            setJobFormData({
                customerId: '',
                assignedSalesPersonId: formData.userId || selectedUserFilter || user?.id || '',
                jobAddress: '',
                type: JobType.Consultation,
                estimatedValue: 0,
                priority: Priority.Medium,
                notes: ''
            });
        } catch (error: any) {
            console.error('Error creating job:', error);
            showError(error.message || 'Failed to create job');
        }
    };

    const handleAppointmentSubmit = async () => {
        await handleSubmit();
    };

    const handleViewCustomer = () => {
        if (!selectedCustomer) return;
        
        if (isMobile) {
            // Open dialog on mobile
            setIsViewCustomerDialogOpen(true);
        } else {
            // Open in new tab on desktop
            window.open(`/crm/customers/${selectedCustomer.id}`, '_blank');
        }
    };

    const handleViewJob = () => {
        if (!selectedJob) return;
        
        if (isMobile) {
            // Open dialog on mobile
            setIsViewJobDialogOpen(true);
        } else {
            // Navigate to customer detail page with job tab selected
            navigate(`/crm/customers/${selectedJob.customerId}`, {
                state: { selectedJobId: selectedJob.id }
            });
        }
    };

    return (
        <Box sx={{ 
            height: '100%', 
            display: 'flex', 
            flexDirection: 'column',
            width: '100%'
        }}>
            {/* Syncing Banner */}
            {!isOffline && isSyncing && (
                <Box 
                    sx={{ 
                        bgcolor: 'info.main', 
                        color: 'info.contrastText',
                        p: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1
                    }}
                >
                    <CloudSyncIcon fontSize="small" />
                    <Typography variant="body2">
                        Syncing {pendingChangesCount} pending changes...
                    </Typography>
                </Box>
            )}
            
            {/* Calendar Container - with swipe navigation on mobile */}
            <Box
                ref={calendarContainerRef}
                sx={{
                    flexGrow: 1,
                    position: 'relative',
                    overflow: 'hidden', // Hide content during swipe
                    // Prevent text selection during swipe on mobile
                    ...(isMobile && {
                        WebkitUserSelect: 'none',
                        userSelect: 'none',
                        touchAction: 'pan-y' // Allow vertical scroll, horizontal handled by swipe
                    }),
                    '& .fc': {
                        height: '100%'
                    },
                    '& .fc-event': {
                        cursor: 'pointer'
                    },
                    // Add space below title when user filter is active
                    ...(selectedUserFilter && selectedUserFilter !== user?.id && {
                        '& .fc-toolbar-chunk:has(.fc-toolbar-title)': {
                            position: 'relative',
                            paddingBottom: '20px'
                        }
                    }),
                    // Apply transform to the view harness area during swipe
                    ...(isMobile && {
                        '& .fc-view-harness': {
                            transform: `translateX(${dragOffset}px)`,
                            transition: isSnapping ? 'transform 0.3s cubic-bezier(0.4, 0.0, 0.2, 1)' : 'none',
                            willChange: 'transform',
                            backgroundColor: 'background.paper'
                        }
                    })
                }}>

                {/* Swipe preview panels - real FullCalendar instances positioned adjacent to main view */}
                {isMobile && currentPeriodStart && shouldShowPreviews && (
                    <>
                        {/* Previous period preview (positioned to the left of the main calendar view, below toolbar) */}
                        <Box
                            sx={{
                                position: 'absolute',
                                left: 0,
                                right: 0,
                                top: `${viewHarnessTop}px`,
                                bottom: 0,
                                transform: `translateX(calc(-100% + ${dragOffset}px))`,
                                transition: isSnapping ? 'transform 0.3s cubic-bezier(0.4, 0.0, 0.2, 1)' : 'none',
                                willChange: 'transform',
                                pointerEvents: 'none',
                                zIndex: 2,
                                bgcolor: 'background.paper',
                                '& .fc': { height: '100%' }
                            }}
                        >
                            <CalendarPreviewPanel
                                date={
                                    currentViewType === 'timeGridDay'
                                        ? addDays(currentPeriodStart, -1)
                                        : addWeeks(currentPeriodStart, -1)
                                }
                                appointments={prevPeriodAppointments}
                                appointmentTypes={appointmentTypes}
                                viewType={currentViewType}
                                firstDayOfWeek={firstDayOfWeek}
                                slotMinTime={selectedBusiness?.restrictToBusinessHours
                                    ? convertTimeSpanToTime(selectedBusiness.businessHoursStart)
                                    : '00:00:00'}
                                slotMaxTime={selectedBusiness?.restrictToBusinessHours
                                    ? convertTimeSpanToTime(selectedBusiness.businessHoursEnd)
                                    : '24:00:00'}
                            />
                        </Box>

                        {/* Next period preview (positioned to the right of the main calendar view, below toolbar) */}
                        <Box
                            sx={{
                                position: 'absolute',
                                left: 0,
                                right: 0,
                                top: `${viewHarnessTop}px`,
                                bottom: 0,
                                transform: `translateX(calc(100% + ${dragOffset}px))`,
                                transition: isSnapping ? 'transform 0.3s cubic-bezier(0.4, 0.0, 0.2, 1)' : 'none',
                                willChange: 'transform',
                                pointerEvents: 'none',
                                zIndex: 2,
                                bgcolor: 'background.paper',
                                '& .fc': { height: '100%' }
                            }}
                        >
                            <CalendarPreviewPanel
                                date={
                                    currentViewType === 'timeGridDay'
                                        ? addDays(currentPeriodStart, 1)
                                        : addWeeks(currentPeriodStart, 1)
                                }
                                appointments={nextPeriodAppointments}
                                appointmentTypes={appointmentTypes}
                                viewType={currentViewType}
                                firstDayOfWeek={firstDayOfWeek}
                                slotMinTime={selectedBusiness?.restrictToBusinessHours
                                    ? convertTimeSpanToTime(selectedBusiness.businessHoursStart)
                                    : '00:00:00'}
                                slotMaxTime={selectedBusiness?.restrictToBusinessHours
                                    ? convertTimeSpanToTime(selectedBusiness.businessHoursEnd)
                                    : '24:00:00'}
                            />
                        </Box>
                    </>
                )}

                {/* User filter subtitle */}
                {selectedUserFilter && selectedUserFilter !== user?.id && (() => {
                    const filteredUser = availableUsers.find(u => u.id === selectedUserFilter);
                    if (!filteredUser) return null;
                    return (
                        <Typography
                            variant="h1"
                            sx={{
                                position: 'absolute',
                                top: '3.5rem',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                zIndex: 1,
                                color: theme.textColor,
                                opacity: 0.9,
                                fontSize: '1.5rem',
                                fontWeight: 'bold'
                            }}
                        >
                            {filteredUser.firstName} {filteredUser.lastName}
                        </Typography>
                    );
                })()}
                
                <FullCalendar
                    ref={calendarRef}
                    plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin]}
                    slotLabelFormat={{
                        hour: 'numeric',
                        minute: '2-digit',
                        omitZeroMinute: true,
                        meridiem: 'short',
                        hour12: true
                    }}
                    eventTimeFormat={{
                        hour: 'numeric',
                        minute: '2-digit',
                        meridiem: 'short'
                    }}
                    initialView={currentViewType}
                    headerToolbar={{
                        left: isMobile ? 'prev,next' : 'prev,next today',
                        center: 'title',
                        right: isMobile ? 'listWeek,timeGridDay' : 'dayGridMonth,timeGridWeek,timeGridDay'
                    }}
                    titleFormat={{
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric'
                    }}
                    locale="en-GB"
                    views={{
                        listWeek: {
                            buttonText: 'week'
                        },
                        timeGridDay: {
                            buttonText: 'day'
                        }
                    }}
                    editable={!isMobile}
                    selectable={!isMobile}
                    selectMirror={true}
                    dayMaxEvents={true}
                    weekends={true}
                    dragRevertDuration={0}
                    longPressDelay={isMobile ? 500 : 0}
                    eventLongPressDelay={isMobile ? 500 : 0}
                    selectLongPressDelay={isMobile ? 500 : 0}
                    nextDayThreshold="00:00:00"
                    fixedWeekCount={false}
                    droppable={!isMobile}
                    forceEventDuration={true}
                    eventDurationEditable={!isMobile}
                    eventStartEditable={!isMobile}
                    eventOverlap={true}
                    // Allow events to overlap visually with stacking effect
                    slotEventOverlap={true}
                    handleWindowResize={true}
                    stickyHeaderDates={true}
                    dragScroll={true}
                    unselectAuto={true}
                    height="100%"
                    nowIndicator={true}
                    timeZone="local"
                    eventSourceSuccess={(events, xhr) => {
                        console.log('Events loaded successfully:', events.length);
                        return events;
                    }}
                    eventContent={(arg) => {
                        const appointment = arg.event.extendedProps.appointment as Appointment;
                        const customerName = appointment?.customerName;
                        const customerPhone = appointment?.customerPhone;
                        const address = appointment?.address;
                        const description = appointment?.description;
                        const hasCustomer = appointment?.customerId;
                        const isAllDay = arg.event.allDay;

                        // Check if we should show time (hide time on mobile week view since time is already shown on the left)
                        const shouldShowTime = !(isMobile && arg.view.type === 'listWeek');

                        // Format start time (only for non-all-day events and when we should show it)
                        const startTime = shouldShowTime && !isAllDay && arg.event.start ? new Date(arg.event.start).toLocaleTimeString('en-GB', {
                            hour: 'numeric',
                            minute: '2-digit',
                            hour12: true
                        }) : '';

                        // Extract suburb from address (second comma-separated part, assumes NZ-style: "123 Main St, Ponsonby, Auckland 1021" → "Ponsonby")
                        const addressParts = address?.split(',').map(p => p.trim()) || [];
                        const suburb = addressParts.length >= 3 ? addressParts[1] : '';

                        // Common styles for ellipsis (single line)
                        const ellipsisStyle = "overflow: hidden; text-overflow: ellipsis; white-space: nowrap;";

                        // Style for multi-line text that wraps but stays within bounds
                        const multiLineStyle = "overflow: hidden; word-wrap: break-word; overflow-wrap: break-word;";

                        // Top row with start time (left) and suburb (right)
                        const topRow = (startTime || suburb) ? `
                            <div style="display: flex; justify-content: space-between; align-items: center; gap: 4px;">
                                ${startTime ? `<div style="font-size: 10px; flex-shrink: 0;">${startTime}</div>` : '<div></div>'}
                                ${suburb ? `<div style="font-size: 10px; opacity: 0.85; ${ellipsisStyle} flex-shrink: 1; min-width: 0;">${escapeHtml(suburb)}</div>` : ''}
                            </div>
                        ` : '';

                        // If there's a customer, show customer info + address + description
                        if (hasCustomer && customerName) {
                            return {
                                html: `
                                    <div style="padding: 2px 1px; line-height: 1.25; height: 100%; overflow: hidden;">
                                        ${topRow}
                                        <div style="font-size: 0.82em; ${ellipsisStyle}">${escapeHtml(customerName)}</div>
                                        ${customerPhone ? `<div style="font-size: 0.82em; opacity: 0.9; ${ellipsisStyle}">${escapeHtml(customerPhone)}</div>` : ''}
                                        ${address ? `<div style="font-size: 0.82em; opacity: 0.8; ${ellipsisStyle}">${escapeHtml(address)}</div>` : ''}
                                        ${description ? `<div style="font-size: 0.82em; opacity: 0.8; ${ellipsisStyle}">${escapeHtml(description)}</div>` : ''}
                                    </div>
                                `
                            };
                        }

                        // If no customer, show time and description (description can wrap to multiple lines)
                        return {
                            html: `
                                <div style="padding: 2px 1px; line-height: 1.25; height: 100%; overflow: hidden; display: flex; flex-direction: column;">
                                    ${topRow || (startTime ? `<div style="font-size: 10px;">${startTime}</div>` : '')}
                                    <div style="font-size: 0.82em; ${multiLineStyle}">${escapeHtml(description || arg.event.title)}</div>
                                </div>
                            `
                        };
                    }}
                    eventDidMount={(arg) => {
                        const appointment = arg.event.extendedProps.appointment as Appointment;
                        if (appointment) {
                            const backgroundColor = getAppointmentColor(appointment);
                            const textColor = getAppointmentTextColor(appointment);
                            const borderColor = darkenColor(backgroundColor, 30); // Calculate 30% darker color
                            arg.el.style.backgroundColor = backgroundColor;
                            arg.el.style.color = textColor;
                            arg.el.style.border = `1px solid ${borderColor}`; // Use the calculated darker color
                        }
                    }}
                    events={filteredEvents}
                    firstDay={(() => {
                        console.log('FullCalendar firstDay being set to:', firstDayOfWeek);
                        return firstDayOfWeek;
                    })()} // Set the first day of week from business settings
                    select={handleDateSelect}
                    eventClick={handleEventClick}
                    eventDrop={handleEventDrop}
                    eventResize={handleEventResize}
                    datesSet={handleDatesSet}
                    businessHours={getBusinessHoursConfig()}
                    eventDragStart={(info) => {
                        console.log('Drag start detected for event:', info.event.id);
                    }}
                    eventDragStop={(info) => {
                        console.log('Drag stop detected for event:', info.event.id);
                    }}
                    eventReceive={(info) => {
                        console.log('Event received:', info.event.id);
                        
                        // This should not normally happen with internal drag and drop,
                        // but in case eventDrop is not triggered, handle it here
                        let appointment = appointments.find(a => a.id === info.event.id);
                        
                        // If we can't find the appointment in state, try to create a minimal one
                        // from the event data (similar to the recovery in handleEventDrop)
                        if (!appointment) {
                            console.warn('Event received but appointment not found in state, creating from event:', info.event.id);
                            
                            // Extract data from event
                            const eventProps = info.event.extendedProps || {};
                            
                            // Create a temporary appointment with minimal required data
                            appointment = {
                                id: info.event.id,
                                title: info.event.title,
                                startTime: info.event.start?.toISOString() || '',
                                endTime: info.event.end?.toISOString() || '',
                                description: eventProps.description || '',
                                appointmentTypeId: eventProps.appointmentTypeId || '',
                                recurringAppointmentId: eventProps.recurringAppointmentId || undefined,
                                recurrenceRule: eventProps.recurrenceRule || undefined,
                                isAllDay: info.event.allDay || false,
                                timeZoneId: Intl.DateTimeFormat().resolvedOptions().timeZone,
                                businessId: selectedBusiness?.id || '',
                                hasWriteAccess: true,
                                createdAt: new Date().toISOString(),
                                updatedAt: new Date().toISOString()
                            };
                            
                            // If we still lack critical data, refresh the calendar instead
                            if (!appointment.appointmentTypeId || !appointment.startTime) {
                                console.error('Insufficient data for appointment in eventReceive:', info.event.id);
                                // Force calendar refresh
                                const calendarApi = calendarRef.current?.getApi();
                                if (calendarApi) {
                                    calendarApi.refetchEvents();
                                }
                                return;
                            }
                            
                            console.log('Created fallback appointment from event data:', appointment);
                        }
                        
                        if (appointment) {
                            console.log('Processing event receive as drag-drop fallback:', info.event.id);
                            
                            // Create a synthetic EventDropArg to pass to handleEventDrop
                            const syntheticDropInfo = {
                                event: info.event,
                                oldEvent: {
                                    start: new Date(appointment.startTime),
                                    end: new Date(appointment.endTime),
                                    id: appointment.id,
                                    title: appointment.title,
                                },
                                delta: {
                                    days: 0, // We don't know the exact delta, but handleEventDrop will calculate from dates
                                    milliseconds: 0
                                },
                                revert: () => {
                                    // Force calendar refresh to revert
                                    const calendarApi = calendarRef.current?.getApi();
                                    if (calendarApi) {
                                        calendarApi.refetchEvents();
                                    }
                                },
                                view: info.view
                            };
                            
                            // Process as a normal drop
                            handleEventDrop(syntheticDropInfo as EventDropArg);
                        }
                    }}
                    loading={(isLoading) => setIsLoadingAppointments(isLoading)}
                    drop={(info) => {
                        console.log('External item dropped on calendar:', info.draggedEl);
                    }}
                    slotMinTime={selectedBusiness?.restrictToBusinessHours 
                        ? convertTimeSpanToTime(selectedBusiness.businessHoursStart) 
                        : '00:00:00'
                    }
                    slotMaxTime={selectedBusiness?.restrictToBusinessHours 
                        ? convertTimeSpanToTime(selectedBusiness.businessHoursEnd) 
                        : '24:00:00'
                    }
                />

                {/* Inline Sync Button - will be injected into calendar toolbar */}
                <Box 
                    ref={syncButtonContainerRef}
                    className="sync-button-inline"
                    sx={{ 
                        display: 'inline-flex',
                        alignItems: 'center',
                        ml: 2,
                        verticalAlign: 'middle'
                    }}
                >
                    <Tooltip title={isOffline ? "You are offline" : (isSyncing ? `Syncing ${pendingChangesCount} changes...` : (pendingChangesCount > 0 ? `${pendingChangesCount} changes pending sync` : "Online - All synced"))}>
                        <span>
                            <IconButton
                                disabled
                                size="small"
                                sx={{ 
                                    color: isOffline ? 'warning.main' : (isSyncing || pendingChangesCount > 0 ? 'info.main' : 'info.main'),
                                    cursor: 'default',
                                    '&.Mui-disabled': {
                                        color: isOffline ? 'warning.main' : (isSyncing || pendingChangesCount > 0 ? 'info.main' : 'info.main'),
                                        opacity: 1
                                    }
                                }}
                            >
                                <Badge 
                                    badgeContent={pendingChangesCount} 
                                    color="error"
                                    invisible={pendingChangesCount === 0 || isOffline}
                                >
                                    {isOffline ? <CloudOffIcon fontSize="small" /> : (isSyncing ? <CloudSyncIcon fontSize="small" /> : <CloudDoneIcon fontSize="small" />)}
                                </Badge>
                            </IconButton>
                        </span>
                    </Tooltip>
                </Box>

                <Box sx={{ 
                    position: 'fixed', 
                    bottom: { xs: 12, sm: 24 }, 
                    right: { xs: 12, sm: 24 }, 
                    zIndex: 1000,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: { xs: 1, sm: 2 }
                }}>
                    <Tooltip title="Create Appointment">
                        <Fab
                            color="primary"
                            aria-label="Create Appointment"
                            onClick={async () => {
                                if (!selectedBusiness) {
                                    showError("Please select a business first");
                                    return;
                                }
                                if (appointmentTypes.length === 0) {
                                    showError("No appointment types available. Please create an appointment type first.");
                                    return;
                                }
                                setError(null);
                                setSuccess(null);
                                const firstType = appointmentTypes[0];
                                setSelectedAppointmentType(firstType);
                                setFormData(prev => ({
                                    ...prev,
                                    appointmentTypeId: firstType.id,
                                    start: new Date(),
                                    end: new Date(Date.now() + 60 * 60 * 1000), // 1 hour from now
                                    userId: selectedUserFilter || user?.id
                                }));
                                
                                setIsEditing(false);
                                // Open dialog immediately
                                setIsDialogOpen(true);

                                // Load customers in background
                                loadCustomers();
                            }}
                            sx={{ 
                                bgcolor: theme.navigationColor,
                                '&:hover': {
                                    bgcolor: `${theme.navigationColor}CC`
                                }
                            }}
                        >
                            <AddIcon />
                        </Fab>
                    </Tooltip>

                    {(appointmentTypes.length > 1 || availableUsers.length > 1 || isMobile) && (
                        <Tooltip title="Filters">
                            <Fab 
                                color="primary" 
                                onClick={() => setFilterPanelOpen(true)}
                                sx={{ 
                                    bgcolor: theme.navigationColor,
                                    '&:hover': {
                                        bgcolor: `${theme.navigationColor}CC`
                                    }
                                }}
                            >
                                <FilterListIcon />
                                {(selectedTypeFilters.length < appointmentTypes.length || 
                                 selectedUserFilter !== null) && (
                                    <Badge 
                                        color="secondary" 
                                        sx={{ 
                                            position: 'absolute',
                                            top: 8,
                                            right: 8
                                        }}
                                    />
                                )}
                            </Fab>
                        </Tooltip>
                    )}
                </Box>

                <Drawer
                    anchor={isMobile ? "bottom" : "right"}
                    open={filterPanelOpen}
                    onClose={() => setFilterPanelOpen(false)}
                    PaperProps={{
                        sx: {
                            width: isMobile ? '100%' : 320,
                            maxHeight: isMobile ? '80vh' : '100%',
                            p: { xs: 1.5, sm: 2 },
                            bgcolor: theme.backgroundColor,
                            borderTopLeftRadius: isMobile ? 16 : 0,
                            borderTopRightRadius: isMobile ? 16 : 0,
                            marginTop: 0, // Remove margin top since we have the Toolbar placeholder in Layout
                            '&::before': {
                                content: '""',
                                display: isMobile ? 'block' : 'none',
                                width: 40,
                                height: 4,
                                backgroundColor: 'grey.300',
                                borderRadius: 2,
                                position: 'absolute',
                                top: 8,
                                left: '50%',
                                transform: 'translateX(-50%)'
                            }
                        }
                    }}
                >
                    <Stack spacing={3} marginTop={isMobile ? 3 : 8}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="h5" sx={{ color: theme.textColor, fontWeight: 500 }}>
                                Filters
                            </Typography>
                            <IconButton
                                onClick={() => setFilterPanelOpen(false)}
                                sx={{ color: theme.textColor }}
                            >
                                <CloseIcon />
                            </IconButton>
                        </Box>
                        <Box>
                            <Typography variant="h6" gutterBottom sx={{ color: theme.textColor }}>
                                Appointment Types
                            </Typography>
                            <Stack direction="row" flexWrap="wrap" gap={1}>
                                {appointmentTypes.map(type => (
                                    <Chip
                                        key={type.id}
                                        label={type.name}
                                        onClick={() => handleTypeFilterChange(type.id)}
                                        color={selectedTypeFilters.includes(type.id) ? "primary" : "default"}
                                        sx={{ 
                                            backgroundColor: selectedTypeFilters.includes(type.id) ? type.color : undefined,
                                            color: selectedTypeFilters.includes(type.id) ? type.textColor : undefined,
                                            m: 0.5
                                        }}
                                    />
                                ))}
                            </Stack>
                        </Box>

                        <Box>
                            <Typography variant="h6" gutterBottom sx={{ color: theme.textColor }}>
                                Users
                            </Typography>
                            <Stack direction="row" flexWrap="wrap" gap={1}>
                                {availableUsers.map(user => (
                                    <Chip
                                        key={user.id}
                                        label={`${user.firstName} ${user.lastName}`}
                                        onClick={() => handleUserFilterChange(user.id)}
                                        color={selectedUserFilter === user.id ? "primary" : "default"}
                                        sx={{ 
                                            backgroundColor: selectedUserFilter === user.id 
                                                ? theme.navigationColor 
                                                : undefined,
                                            color: selectedUserFilter === user.id 
                                                ? theme.secondaryColor 
                                                : undefined,
                                            m: 0.5
                                        }}
                                    />
                                ))}
                            </Stack>
                        </Box>

                        {isMobile && (
                            <>
                                <Divider sx={{ borderColor: `${theme.textColor}22` }} />
                                <Box>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={swipeNavigationEnabled}
                                                onChange={(e) => setSwipeNavigationEnabled(e.target.checked)}
                                                sx={{
                                                    '& .MuiSwitch-switchBase.Mui-checked': {
                                                        color: theme.navigationColor,
                                                    },
                                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                        backgroundColor: theme.navigationColor,
                                                    },
                                                }}
                                            />
                                        }
                                        label={
                                            <Typography sx={{ color: theme.textColor }}>
                                                Swipe Navigation
                                            </Typography>
                                        }
                                    />
                                </Box>
                            </>
                        )}
                    </Stack>
                </Drawer>

                <BaseDialog 
                    open={isDialogOpen} 
                    onClose={handleClose}
                    TransitionProps={{
                        onExited: handleDialogExited
                    }}
                    title={isEditing ? 'Edit Appointment' : 'Create Appointment'}
                    fullWidth
                    contentSx={{
                        pb: 4
                    }}
                    actions={
                        <>
                            {isEditing && selectedAppointment?.hasWriteAccess && (
                                <Button 
                                    onClick={handleDelete}
                                    variant="contained"
                                    color="error"
                                    sx={{ mr: 'auto' }}>
                                    Delete
                                </Button>
                            )}
                            <Button 
                                onClick={handleClose}
                                variant="outlined"
                                color="primary">Cancel</Button>
                            <Button onClick={handleAppointmentSubmit} variant="contained" disabled={loading}>
                                {isEditing ? 'Update' : 'Create'}
                            </Button>
                        </>
                    }
                    actionsSx={{
                        bgcolor: 'background.paper',
                        px: 3,
                        py: 2
                    }}
                >
                        {error && <Alert severity="error" sx={{ mt: 2, mb: 2 }}>{error}</Alert>}
                        {success && <Alert severity="success" sx={{ mt: 2, mb: 2 }}>{success}</Alert>}

                        {(() => {
                            const selectValue = formData.userId || user?.id || '';
                            // Use sharedUsers if available, otherwise fall back to availableUsers
                            const usersToDisplay = sharedUsers.length > 0 ? sharedUsers : availableUsers;
                            // Show the dropdown if:
                            // 1. We're currently loading users (show with loading indicator)
                            // 2. We have multiple users to choose from
                            const shouldShow = isLoadingUsers || usersToDisplay.length > 1;
                            const isCurrentUserInList = usersToDisplay.some(u => u.id === user?.id);

                            console.log('[User Select Render] shouldShow:', shouldShow);
                            console.log('[User Select Render] usersToDisplay:', usersToDisplay);
                            console.log('[User Select Render] selectValue:', selectValue);

                            if (!shouldShow) return null;

                            return (
                                <FormControl fullWidth margin="normal">
                                    <InputLabel>User</InputLabel>
                                    <Select
                                        value={selectValue}
                                        onChange={(e) => {
                                            console.log('[User Select] onChange - new value:', e.target.value);
                                            setFormData(prev => ({ ...prev, userId: e.target.value as string }));
                                        }}
                                        label="User"
                                        disabled={isLoadingUsers}
                                        renderValue={(value) => {
                                            const selectedUser = usersToDisplay.find(u => u.id === value);
                                            if (selectedUser) {
                                                return `${selectedUser.firstName} ${selectedUser.lastName}${selectedUser.id === user?.id ? ' (Me)' : ''}`;
                                            }
                                            if (value === user?.id) {
                                                return `${user?.firstName} ${user?.lastName} (Me)`;
                                            }
                                            return value as string;
                                        }}
                                        endAdornment={
                                            isLoadingUsers && (
                                                <InputAdornment position="end" sx={{ mr: 3 }}>
                                                    <CircularProgress size={20} />
                                                </InputAdornment>
                                            )
                                        }
                                    >
                                        {isLoadingUsers ? (
                                            <MenuItem value={selectValue}>
                                                {(formData.userId === user?.id || !formData.userId)
                                                    ? `${user?.firstName} ${user?.lastName} (Me)`
                                                    : 'Loading users...'}
                                            </MenuItem>
                                        ) : [
                                            !isCurrentUserInList && (
                                                <MenuItem key="current-user" value={user?.id || ''}>
                                                    {user?.firstName} {user?.lastName} (Me)
                                                </MenuItem>
                                            ),
                                            ...usersToDisplay.map((displayUser) => (
                                                <MenuItem key={displayUser.id} value={displayUser.id}>
                                                    {displayUser.firstName} {displayUser.lastName}
                                                    {displayUser.id === user?.id ? ' (Me)' : ''}
                                                </MenuItem>
                                            ))
                                        ].filter(Boolean)}
                                    </Select>
                                </FormControl>
                            );
                        })()}

                        {appointmentTypes.length > 1 && (
                            <FormControl fullWidth margin="normal">
                                <InputLabel>Appointment Type</InputLabel>
                                <Select
                                    value={formData.appointmentTypeId}
                                    onChange={handleAppointmentTypeChange}
                                    label="Appointment Type"
                                    required
                                >
                                    {appointmentTypes.map((type) => (
                                        <MenuItem key={type.id} value={type.id}>
                                            {type.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                        
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                            <Autocomplete
                                fullWidth
                                loading={isLoadingCustomers}
                                disabled={isOffline}
                                options={[
                                    ...(isOffline ? [] : [{ id: -1, firstName: '➕ Create', lastName: 'New Customer', isCreateOption: true } as any]),
                                    // Include selectedCustomer in options if not already present
                                    ...(selectedCustomer && !customers.some(c => c.id === selectedCustomer.id) ? [selectedCustomer] : []),
                                    ...customers
                                ]}
                                getOptionLabel={(customer) => {
                                    if (customer.isCreateOption) return '➕ Create New Customer';
                                    return `${customer.firstName || ''} ${customer.lastName || ''}`;
                                }}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                value={selectedCustomer}
                            onChange={(event, newValue) => {
                                // Check if "Create New Customer" was selected
                                if (newValue && (newValue as any).isCreateOption) {
                                    setIsCustomerDialogOpen(true);
                                    return;
                                }
                                
                                setSelectedCustomer(newValue);
                                // Update form data with customer ID
                                setFormData(prev => ({
                                    ...prev,
                                    customerId: newValue?.id
                                }));
                                
                                if (newValue) {
                                    // Load jobs for the selected customer
                                    jobService.getJobsByCustomer(newValue.id).then((jobs: Job[]) => {
                                        setCustomerJobs(jobs);
                                    }).catch((error: any) => {
                                        console.error('Error loading customer jobs:', error);
                                        setCustomerJobs([]);
                                    });
                                } else {
                                    setCustomerJobs([]);
                                    setSelectedJob(null);
                                }
                            }}
                            inputValue={customerSearchTerm}
                            onInputChange={(event, newInputValue, reason) => {
                                setCustomerSearchTerm(newInputValue);
                                
                                // Only search if the input change was due to user typing
                                if (reason === 'input') {
                                    // Clear existing customers when user starts typing to prevent old results from showing
                                    setCustomers([]);
                                    debouncedSearchCustomers(newInputValue);
                                }
                            }}
                            filterOptions={(options, state) => {
                                // Disable client-side filtering since we're doing server-side search
                                // This prevents old results from showing while new ones are loading
                                return options;
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Customer (Optional)"
                                    placeholder="Search by name, email, phone, or address..."
                                    margin="normal"
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {isLoadingCustomers ? <CircularProgress color="inherit" size={20} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                            renderOption={(props, customer) => {
                                if (customer.isCreateOption) {
                                    return (
                                        <Box component="li" {...props} sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                            <Box>
                                                <Typography variant="body1">
                                                    ➕ Create New Customer
                                                </Typography>
                                            </Box>
                                        </Box>
                                    );
                                }
                                
                                // Helper function to check if a value is not empty
                                const hasValue = (value: any) => {
                                    return value && typeof value === 'string' && value.trim().length > 0;
                                };
                                
                                // Construct full address
                                const addressParts = [
                                    customer.address,
                                    customer.city,
                                    customer.state,
                                    customer.zipCode
                                ].filter(hasValue);
                                const fullAddress = addressParts.join(', ');
                                
                                // Construct full name
                                const fullName = `${customer.firstName || ''} ${customer.lastName || ''}`.trim();
                                
                                return (
                                    <Box component="li" {...props}>
                                        <Box sx={{ py: 1 }}>
                                            {hasValue(fullName) && (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                                    <PersonIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                                                    <Typography variant="body2" fontWeight="medium">
                                                        {fullName}
                                                    </Typography>
                                                </Box>
                                            )}
                                            {hasValue(customer.phoneNumber) && (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                                    <PhoneIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                                                    <Typography variant="caption" color="text.secondary">
                                                        {customer.phoneNumber}
                                                    </Typography>
                                                </Box>
                                            )}
                                            {hasValue(customer.email) && (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                                    <EmailIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                                                    <Typography variant="caption" color="text.secondary">
                                                        {customer.email}
                                                    </Typography>
                                                </Box>
                                            )}
                                            {hasValue(fullAddress) && (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <LocationOnIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                                                    <Typography variant="caption" color="text.secondary">
                                                        {fullAddress}
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Box>
                                    </Box>
                                );
                            }}
                            />
                            
                            {/* View Customer Button */}
                            {selectedCustomer && (
                                <Tooltip title="View Customer">
                                    <IconButton
                                        onClick={handleViewCustomer}
                                        sx={{ 
                                            mt: 2,
                                            color: 'primary.main'
                                        }}
                                    >
                                        <OpenInNewIcon />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Box>
                        
                        {/* Job Selection */}
                        {selectedCustomer && (
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                                <Autocomplete
                                    fullWidth
                                    disabled={isOffline}
                                    options={[
                                    ...(isOffline ? [] : [{ id: -1, jobNumber: '➕ Create', jobAddress: 'New Job', isCreateOption: true } as any]),
                                    ...customerJobs.sort((a, b) => {
                                        // Sort by createdDate descending (most recent first)
                                        return new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime();
                                    })
                                ]}
                                getOptionLabel={(job) => {
                                    if (job.isCreateOption) return '➕ Create New Job';
                                    return `${job.jobNumber} - ${job.jobAddress}`;
                                }}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                value={selectedJob}
                                onChange={(event, newValue) => {
                                    // Check if "Create New Job" was selected
                                    if (newValue && (newValue as any).isCreateOption) {
                                        handleCreateNewJob();
                                        return;
                                    }
                                    
                                    handleSelectJob(newValue);
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Job (Optional)"
                                        placeholder="Select a job or create new..."
                                        margin="normal"
                                    />
                                )}
                                renderOption={(props, job) => {
                                    if (job.isCreateOption) {
                                        return (
                                            <Box component="li" {...props} sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                                <Box>
                                                    <Typography variant="body1">
                                                        ➕ Create New Job
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        );
                                    }
                                    
                                    return (
                                        <Box component="li" {...props}>
                                            <Box sx={{ py: 1, width: '100%' }}>
                                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                    {job.jobNumber}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {job.jobAddress}
                                                </Typography>
                                                {job.statusName && (
                                                    <Chip
                                                        label={job.statusName}
                                                        size="small"
                                                        sx={{
                                                            ml: 1,
                                                            height: 20,
                                                            fontSize: '0.7rem',
                                                            backgroundColor: job.statusColor || 'default',
                                                            color: 'white'
                                                        }}
                                                    />
                                                )}
                                            </Box>
                                        </Box>
                                    );
                                }}
                                />
                                
                                {/* View Job Button */}
                                {selectedJob && (
                                    <Tooltip title="View Job">
                                        <IconButton
                                            onClick={handleViewJob}
                                            sx={{ 
                                                mt: 2,
                                                color: 'primary.main'
                                            }}
                                        >
                                            <OpenInNewIcon />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Box>
                        )}

                        {selectedAppointmentType?.requiresLocation && (
                            <Box sx={{ mt: 2 }}>
                                {selectedBusiness ? (
                                    <GoogleMapsAutocomplete
                                        value={formData.address || ''}
                                        onChange={(address, lat, lng) => {
                                            setFormData(prev => ({
                                                ...prev,
                                                address,
                                                latitude: lat,
                                                longitude: lng
                                            }));
                                        }}
                                        defaultBounds={{
                                            city: selectedBusiness.defaultCity,
                                            country: selectedBusiness.defaultCountry
                                        }}
                                    />
                                ) : (
                                    <TextField
                                        fullWidth
                                        label="Address"
                                        value={formData.address || ''}
                                        onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                                        helperText="Loading business settings..."
                                        margin="normal"
                                    />
                                )}
                            </Box>
                        )}

                        <TextField
                            label="Description"
                            value={formData.description}
                            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                            fullWidth
                            margin="normal"
                            multiline
                            rows={3}
                        />

                        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
                            <Box sx={{ display: 'flex', mb: 1, gap: 2 }}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={formData.isAllDay}
                                            onChange={(e) => setFormData(prev => ({ ...prev, isAllDay: e.target.checked }))}
                                        />
                                    }
                                    sx={{ flex: 1 }}
                                    label="All Day"
                                />
                                {(!isEditing || !selectedAppointment?.recurringAppointmentId || isEditingSeries) && (
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={showRecurrenceOptions}
                                                disabled={Boolean(isEditing && Boolean(selectedAppointment?.recurringAppointmentId) && !isEditingSeries)}
                                                onChange={(e) => {
                                                    setShowRecurrenceOptions(e.target.checked);
                                                    if (!e.target.checked) {
                                                        setFormData(prev => ({ ...prev, recurrenceRule: '' }));
                                                        setRecurrenceEndDate(null);
                                                    } else {
                                                        // Set default recurrence pattern when checked
                                                        handleRecurrenceChange('WEEKLY', 1);
                                                    }
                                                }}
                                            />
                                        }
                                        sx={{ flex: 1 }}
                                        label="Recurring"
                                    />
                                )}
                            </Box>

                            {showRecurrenceOptions && (!isEditing || !selectedAppointment?.recurringAppointmentId || isEditingSeries) && (
                                <Box sx={{ bgcolor: 'background.default', borderRadius: 1 }}>
                                    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                                        <FormControl sx={{ flex: 1 }}>
                                            <InputLabel>Pattern</InputLabel>
                                            <Select
                                                value={formData.recurrenceRule?.split(';')[0].split('=')[1] || ''}
                                                onChange={(e) => handleRecurrenceChange(e.target.value)}
                                                label="Pattern"
                                            >
                                                <MenuItem value="">None</MenuItem>
                                                <MenuItem value="DAILY">Daily</MenuItem>
                                                <MenuItem value="WEEKLY">Weekly</MenuItem>
                                                <MenuItem value="MONTHLY">Monthly</MenuItem>
                                                <MenuItem value="YEARLY">Yearly</MenuItem>
                                            </Select>
                                        </FormControl>

                                        <DatePicker
                                            label="End Date (Optional)"
                                            value={recurrenceEndDate}
                                            onChange={(newDate: Date | null) => {
                                                setRecurrenceEndDate(newDate);
                                                const pattern = formData.recurrenceRule?.split(';')[0].split('=')[1] || '';
                                                const interval = formData.recurrenceRule?.match(/INTERVAL=(\d+)/)?.[1] || '1';
                                                handleRecurrenceChange(pattern, parseInt(interval));
                                            }}
                                            sx={{ flex: 1 }}
                                        />
                                    </Box>

                                    {formData.recurrenceRule && (
                                        <TextField
                                            label="Interval"
                                            type="number"
                                            value={formData.recurrenceRule.match(/INTERVAL=(\d+)/)?.[1] || '1'}
                                            onChange={(e) => {
                                                const value = parseInt(e.target.value) || 1;
                                                const pattern = formData.recurrenceRule?.split(';')[0].split('=')[1] || '';
                                                handleRecurrenceChange(pattern, value);
                                            }}
                                            fullWidth
                                            inputProps={{ min: 1 }}
                                            sx={{ mb: 2 }}
                                            helperText="How often the appointment should repeat (e.g., every 2 weeks)"
                                        />
                                    )}
                                </Box>
                            )}

                            {formData.isAllDay ? (
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    <DatePicker
                                        label="Start Date"
                                        value={formData.start}
                                        onChange={(date) => {
                                            if (date) {
                                                // Set time to start of day
                                                const newDate = new Date(date);
                                                newDate.setHours(0, 0, 0, 0);
                                                setFormData(prev => ({ ...prev, start: newDate }));
                                            }
                                        }}
                                        sx={{ flex: 1 }}
                                    />
                                    <DatePicker
                                        label="End Date"
                                        value={formData.end}
                                        onChange={(date) => {
                                            if (date) {
                                                // Set time to end of day
                                                const newDate = new Date(date);
                                                newDate.setHours(23, 59, 59, 999);
                                                setFormData(prev => ({ ...prev, end: newDate }));
                                            }
                                        }}
                                        sx={{ flex: 1 }}
                                    />
                                </Box>
                            ) : isMobile ? (
                                // Mobile: Separate Date + Time pickers, stacked rows for Start and End
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    {/* Start Date/Time Row */}
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <DatePicker
                                            label="Start Date"
                                            value={formData.start}
                                            onChange={(date) => {
                                                if (date) {
                                                    const newDate = new Date(date);
                                                    // Preserve the time from current start
                                                    newDate.setHours(formData.start.getHours(), formData.start.getMinutes(), 0, 0);
                                                    setFormData(prev => ({ ...prev, start: newDate }));
                                                }
                                            }}
                                            sx={{ flex: 1 }}
                                        />
                                        <TimePicker
                                            label="Start Time"
                                            value={formData.start}
                                            onChange={(date) => {
                                                if (date) {
                                                    const newDate = new Date(formData.start);
                                                    newDate.setHours(date.getHours(), date.getMinutes(), 0, 0);
                                                    setFormData(prev => ({ ...prev, start: newDate }));
                                                }
                                            }}
                                            ampm={true}
                                            timeSteps={{ hours: 1, minutes: 15 }}
                                            desktopModeMediaQuery="@media (min-width: 0px)"
                                            sx={{ flex: 1 }}
                                            slotProps={{
                                                digitalClockSectionItem: {
                                                    sx: {
                                                        '&:first-of-type': { mt: '80px' },
                                                        '&:last-of-type': { mb: '80px' }
                                                    }
                                                }
                                            }}
                                        />
                                    </Box>
                                    {/* End Date/Time Row */}
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <DatePicker
                                            label="End Date"
                                            value={formData.end}
                                            onChange={(date) => {
                                                if (date) {
                                                    const newDate = new Date(date);
                                                    // Preserve the time from current end
                                                    newDate.setHours(formData.end.getHours(), formData.end.getMinutes(), 0, 0);
                                                    setFormData(prev => ({ ...prev, end: newDate }));
                                                }
                                            }}
                                            sx={{ flex: 1 }}
                                        />
                                        <TimePicker
                                            label="End Time"
                                            value={formData.end}
                                            onChange={(date) => {
                                                if (date) {
                                                    const newDate = new Date(formData.end);
                                                    newDate.setHours(date.getHours(), date.getMinutes(), 0, 0);
                                                    setFormData(prev => ({ ...prev, end: newDate }));
                                                }
                                            }}
                                            ampm={true}
                                            timeSteps={{ hours: 1, minutes: 15 }}
                                            desktopModeMediaQuery="@media (min-width: 0px)"
                                            sx={{ flex: 1 }}
                                            slotProps={{
                                                digitalClockSectionItem: {
                                                    sx: {
                                                        '&:first-of-type': { mt: '80px' },
                                                        '&:last-of-type': { mb: '80px' }
                                                    }
                                                }
                                            }}
                                        />
                                    </Box>
                                </Box>
                            ) : (
                                // Desktop: Combined DateTimePickers side by side
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    <DateTimePicker
                                        label="Start Time"
                                        value={formData.start}
                                        onChange={(date) => {
                                            if (date) {
                                                setFormData(prev => ({ ...prev, start: date }));
                                            }
                                        }}
                                        ampm={true}
                                        timeSteps={{ hours: 1, minutes: 15 }}
                                        desktopModeMediaQuery="@media (min-width: 0px)"
                                        sx={{ flex: 1 }}
                                    />
                                    <DateTimePicker
                                        label="End Time"
                                        value={formData.end}
                                        onChange={(date) => {
                                            if (date) {
                                                setFormData(prev => ({ ...prev, end: date }));
                                            }
                                        }}
                                        ampm={true}
                                        timeSteps={{ hours: 1, minutes: 15 }}
                                        desktopModeMediaQuery="@media (min-width: 0px)"
                                        sx={{ flex: 1 }}
                                    />
                                </Box>
                            )}

                        <FormControl fullWidth margin="normal" sx={{ display: 'none' }}>
                            <InputLabel>Time Zone</InputLabel>
                            <Select
                                value={formData.timeZoneId}
                                onChange={(e) => setFormData(prev => ({ ...prev, timeZoneId: e.target.value }))}
                                label="Time Zone"
                            >
                                {timeZones.map((zone) => (
                                    <MenuItem key={zone} value={zone}>
                                        {zone}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        </LocalizationProvider>
                </BaseDialog>

                <BaseDialog
                    open={isRecurringDialogOpen}
                    onClose={handleRecurringDialogClose}
                    title={recurringAction === 'edit' ? 'Edit Recurring Appointment' : 'Delete Recurring Appointment'}
                    maxWidth="sm"
                    fullWidth
                    contentSx={{ mt: 2 }}
                    actions={
                        <>
                            <Button 
                                onClick={handleRecurringDialogClose} 
                                variant="contained"
                                color="error"
                                sx={{ color: 'white' }}
                            >
                                Cancel
                            </Button>
                            <Button 
                                onClick={() => handleRecurringChoice(false)}
                                variant="contained"
                                color="primary"
                            >
                                Just This One
                            </Button>
                            <Button 
                                onClick={() => handleRecurringChoice(true)}
                                variant="contained"
                                color="primary"
                            >
                                Entire Series
                            </Button>
                        </>
                    }
                >
                    <DialogContentText>
                        {recurringAction === 'edit' 
                            ? 'Would you like to edit just this occurrence or the entire series?' : 
                            'Would you like to delete just this occurrence or the entire series?'}
                    </DialogContentText>
                </BaseDialog>

                {/* Job Creation Dialog */}
                <BaseDialog 
                    open={isJobDialogOpen} 
                    onClose={() => setIsJobDialogOpen(false)} 
                    title={`Create Job for ${selectedCustomer?.firstName || ''} ${selectedCustomer?.lastName || ''}`}
                    fullWidth
                    sx={{
                        '& .MuiDialog-paper': {
                            maxWidth: { xs: 'calc(100vw - 10px)', sm: '900px' }
                        }
                    }}
                    actions={
                        <>
                            <Button 
                                onClick={() => setIsJobDialogOpen(false)}
                                variant="outlined"
                                color="primary"
                            >
                                Cancel
                            </Button>
                            <Button onClick={handleCreateJob} variant="contained">
                                Create Job
                            </Button>
                        </>
                    }
                >
                    {selectedCustomer && (
                        <Box sx={{ mt: 3, mb: 3 }}>
                            <CustomerDetailsCard
                                customer={selectedCustomer}
                                title="Customer:"
                                showChangeButton={false}
                            />
                        </Box>
                    )}
                    <JobForm
                        formData={jobFormData}
                        onFormDataChange={setJobFormData}
                        defaultBounds={{
                            city: selectedBusiness?.defaultCity,
                            country: selectedBusiness?.defaultCountry
                        }}
                        showCustomerSelection={false}
                    />
                </BaseDialog>

                {/* Customer Creation Dialog */}
                <CustomerFormDialog
                    open={isCustomerDialogOpen}
                    onClose={() => setIsCustomerDialogOpen(false)}
                    onSuccess={handleCustomerCreated}
                    business={selectedBusiness}
                    title="Create New Customer"
                    defaultAssignedSalesPersonId={formData.userId || selectedUserFilter || user?.id}
                />

                {/* View Customer Dialog (Mobile) */}
                <BaseDialog
                    open={isViewCustomerDialogOpen}
                    onClose={() => setIsViewCustomerDialogOpen(false)}
                    title="Customer Details"
                    fullWidth
                    maxWidth="md"
                    actions={
                        <Button 
                            onClick={() => setIsViewCustomerDialogOpen(false)}
                            variant="contained"
                            color="primary"
                        >
                            Close
                        </Button>
                    }
                >
                    {selectedCustomer && (
                        <Box sx={{ py: 2 }}>
                            <CustomerDetailsCard
                                customer={selectedCustomer}
                                showChangeButton={false}
                            />
                        </Box>
                    )}
                </BaseDialog>

                {/* View Job Dialog (Mobile) */}
                <BaseDialog
                    open={isViewJobDialogOpen}
                    onClose={() => setIsViewJobDialogOpen(false)}
                    title="Job Details"
                    fullWidth
                    maxWidth="md"
                    actions={
                        <Button 
                            onClick={() => setIsViewJobDialogOpen(false)}
                            variant="contained"
                            color="primary"
                        >
                            Close
                        </Button>
                    }
                >
                    {selectedJob && (
                        <Box sx={{ py: 2 }}>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Job Number
                                    </Typography>
                                    <Typography variant="body1" fontWeight="medium">
                                        {selectedJob.jobNumber}
                                    </Typography>
                                </Grid>
                                {selectedJob.jobAddress && (
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Address
                                        </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <LocationOnIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
                                            <Typography variant="body1">
                                                {selectedJob.jobAddress}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                )}
                                {selectedJob.statusName && (
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Status
                                        </Typography>
                                        <Chip
                                            label={selectedJob.statusName}
                                            sx={{
                                                backgroundColor: selectedJob.statusColor || 'default',
                                                color: 'white',
                                                mt: 0.5
                                            }}
                                        />
                                    </Grid>
                                )}
                                {selectedJob.estimatedValue !== undefined && (
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Estimated Value
                                        </Typography>
                                        <Typography variant="body1">
                                            ${selectedJob.estimatedValue.toLocaleString()}
                                        </Typography>
                                    </Grid>
                                )}
                                {selectedJob.notes && (
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Notes
                                        </Typography>
                                        <Typography variant="body1">
                                            {selectedJob.notes}
                                        </Typography>
                                    </Grid>
                                )}
                            </Grid>
                        </Box>
                    )}
                </BaseDialog>
            </Box>

            {/* Add Snackbar for notifications */}
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={5000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert 
                    onClose={handleSnackbarClose} 
                    severity={snackbarSeverity}
                    sx={{ width: '100%' }}
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default MainCalendar; 